// ─────────────────────────────────────────────────────────────────
// MAPA — El mapa del comercio internacional · src/App.jsx
// Datos reales: UN Comtrade · WTO · World Bank + análisis IA
// ─────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import LandingPage from "./LandingPage";
import LoginPage from "./LoginPage";
import { useAuth } from "./context/AuthContext";
import { supabase } from "./lib/supabase";
import { getHSDescription } from "./lib/hsDescriptions";
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from "recharts";

// ─── PALETA DE COLORES KIN ────────────────────────────────────────
const COLORS = {
  navy:          '#0A2540',
  navyDeep:      '#061A2E',
  navyLight:     '#163355',
  cloudDancer:   '#FAFAF7',
  teal:          '#0EA5A4',
  tealVibrant:   '#2DD4D3',
  tealDark:      '#0E7574',
  amber:         '#D4A24C',
  amberDark:     '#BA7517',
  mahogany:      '#6B2737',
  mahoganyLight: '#B85365',
  slate:         '#1E293B',
  gray:          '#64748B',
  grayLight:     '#94A3B8',
  border:        'rgba(10, 37, 64, 0.08)',
  borderLight:   'rgba(250, 250, 247, 0.1)',
};

// ─── CONSTANTES ──────────────────────────────────────────────────
const TODOS = { iso:"all", name:"🌍 Todos los países (análisis global)" };

const COUNTRIES = [
  { iso:"col", name:"Colombia 🇨🇴" }, { iso:"usa", name:"Estados Unidos 🇺🇸" },
  { iso:"deu", name:"Alemania 🇩🇪" }, { iso:"chn", name:"China 🇨🇳" },
  { iso:"mex", name:"México 🇲🇽" },  { iso:"esp", name:"España 🇪🇸" },
  { iso:"ita", name:"Italia 🇮🇹" },  { iso:"bra", name:"Brasil 🇧🇷" },
  { iso:"arg", name:"Argentina 🇦🇷" },{ iso:"per", name:"Perú 🇵🇪" },
  { iso:"chl", name:"Chile 🇨🇱" },   { iso:"gbr", name:"Reino Unido 🇬🇧" },
  { iso:"nld", name:"Países Bajos 🇳🇱"},{ iso:"jpn", name:"Japón 🇯🇵" },
  { iso:"kor", name:"Corea del Sur 🇰🇷"},{ iso:"ind", name:"India 🇮🇳" },
  { iso:"sau", name:"Arabia Saudita 🇸🇦"},{ iso:"sgp", name:"Singapur 🇸🇬" },
  { iso:"aus", name:"Australia 🇦🇺" }, { iso:"can", name:"Canadá 🇨🇦" },
  { iso:"fra", name:"Francia 🇫🇷" },  { iso:"ecu", name:"Ecuador 🇪🇨" },
  { iso:"ury", name:"Uruguay 🇺🇾" },  { iso:"prt", name:"Portugal 🇵🇹" },
  { iso:"tur", name:"Turquía 🇹🇷" },  { iso:"zaf", name:"Sudáfrica 🇿🇦" },
  { iso:"vnm", name:"Vietnam 🇻🇳" },  { iso:"tha", name:"Tailandia 🇹🇭" },
  { iso:"cri", name:"Costa Rica 🇨🇷" },{ iso:"pan", name:"Panamá 🇵🇦" },
  { iso:"idn", name:"Indonesia 🇮🇩" }, { iso:"mar", name:"Marruecos 🇲🇦" },
];

const SAMPLE_HS = [
  { code:"0901", desc:"Café" },              { code:"0803", desc:"Bananos y plátanos" },
  { code:"6403", desc:"Calzado de cuero" },  { code:"8471", desc:"Computadoras" },
  { code:"8703", desc:"Automóviles" },       { code:"6204", desc:"Ropa de mujer" },
  { code:"0306", desc:"Camarones" },         { code:"7108", desc:"Oro" },
  { code:"1701", desc:"Azúcar de caña" },    { code:"0806", desc:"Uvas frescas" },
  { code:"0304", desc:"Filetes de pescado" },{ code:"0902", desc:"Té negro o verde" },
];

const MESES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

// ─── DATOS DEL HOMEPAGE ───────────────────────────────────────────
const POPULAR_PILLS = [
  { code:"0901", desc:"Café" },
  { code:"8542", desc:"Semiconductores" },
  { code:"7108", desc:"Oro" },
  { code:"8703", desc:"Autos" },
  { code:"0803", desc:"Banano" },
  { code:"2709", desc:"Petróleo" },
];

const BENTO_PRODUCTS = [
  { code:"2709", name:"Petróleo crudo",        value:"USD 1.2T", change:-4.2,
    bg:COLORS.navy,    textColor:COLORS.cloudDancer, codeColor:COLORS.gray },
  { code:"8542", name:"Circuitos integrados",  value:"USD 580B", change:18.4,
    bg:COLORS.teal,    textColor:"#fff",            codeColor:"rgba(255,255,255,0.6)" },
  { code:"7108", name:"Oro",                   value:"USD 420B", change:12.3,
    bg:COLORS.amber,   textColor:COLORS.navy,       codeColor:"rgba(10,37,64,0.5)" },
  { code:"8703", name:"Vehículos de motor",    value:"USD 390B", change:-2.1,
    bg:"#fff",         textColor:COLORS.navy,       codeColor:COLORS.gray, bordered:true },
  { code:"8517", name:"Teléfonos inteligentes",value:"USD 320B", change:5.8,
    bg:"#fff",         textColor:COLORS.navy,       codeColor:COLORS.gray, bordered:true },
  { code:"3004", name:"Medicamentos",          value:"USD 280B", change:7.1,
    bg:"#fff",         textColor:COLORS.navy,       codeColor:COLORS.gray, bordered:true },
];

// ─── PALETA LEGADA ────────────────────────────────────────────────
const C = {
  blue:"#0EA5A4",  green:"#10B981",  gold:"#F4A300",  dark:"#fff",
  sky:"#2DD4D3",   red:COLORS.mahogany, purple:"#A78BFA", gray:"rgba(255,255,255,.45)",
  light:"rgba(255,255,255,.06)", border:"rgba(255,255,255,.10)",
};

// ─── HELPERS ─────────────────────────────────────────────────────
const fmtUSD = v => {
  if(v==null||isNaN(v)) return "—";
  const n=Number(v);
  if(n>=1e12) return `USD ${(n/1e12).toFixed(1)}T`;
  if(n>=1e9)  return `$${(n/1e9).toFixed(2)}B`;
  if(n>=1e6)  return `$${(n/1e6).toFixed(2)}M`;
  if(n>=1e3)  return `$${(n/1e3).toFixed(1)}K`;
  return `$${n.toLocaleString()}`;
};
const fmtKG = v => {
  if(v==null||isNaN(v)) return "—";
  const n=Number(v);
  if(n>=1e9) return `${(n/1e9).toFixed(2)}B kg`;
  if(n>=1e6) return `${(n/1e6).toFixed(2)}M kg`;
  if(n>=1e3) return `${(n/1e3).toFixed(1)}K kg`;
  return `${n.toFixed(0)} kg`;
};
const fmtPct = v => v==null?"—":`${Number(v)>0?"+":""}${Number(v).toFixed(1)}%`;
const stripFlag = s => s.replace(/[\u{1F1E0}-\u{1F1FF}]{2}/gu,"").trim();
const trendIcon = v => {
  if(v==null) return {label:"—",color:C.gray};
  const n=Number(v);
  if(n>10)  return {label:`▲ ${fmtPct(n)}`,color:C.green};
  if(n>0)   return {label:`↑ ${fmtPct(n)}`,color:"#4CAF50"};
  if(n<-10) return {label:`▼ ${fmtPct(n)}`,color:COLORS.mahogany};
  if(n<0)   return {label:`↓ ${fmtPct(n)}`,color:C.gold};
  return {label:`→ ${fmtPct(n)}`,color:C.gray};
};

// ─── LLAMADA CLAUDE (proxy seguro) ───────────────────────────────
async function callClaude(system, user, maxTokens=3000) {
  const res = await fetch("/api/claude", {
    method:"POST",
    headers:{"Content-Type":"application/json"},
    body: JSON.stringify({
      model:"claude-sonnet-4-20250514",
      max_tokens: maxTokens,
      system,
      messages:[{role:"user", content:user}],
    }),
  });
  if(!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return (data.content||[]).filter(b=>b.type==="text").map(b=>b.text).join("\n");
}

function extractJSON(text) {
  const clean = text.replace(/```json\s*/gi,"").replace(/```\s*/g,"").trim();
  const m = clean.match(/\{[\s\S]*\}/);
  if(!m) throw new Error("Sin JSON en respuesta");
  return JSON.parse(m[0]);
}

async function getHSCodes(query) {
  const text = await callClaude(
    "Experto en clasificación arancelaria. Solo JSON puro sin markdown.",
    `Producto: "${query}". 3-5 códigos HS de 4 dígitos.\nFormato: {"suggestions":[{"code":"XXXX","description":"desc español","sector":"sector"}]}`,
    600
  );
  return extractJSON(text).suggestions || [];
}

// ─── FETCH REAL DATA (UN Comtrade + WTO + World Bank en paralelo) ─
async function fetchRealData({ hs, exporterIso, importerIso }) {
  const reporter = (!exporterIso || exporterIso === "all") ? "WLD" : exporterIso.toUpperCase();
  const partner  = (!importerIso || importerIso === "all") ? "WLD" : importerIso.toUpperCase();
  const year     = new Date().getFullYear() - 1;

  const [comtradeRes, wtoRes, wbExpRes, wbImpRes] = await Promise.allSettled([
    // UN Comtrade: export flow
    fetch("/api/comtrade", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reporter, partner, hsCode: hs, year, flow: "X" }),
    }).then(r => r.json()),

    // WTO: tariff rate (importer charges on this HS code)
    fetch("/api/worldbank", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reporter: partner, partner: reporter, hsCode: hs }),
    }).then(r => r.json()),

    // World Bank: trade openness for exporter
    exporterIso !== "all"
      ? fetch(`/api/worldbank?country=${reporter}&indicator=NE.TRD.GNFS.ZS`).then(r => r.json())
      : Promise.resolve(null),

    // World Bank: GDP for importer
    importerIso !== "all"
      ? fetch(`/api/worldbank?country=${partner}&indicator=NY.GDP.MKTP.CD`).then(r => r.json())
      : Promise.resolve(null),
  ]);

  const safe = r => (r.status === "fulfilled" && r.value && !r.value.error) ? r.value : null;

  return {
    comtrade:         safe(comtradeRes),
    wto:              safe(wtoRes),
    worldbankExporter: safe(wbExpRes),
    worldbankImporter: safe(wbImpRes),
  };
}

// ─── TRADE DATA (con contexto de datos reales) ────────────────────
async function getTradeData({ hs, hsDesc, exporterName, importerName, realData: rd }) {
  const isAllImporter = importerName === "todos";
  const isAllExporter = exporterName === "todos";

  // Build verified data context from real APIs
  let verifiedCtx = "";
  if (rd?.comtrade?.data?.length > 0) {
    const sample = rd.comtrade.data.slice(0, 8);
    verifiedCtx += `\n\n=== DATOS REALES VERIFICADOS — UN Comtrade (${rd.comtrade.lastUpdate}) ===\n`;
    verifiedCtx += JSON.stringify(sample, null, 2);
    verifiedCtx += `\nFuente: ${rd.comtrade.sourceUrl}\n`;
    verifiedCtx += `IMPORTANTE: USA estos valores reales de tradeValue_usd y netWeight_kg en tu JSON. NO los inventes.\n`;
  }
  if (rd?.wto?.tariffs?.length > 0) {
    const rate  = rd.wto.latestRate_pct;
    const isTLC = rd.wto.isTLC;
    verifiedCtx += `\n=== ARANCEL REAL VERIFICADO — WTO (${rd.wto.lastUpdate}) ===\n`;
    verifiedCtx += isTLC
      ? `Arancel TLC aplicado: ${rate != null ? rate + "%" : "no disponible"}. Tasa MFN general: ${rd.wto.mfnRate_pct}%.\n`
      : `Arancel MFN aplicado: ${rate != null ? rate + "%" : "no disponible"}\n`;
    verifiedCtx += `USA este valor en el campo arancel_aplicado_pct del JSON.\n`;
  }
  if (rd?.worldbankImporter?.data?.length > 0) {
    const gdp = rd.worldbankImporter.data[0];
    verifiedCtx += `\n=== PIB DEL IMPORTADOR — World Bank ===\n`;
    verifiedCtx += `${gdp.country}: USD ${gdp.value?.toLocaleString()} (${gdp.year})\n`;
  }

  const sys = `Experto en estadísticas de comercio internacional: UN Comtrade, ITC Trade Map, OMC. Responde SOLO con JSON puro válido. Sin texto extra. Sin markdown.`;
  const contextMsg = isAllImporter
    ? `Analiza las exportaciones GLOBALES de ${exporterName === "todos" ? "el mundo" : exporterName} de este producto hacia TODOS los mercados del mundo.`
    : isAllExporter
    ? `Analiza las importaciones de ${importerName} de este producto desde TODOS los países exportadores del mundo.`
    : `Analiza el comercio bilateral entre ${exporterName} (exportador) y ${importerName} (importador).`;

  const usr = `Datos de comercio:
- Producto: ${hsDesc} (HS: ${hs})
- Exportador: ${isAllExporter ? "Mundo (todos los países)" : exporterName}
- Importador: ${isAllImporter ? "Mundo (todos los países)" : importerName}
- Contexto: ${contextMsg}
- Años: 2018-2024 (7 filas obligatorias)
${verifiedCtx}

Genera JSON con datos REALISTAS. Si tienes datos verificados arriba, úsalos como base para los valores correspondientes:
{"tabla":[{"año":2024,"fob_usd":5000000,"cantidad_kg":2000000,"precio_unitario_usd_kg":2.5,"variacion_pct":5.2,"principales_exportadores":["País A","País B","País C"],"principales_importadores":["Empresa X","País Y"],"nota":"evento relevante"},{"año":2023,"fob_usd":4750000,"cantidad_kg":1900000,"precio_unitario_usd_kg":2.5,"variacion_pct":3.1,"principales_exportadores":["País A"],"principales_importadores":["Empresa X"],"nota":"nota"},{"año":2022,"fob_usd":4600000,"cantidad_kg":1840000,"precio_unitario_usd_kg":2.5,"variacion_pct":-1.5,"principales_exportadores":["País A"],"principales_importadores":["Empresa X"],"nota":"nota"},{"año":2021,"fob_usd":4670000,"cantidad_kg":1870000,"precio_unitario_usd_kg":2.5,"variacion_pct":8.2,"principales_exportadores":["País A"],"principales_importadores":["Empresa X"],"nota":"nota"},{"año":2020,"fob_usd":4315000,"cantidad_kg":1726000,"precio_unitario_usd_kg":2.5,"variacion_pct":-7.1,"principales_exportadores":["País A"],"principales_importadores":["Empresa X"],"nota":"COVID-19"},{"año":2019,"fob_usd":4645000,"cantidad_kg":1858000,"precio_unitario_usd_kg":2.5,"variacion_pct":2.4,"principales_exportadores":["País A"],"principales_importadores":["Empresa X"],"nota":"nota"},{"año":2018,"fob_usd":4535000,"cantidad_kg":1814000,"precio_unitario_usd_kg":2.5,"variacion_pct":0,"principales_exportadores":["País A"],"principales_importadores":["Empresa X"],"nota":"año base"}],"resumen":{"años_con_datos":7,"primer_año_registro":2018,"total_acumulado_usd":32515000,"promedio_anual_usd":4645000,"crecimiento_tendencia":"creciente","precio_promedio_kg":2.5,"arancel_aplicado_pct":5.0,"acuerdos_comerciales":["Acuerdo 1"],"principales_competidores_exportadores":["País 1","País 2","País 3"],"participacion_mercado_pct":12.5},"analisis":{"oportunidad":"texto con emojis","riesgos":"texto con emojis","contexto_mercado":"texto con emojis","recomendacion":"texto con emojis"}}`;

  const text = await callClaude(sys, usr, 3000);
  return extractJSON(text);
}

async function getMarketIntel({ hs, hsDesc, exporterName, importerName, realData: rd }) {
  // Build context from real data
  let verifiedCtx = "";
  if (rd?.wto?.tariffs?.length > 0 && rd.wto.latestRate_pct != null) {
    verifiedCtx += `\nArancel MFN real (WTO ${rd.wto.lastUpdate}): ${rd.wto.latestRate_pct}%. Usa este valor en benchmark_precio y top_mercados.\n`;
  }
  if (rd?.comtrade?.data?.length > 0) {
    const totalValue = rd.comtrade.data.reduce((s, r) => s + (r.tradeValue_usd || 0), 0);
    if (totalValue > 0) {
      verifiedCtx += `\nValor comercio real (UN Comtrade ${rd.comtrade.period}): USD ${totalValue.toLocaleString()}.\n`;
    }
  }
  if (rd?.worldbankImporter?.data?.length > 0) {
    const gdp = rd.worldbankImporter.data[0];
    verifiedCtx += `\nPIB del importador (World Bank ${gdp.year}): USD ${gdp.value?.toLocaleString()}.\n`;
  }

  const sys = `Experto senior en comercio exterior y análisis de mercados globales. Responde SOLO con JSON puro. Sin texto extra. Sin markdown.`;
  const usr = `Inteligencia de mercado para:
- Producto: ${hsDesc} (HS: ${hs})
- Exportador: ${exporterName}
- Importador: ${importerName}
${verifiedCtx ? "\n=== DATOS REALES VERIFICADOS ===\n" + verifiedCtx : ""}

JSON con valores REALES. Incorpora los datos verificados donde corresponda:
{"opportunity_score":{"overall":7.5,"crecimiento_demanda":8,"nivel_competencia":6,"barreras_arancelarias":7,"tamaño_mercado":8,"estabilidad_precio":7,"facilidad_logistica":6},"top_mercados":[{"pais":"País 1","valor_importacion_musd":500,"crecimiento_pct":8.2,"oportunidad":"alta","arancel_pct":0},{"pais":"País 2","valor_importacion_musd":320,"crecimiento_pct":5.1,"oportunidad":"media","arancel_pct":5},{"pais":"País 3","valor_importacion_musd":280,"crecimiento_pct":12.0,"oportunidad":"muy alta","arancel_pct":3},{"pais":"País 4","valor_importacion_musd":200,"crecimiento_pct":-1.5,"oportunidad":"baja","arancel_pct":15},{"pais":"País 5","valor_importacion_musd":180,"crecimiento_pct":6.8,"oportunidad":"media","arancel_pct":8}],"estacionalidad":{"mejor_meses":["Enero","Febrero","Marzo"],"peor_meses":["Julio","Agosto"],"indice_meses":[70,80,90,75,60,50,40,45,65,80,85,75],"razon":"explicación de estacionalidad"},"documentos_exportacion":["Factura comercial","Lista de empaque","Certificado de origen"],"documentos_importacion":["Factura comercial","Lista de empaque","Declaración de importación"],"certificaciones":["Cert 1","Cert 2"],"barreras_no_arancelarias":["Barrera 1","Barrera 2"],"logistica":{"transporte_recomendado":"marítimo","incoterms_tipicos":["FOB","CIF"],"flete_estimado_usd_ton":150,"dias_transito":30,"puertos_origen":["Puerto 1"],"puertos_destino":["Puerto 2"],"consejo_logistico":"consejo con emoji"},"benchmark_precio":{"precio_origen_usd_kg":2.5,"promedio_global_usd_kg":3.0,"competitivo":true,"comentario":"análisis con emoji"},"perfil_comprador":{"tipo_empresa":"tipo de empresa importadora","volumen_minimo_ton":10,"forma_pago_tipica":"Carta de crédito","requisitos_calidad":"estándares requeridos"},"consejo_experto":"consejo estratégico de 2-3 oraciones con emojis"}

Rellena con datos reales para ${hsDesc}. Exportador: ${exporterName === "todos" ? "análisis global" : exporterName}. Importador: ${importerName === "todos" ? "análisis global" : importerName}.`;
  const text = await callClaude(sys, usr, 3000);
  return extractJSON(text);
}

// ─── ESTILOS ──────────────────────────────────────────────────────
const S = {
  app:     {fontFamily:"'Inter',system-ui,sans-serif",background:"#040D1A",minHeight:"100vh",padding:16},
  spin:    {width:20,height:20,border:"3px solid rgba(255,255,255,.1)",borderTop:"3px solid #0EA5A4",borderRadius:"50%",animation:"spin .8s linear infinite",flexShrink:0},
  bigSpin: {width:52,height:52,border:"5px solid rgba(255,255,255,.1)",borderTop:"5px solid #0EA5A4",borderRadius:"50%",animation:"spin .8s linear infinite",margin:"0 auto 20px"},
  card:    {maxWidth:600,margin:"0 auto",background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",borderRadius:24,padding:"32px 28px",boxShadow:"0 24px 60px rgba(0,0,0,.5)"},
  hdr:     {display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:24},
  back:    {background:"rgba(255,255,255,.06)",border:"1px solid rgba(255,255,255,.1)",borderRadius:10,padding:"8px 16px",cursor:"pointer",fontSize:13,color:"rgba(255,255,255,.7)"},
  pills:   {display:"flex",gap:6,alignItems:"center"},
  pill:    {width:10,height:10,borderRadius:"50%"},
  typBdg:  {fontSize:12,fontWeight:700,background:"rgba(14,165,164,.15)",color:"#0EA5A4",padding:"4px 12px",borderRadius:20,border:"1px solid rgba(14,165,164,.3)"},
  title:   {fontSize:24,fontWeight:900,color:"#fff",margin:"0 0 8px",letterSpacing:-.5},
  subt:    {fontSize:14,color:"rgba(255,255,255,.45)",margin:"0 0 20px",lineHeight:1.6},
  srchRow: {display:"flex",gap:8,marginBottom:18},
  inp:     {flex:1,padding:"13px 16px",borderRadius:12,border:"1px solid rgba(255,255,255,.12)",background:"rgba(255,255,255,.06)",fontSize:15,outline:"none",fontFamily:"'Inter',system-ui,sans-serif",color:"#fff"},
  srchBtn: {padding:"12px 20px",background:"#0EA5A4",color:"#fff",border:"none",borderRadius:12,cursor:"pointer",fontWeight:700,fontSize:14,whiteSpace:"nowrap"},
  orTxt:   {textAlign:"center",color:"rgba(255,255,255,.2)",fontSize:13,margin:"4px 0 12px"},
  qGrid:   {display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:8},
  qBtn:    {padding:"10px 12px",background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",borderRadius:10,cursor:"pointer",fontSize:13,color:"rgba(255,255,255,.7)",display:"flex",flexDirection:"column",gap:4,textAlign:"left"},
  hsTag:   {fontSize:10,color:"#0EA5A4",fontWeight:700,background:"rgba(14,165,164,.15)",padding:"2px 6px",borderRadius:4},
  ldRow:   {display:"flex",alignItems:"center",justifyContent:"center",gap:12,padding:24,color:"rgba(255,255,255,.45)",fontSize:14},
  sugBox:  {marginTop:8},
  sugLbl:  {fontSize:13,color:"#0EA5A4",fontWeight:700,marginBottom:10},
  sugBtn:  {width:"100%",display:"flex",alignItems:"center",gap:12,padding:"12px 16px",background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",borderRadius:12,cursor:"pointer",marginBottom:8,textAlign:"left"},
  sugCode: {fontSize:12,fontWeight:700,color:"#fff",background:"#0EA5A4",padding:"4px 8px",borderRadius:6,whiteSpace:"nowrap"},
  sugDesc: {fontSize:14,fontWeight:600,color:"#fff"},
  sugSec:  {fontSize:12,color:"rgba(255,255,255,.4)",marginTop:2},
  sugArr:  {marginLeft:"auto",color:"#0EA5A4",fontWeight:700},
  prodBar: {display:"flex",alignItems:"center",gap:8,padding:"10px 14px",background:"rgba(14,165,164,.08)",borderRadius:10,border:"1px solid rgba(14,165,164,.2)",fontSize:13,marginBottom:20,flexWrap:"wrap",color:"rgba(255,255,255,.7)"},
  hsBdg:   {background:"#0EA5A4",color:"#fff",padding:"2px 8px",borderRadius:4,fontSize:11,fontWeight:700},
  chgBtn:  {background:"none",border:"none",color:"#0EA5A4",cursor:"pointer",fontSize:12,textDecoration:"underline",marginLeft:"auto"},
  cGrid:   {display:"flex",alignItems:"center",gap:12,marginBottom:24,flexWrap:"wrap"},
  cCard:   {flex:1,minWidth:150},
  cLbl:    {fontSize:12,fontWeight:600,color:"rgba(255,255,255,.45)",marginBottom:6},
  cSel:    {width:"100%",padding:"10px 12px",borderRadius:10,border:"1px solid rgba(255,255,255,.12)",fontSize:14,background:"rgba(255,255,255,.06)",cursor:"pointer",color:"#fff"},
  flowArr: {fontSize:26,fontWeight:900,color:"#0EA5A4",paddingTop:20},
  nextBtn: {width:"100%",padding:16,background:"#0EA5A4",color:"#fff",border:"none",borderRadius:14,fontSize:16,fontWeight:800,cursor:"pointer",boxShadow:"0 4px 24px rgba(14,165,164,.4)"},
  warn:    {color:"#F87171",textAlign:"center",marginTop:8,fontSize:13},
  resWrap: {maxWidth:1100,margin:"0 auto",paddingBottom:48},
  resNav:  {display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,marginBottom:16,flexWrap:"wrap",background:"rgba(255,255,255,.04)",padding:"14px 20px",borderRadius:14,border:"1px solid rgba(255,255,255,.07)"},
  resTit:  {fontSize:18,fontWeight:800,color:"#fff",margin:"0 0 6px"},
  metaRow: {display:"flex",gap:6,flexWrap:"wrap"},
  metaTag: {fontSize:11,background:"rgba(14,165,164,.12)",color:"#0EA5A4",padding:"3px 10px",borderRadius:20,fontWeight:600,border:"1px solid rgba(14,165,164,.2)"},
  newBtn:  {background:"none",border:"1px solid rgba(255,255,255,.15)",color:"rgba(255,255,255,.7)",padding:"8px 16px",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:13,whiteSpace:"nowrap"},
  ldBox:   {background:"rgba(255,255,255,.04)",borderRadius:16,padding:"52px 24px",textAlign:"center",border:"1px solid rgba(255,255,255,.07)",marginBottom:20},
  ldTit:   {fontSize:18,fontWeight:700,color:"#fff",margin:"0 0 10px"},
  ldSub:   {fontSize:14,color:"rgba(255,255,255,.4)"},
  ldSteps: {marginTop:24,display:"inline-flex",flexDirection:"column",gap:10,textAlign:"left"},
  ldStep:  {fontSize:13,color:"rgba(255,255,255,.45)",display:"flex",gap:8,alignItems:"center"},
  errBox:  {background:"rgba(255,255,255,.04)",borderRadius:16,padding:"40px 24px",textAlign:"center",border:"2px solid rgba(248,113,113,.3)",marginBottom:20},
  kpiRow:  {display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(155px,1fr))",gap:12,marginBottom:16},
  kpi:     {background:"rgba(255,255,255,.04)",borderRadius:12,padding:"16px",border:"1px solid rgba(255,255,255,.07)"},
  kpiLbl:  {fontSize:10,color:"rgba(255,255,255,.4)",fontWeight:700,textTransform:"uppercase",letterSpacing:.5,marginBottom:4},
  kpiVal:  {fontSize:20,fontWeight:900,color:"#fff"},
  kpiSub:  {fontSize:11,color:"rgba(255,255,255,.3)",marginTop:2},
  box:     {background:"rgba(255,255,255,.04)",borderRadius:16,padding:"20px",border:"1px solid rgba(255,255,255,.07)",marginBottom:16},
  boxTit:  {fontSize:15,fontWeight:700,color:"#fff",marginBottom:4},
  boxSub:  {fontSize:12,color:"rgba(255,255,255,.4)",marginBottom:14},
  tbl:     {width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:780},
  th:      {background:"rgba(255,255,255,.06)",color:"rgba(255,255,255,.55)",padding:"10px 14px",textAlign:"left",fontWeight:700,fontSize:12,whiteSpace:"nowrap"},
  td:      {padding:"11px 14px",borderBottom:"1px solid rgba(255,255,255,.05)",verticalAlign:"top",color:"rgba(255,255,255,.8)"},
  extBox:  {background:"rgba(255,255,255,.04)",borderRadius:14,padding:"16px 18px",border:"1px solid rgba(255,255,255,.07)"},
  extTit:  {fontSize:14,fontWeight:700,color:"#fff",marginBottom:10},
  tagRow:  {display:"flex",flexWrap:"wrap",gap:8},
  tag:     {fontSize:12,padding:"4px 12px",borderRadius:20,fontWeight:600},
  anaGrid: {display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:14,marginBottom:16},
  anaCard: {background:"rgba(255,255,255,.04)",borderRadius:14,padding:"18px 16px",border:"1px solid rgba(255,255,255,.07)",borderTop:"4px solid"},
  anaTit:  {fontSize:13,fontWeight:700,marginBottom:8,color:"#fff"},
  anaTxt:  {fontSize:13,color:"rgba(255,255,255,.55)",lineHeight:1.7},
  note:    {background:"rgba(212,162,76,.08)",border:"1px solid rgba(212,162,76,.25)",borderRadius:12,padding:"12px 18px",marginBottom:16,fontSize:12,color:"rgba(255,255,255,.55)",lineHeight:1.6},
  foot:    {background:"rgba(14,165,164,.1)",border:"1px solid rgba(14,165,164,.2)",borderRadius:16,padding:"20px 24px",color:"#fff",display:"flex",alignItems:"center",justifyContent:"space-between",gap:16,flexWrap:"wrap"},
  footBtn: {background:"#0EA5A4",color:"#fff",border:"none",padding:"10px 22px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:13},
};

// ─── COMPONENTES COMPARTIDOS ──────────────────────────────────────
function StepHdr({ step, total, onBack, tradeType }) {
  return (
    <div style={S.hdr}>
      <button style={S.back} onClick={onBack}>← Atrás</button>
      <div style={S.pills}>
        {Array.from({length:total},(_,i)=>(
          <div key={i} style={{...S.pill,background:i<step?C.green:i===step-1?C.blue:"rgba(255,255,255,.15)"}}/>
        ))}
        <span style={{fontSize:12,color:C.gray,marginLeft:4}}>Paso {step}/{total}</span>
      </div>
      <div style={S.typBdg}>{tradeType==="X"?"Exportar":"Importar"}</div>
    </div>
  );
}

function ScoreBar({ label, value, color }) {
  return (
    <div style={{marginBottom:10}}>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}>
        <span style={{color:"rgba(255,255,255,.55)"}}>{label}</span>
        <span style={{fontWeight:700,color}}>{value}/10</span>
      </div>
      <div style={{background:"rgba(255,255,255,.08)",borderRadius:20,height:8,overflow:"hidden"}}>
        <div style={{width:`${value*10}%`,height:"100%",background:color,borderRadius:20,transition:"width .8s ease"}}/>
      </div>
    </div>
  );
}

function OpportunityBadge({ level }) {
  const map = {
    "muy alta":{bg:"rgba(16,185,129,.15)",color:C.green,icon:"🔥"},
    "alta":    {bg:"rgba(16,185,129,.15)",color:C.green,icon:"✅"},
    "media":   {bg:"rgba(244,163,0,.12)",  color:C.gold, icon:"⚡"},
    "baja":    {bg:"rgba(248,113,113,.12)",color:COLORS.mahoganyLight,icon:"⚠️"},
  };
  const s = map[(level||"").toLowerCase()]||map["media"];
  return <span style={{...S.tag,background:s.bg,color:s.color}}>{s.icon} {level}</span>;
}

function Tab({ label, icon, active, onClick, badge }) {
  return (
    <button onClick={onClick} style={{padding:"10px 16px",border:"none",cursor:"pointer",fontWeight:active?700:500,fontSize:13,borderBottom:active?`3px solid ${C.blue}`:"3px solid transparent",background:"none",color:active?C.blue:"#666",whiteSpace:"nowrap",position:"relative",transition:"color .15s",fontFamily:"'Inter',system-ui,sans-serif"}}>
      {icon} {label}
      {badge&&<span style={{position:"absolute",top:6,right:4,background:C.gold,color:C.dark,fontSize:9,fontWeight:800,padding:"1px 5px",borderRadius:10}}>{badge}</span>}
    </button>
  );
}

// ─── DATAPOINT: número con badge de fuente al hacer hover ─────────
function DataPoint({ value, source, updated, link, color }) {
  const [show, setShow] = useState(false);
  return (
    <span style={{position:"relative",display:"inline-flex",alignItems:"center",gap:3}}>
      <span style={{color:color||"inherit"}}>{value}</span>
      <button
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onFocus={() => setShow(true)}
        onBlur={() => setShow(false)}
        aria-label={`Fuente: ${source}`}
        style={{
          background:"none",border:"none",padding:0,cursor:"pointer",
          color:COLORS.teal,fontSize:10,lineHeight:1,flexShrink:0,
          display:"inline-flex",alignItems:"center",
        }}
      >
        ⊛
      </button>
      {show && (
        <div style={{
          position:"absolute",bottom:"calc(100% + 6px)",left:"50%",transform:"translateX(-50%)",
          background:COLORS.navy,color:COLORS.cloudDancer,
          borderRadius:8,padding:"8px 12px",fontSize:11,whiteSpace:"nowrap",
          boxShadow:"0 4px 16px rgba(0,0,0,0.25)",zIndex:100,
          lineHeight:1.5,
        }}>
          <div style={{fontWeight:700,color:COLORS.tealVibrant,marginBottom:3}}>{source}</div>
          {updated && <div style={{color:COLORS.grayLight}}>Actualizado: {updated}</div>}
          {link && (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              style={{color:COLORS.amber,fontSize:10,display:"block",marginTop:3}}
              onClick={e => e.stopPropagation()}
            >
              Ver fuente →
            </a>
          )}
          {/* Triángulo apuntando hacia abajo */}
          <div style={{
            position:"absolute",top:"100%",left:"50%",transform:"translateX(-50%)",
            width:0,height:0,
            borderLeft:"5px solid transparent",
            borderRight:"5px solid transparent",
            borderTop:`5px solid ${COLORS.navy}`,
          }}/>
        </div>
      )}
    </span>
  );
}

// ─── RESUMEN AGRUPADO: rankings completos paginados (/api/dian-rankings) ───
// Agrupa y suma en SQL sobre el 100% de las filas de dian_exp_detalle/dian_imp_detalle
// (sin tope de 30k/10k) y pagina el resultado ya completo. Nombres de comprador
// normalizados (kin_norm_nombre) y países fusionados (dian_pais_canon).
function RankingBlock({ tipo, hsCode, nit, anio, pais, subpartida, isPaid, onUpgrade }) {
  const esImp = tipo === 'imp';
  const dims = nit
    ? (esImp
        ? [['proveedor','Proveedores'], ['pais','Países origen'], ['subpartida','Productos']]
        : [['comprador','Compradores'], ['pais','Países destino'], ['subpartida','Productos']])
    : (esImp
        ? [['empresa','Importadores'], ['pais','Países origen'], ['proveedor','Proveedores'], ['subpartida','Subpartidas']]
        : [['empresa','Exportadores'], ['pais','Países destino'], ['comprador','Compradores'], ['subpartida','Subpartidas']]);
  const [dim, setDim]         = useState(dims[0][0]);
  const [page, setPage]       = useState(0);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const hsEfectivo = subpartida || hsCode || null;
  // Con subpartida completa (10 dígitos) el ranking por subpartida tendría 1 sola fila
  const hsDigits   = (hsEfectivo || '').replace(/\D/g, '');
  const anioNum    = anio ? parseInt(anio) : null;
  const depKey     = JSON.stringify([tipo, dim, hsEfectivo, nit, anioNum, pais, page]);
  const filtrosKey = JSON.stringify([tipo, dim, hsEfectivo, nit, anioNum, pais]);

  useEffect(() => { setPage(0); }, [filtrosKey]);
  useEffect(() => {
    if (!isPaid) return;
    let dead = false;
    setLoading(true); setError(null);
    fetch('/api/dian-rankings', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tipo, dimension: dim,
        hsCode: hsEfectivo || undefined, nit: nit || undefined,
        pais: pais || undefined, anio: anioNum || undefined,
        page, pageSize: 10,
      }),
    })
      .then(r => r.json())
      .then(d => { if (dead) return; if (d.error) setError(d.error); else setData(d); setLoading(false); })
      .catch(e => { if (!dead) { setError(e.message); setLoading(false); } });
    return () => { dead = true; };
  }, [depKey, isPaid]);                          

  const fmtPesoR = kg => kg >= 1e6 ? `${(kg/1e6).toLocaleString('es-CO',{maximumFractionDigits:1})} kt` : `${Math.round(kg/1e3).toLocaleString('es-CO')} t`;
  const dimLabel = dims.find(d => d[0] === dim)?.[1] || dim;
  const noRevelado = k => typeof k === 'string' && k.startsWith('(');

  const spark = (porAnio) => {
    const years = ['2023','2024','2025','2026'];
    const vals = years.map(y => Number(porAnio?.[y]) || 0);
    const max = Math.max(...vals, 1);
    const xy = vals.map((v, i) => [i*20+2, 22 - v/max*18]);
    const pts = xy.slice(0, 3).map(([x, y]) => `${x.toFixed(0)},${y.toFixed(1)}`).join(' ');
    return (
      <svg width="64" height="24" viewBox="0 0 64 24" style={{ flexShrink:0 }}>
        <title>Tendencia 2023-2026 · el tramo punteado es 2026, año parcial (solo primeros meses cargados)</title>
        <polyline points={pts} fill="none" stroke={COLORS.teal} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        {/* 2026 solo tiene los primeros meses cargados — tramo punteado y atenuado */}
        <line x1={xy[2][0]} y1={xy[2][1].toFixed(1)} x2={xy[3][0]} y2={xy[3][1].toFixed(1)}
          stroke={COLORS.teal} strokeWidth="1.6" strokeDasharray="3 2.5" strokeOpacity="0.45" strokeLinecap="round" />
      </svg>
    );
  };

  if (!isPaid) {
    return (
      <div onClick={onUpgrade} style={{ background:'rgba(255,255,255,.05)', borderRadius:14, border:'1px solid rgba(255,255,255,.1)', padding:'22px 24px', marginBottom:24, cursor:'pointer' }}>
        <div style={{ fontWeight:700, color:'#fff', fontSize:15, marginBottom:4 }}>🔒 Resumen agrupado completo</div>
        <div style={{ fontSize:13, color:COLORS.gray }}>
          {esImp ? 'Importadores, países origen y proveedores' : 'Exportadores, países destino y compradores'} —
          la lista completa con totales exactos, disponible en Plan Pro →
        </div>
      </div>
    );
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.totalCount / data.pageSize)) : 1;

  return (
    <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, border:'1px solid rgba(255,255,255,.1)', overflow:'hidden', marginBottom:24 }}>
      <div style={{ padding:'12px 20px', borderBottom:'1px solid rgba(255,255,255,.08)', background:'rgba(255,255,255,.04)', display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
        <div style={{ fontWeight:700, color:'#fff', fontSize:14, borderLeft:`3px solid ${COLORS.teal}`, paddingLeft:10 }}>Resumen agrupado</div>
        <div style={{ display:'flex', gap:4, background:'rgba(0,0,0,.25)', borderRadius:9, padding:3 }}>
          {dims.map(([k, lbl]) => {
            const disabled = (k === 'pais' && !!pais) || (k === 'subpartida' && (!!subpartida || hsDigits.length >= 10));
            return (
              <button key={k} onClick={() => !disabled && setDim(k)} disabled={disabled}
                title={disabled ? 'Ya filtraste por este campo' : undefined}
                style={{ padding:'5px 12px', border:'none', borderRadius:7, fontSize:12.5, fontWeight:600, cursor:disabled?'not-allowed':'pointer', fontFamily:'inherit',
                  background: dim===k ? COLORS.teal : 'transparent', color: dim===k ? '#04211f' : (disabled ? 'rgba(255,255,255,.25)' : COLORS.gray) }}>
                {lbl}
              </button>
            );
          })}
        </div>
        <div style={{ marginLeft:'auto', fontSize:11.5, color:COLORS.gray }}>
          {data ? <><strong style={{ color:'#fff' }}>{data.totalCount.toLocaleString('es-CO')}</strong> {dimLabel.toLowerCase()} · lista completa</> : ''}
        </div>
      </div>

      {loading && <div style={{ padding:'26px 20px', textAlign:'center', color:COLORS.gray, fontSize:13 }}>Calculando sobre todas las operaciones…</div>}
      {error && !loading && <div style={{ padding:'20px', color:COLORS.amber, fontSize:13 }}>⚠️ {error}</div>}

      {!loading && !error && data && data.rows.map((r, i) => {
        const hsName = dim === 'subpartida' ? getHSDescription(r.nombre) : null;
        return (
        <div key={r.clave || i} style={{ display:'flex', alignItems:'center', gap:12, padding:'9px 20px', borderBottom:'1px solid rgba(255,255,255,.06)',
          background: noRevelado(r.clave) ? 'rgba(224,163,57,.08)' : 'transparent' }}>
          <span style={{ fontSize:11.5, color:'rgba(255,255,255,.35)', width:30, flexShrink:0, fontVariantNumeric:'tabular-nums' }}>{data.page * data.pageSize + i + 1}</span>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:600, fontSize:13.5, color: noRevelado(r.clave) ? COLORS.amber : '#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={hsName ? `${r.nombre} — ${hsName}` : r.nombre}>
              {noRevelado(r.clave) ? 'Comprador no revelado (a la orden)' : r.nombre}
              {hsName && <span style={{ fontWeight:400, color:'rgba(255,255,255,.5)' }}> — {hsName}</span>}
            </div>
            <div style={{ fontSize:11, color:'rgba(255,255,255,.4)' }}>
              {r.pct}% del total
              {r.contrapartes > 1 && ` · ${r.contrapartes.toLocaleString('es-CO')} ${dim === 'empresa' ? (esImp ? 'proveedores' : 'compradores') : (esImp ? 'importadores' : 'exportadores')}`}
            </div>
          </div>
          {/* Con filtro de año el ranking solo trae ese año — no hay tendencia que graficar */}
          {!anioNum && spark(r.porAnio)}
          <div style={{ display:'flex', gap:16, flexShrink:0 }}>
            <div style={{ textAlign:'right', minWidth:56 }}>
              <div style={{ fontSize:12.5, fontWeight:700, color:'#fff', fontVariantNumeric:'tabular-nums' }}>{r.ops.toLocaleString('es-CO')}</div>
              <div style={{ fontSize:9, color:'rgba(255,255,255,.35)', textTransform:'uppercase', letterSpacing:'.05em' }}>Ops</div>
            </div>
            <div style={{ textAlign:'right', minWidth:60 }}>
              <div style={{ fontSize:12.5, fontWeight:700, color:'#fff', fontVariantNumeric:'tabular-nums' }}>{fmtPesoR(r.peso)}</div>
              <div style={{ fontSize:9, color:'rgba(255,255,255,.35)', textTransform:'uppercase', letterSpacing:'.05em' }}>Peso</div>
            </div>
            <div style={{ textAlign:'right', minWidth:66 }}>
              <div style={{ fontSize:12.5, fontWeight:700, color:'#2DD4D3', fontVariantNumeric:'tabular-nums' }}>{fmtUSD(r.valor)}</div>
              <div style={{ fontSize:9, color:'rgba(255,255,255,.35)', textTransform:'uppercase', letterSpacing:'.05em' }}>{esImp ? 'CIF' : 'FOB'}</div>
            </div>
          </div>
        </div>
        );
      })}

      {!loading && !error && data && data.rows.length === 0 && (
        <div style={{ padding:'22px 20px', textAlign:'center', color:COLORS.gray, fontSize:13 }}>Sin resultados con los filtros actuales.</div>
      )}

      {!loading && data && data.totalCount > 0 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, padding:'10px 20px', background:'rgba(255,255,255,.03)', flexWrap:'wrap' }}>
          <span style={{ fontSize:11.5, color:'rgba(255,255,255,.4)' }}>
            Mostrando {data.page * data.pageSize + 1}–{Math.min((data.page + 1) * data.pageSize, data.totalCount)} de {data.totalCount.toLocaleString('es-CO')} — nada se recorta
          </span>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              style={{ padding:'4px 11px', background:'transparent', border:'1px solid rgba(255,255,255,.15)', borderRadius:7, color: page===0 ? 'rgba(255,255,255,.2)' : '#fff', cursor: page===0 ? 'default' : 'pointer', fontSize:13 }}>‹</button>
            <span style={{ fontSize:12, color:COLORS.gray, fontVariantNumeric:'tabular-nums' }}>pág. {page + 1} de {totalPages.toLocaleString('es-CO')}</span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              style={{ padding:'4px 11px', background:'transparent', border:'1px solid rgba(255,255,255,.15)', borderRadius:7, color: page>=totalPages-1 ? 'rgba(255,255,255,.2)' : '#fff', cursor: page>=totalPages-1 ? 'default' : 'pointer', fontSize:13 }}>›</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Estacionalidad({ data }) {
  if(!data) return null;
  const indices = data.indice_meses||Array(12).fill(70);
  const max = Math.max(...indices);
  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(12,1fr)",gap:4,marginBottom:12}}>
        {MESES.map((m,i)=>{
          const h = Math.round((indices[i]/max)*60)+10;
          const isBest = (data.mejor_meses||[]).some(mes=>mes.toLowerCase().startsWith(m.toLowerCase().slice(0,3)));
          return (
            <div key={m} style={{textAlign:"center"}}>
              <div style={{height:70,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
                <div style={{width:"70%",height:h,background:isBest?C.green:C.sky,borderRadius:"4px 4px 0 0",opacity:.85}}/>
              </div>
              <div style={{fontSize:10,color:isBest?C.green:C.gray,fontWeight:isBest?700:400,marginTop:3}}>{m}</div>
            </div>
          );
        })}
      </div>
      {data.razon&&<div style={{marginTop:10,fontSize:12,color:"rgba(255,255,255,.55)",background:C.light,padding:"8px 12px",borderRadius:8}}>💡 {data.razon}</div>}
    </div>
  );
}

function Calculadora({ tradeData, marketIntel, dianData }) {
  // ── Valores de referencia desde datos reales ───────────────────
  const precioKgRef   = tradeData?.resumen?.precio_promedio_kg || dianData?.resumen?.promedioOperacion || 5;
  const arancelPct    = tradeData?.resumen?.arancel_aplicado_pct ?? 5;
  const fleteRefTon   = marketIntel?.logistica?.flete_estimado_usd_ton || 1200;
  const dianFobRef    = dianData?.resumen?.totalValorFobUsd && dianData?.resumen?.totalOperaciones
    ? Math.round(dianData.resumen.totalValorFobUsd / dianData.resumen.totalOperaciones * 100) / 100
    : null;

  // ── Inputs editables ───────────────────────────────────────────
  const [qty,         setQty]         = useState("1000");      // kg
  const [precioFob,   setPrecioFob]   = useState("");          // USD/kg
  const [precioVenta, setPrecioVenta] = useState("");          // USD/kg venta Colombia
  const [flete,       setFlete]       = useState("");          // USD/ton
  const [seguroPct,   setSeguroPct]   = useState("0.5");       // % del CIF
  const [aduana,      setAduana]      = useState("350");       // USD flat
  const [portuarios,  setPortuarios]  = useState("200");       // USD flat
  const [transInterno,setTransInterno]= useState("150");       // USD flat
  const [ivaBase,     setIvaBase]     = useState("19");        // %

  // ── Líneas dinámicas (toggle on/off) ──────────────────────────
  const [activo, setActivo] = useState({
    seguro:true, aduana:true, portuarios:true, transInterno:true, iva:true,
  });
  const toggle = k => setActivo(p=>({...p,[k]:!p[k]}));

  // ── Cálculos ───────────────────────────────────────────────────
  const qtyN        = parseFloat(qty)         || 0;
  const fobKg       = parseFloat(precioFob)   || precioKgRef;
  const fleteN      = parseFloat(flete)       || fleteRefTon;
  const ventaKg     = parseFloat(precioVenta) || fobKg * 1.4;

  const valorFob    = qtyN * fobKg;
  const valorFlete  = (qtyN / 1000) * fleteN;
  const valorSeguro = activo.seguro   ? (valorFob + valorFlete) * (parseFloat(seguroPct)||0) / 100 : 0;
  const valorCif    = valorFob + valorFlete + valorSeguro;
  const valorArancel= valorCif * arancelPct / 100;
  const valorIva    = activo.iva      ? (valorCif + valorArancel) * (parseFloat(ivaBase)||19) / 100 : 0;
  const valorAduana = activo.aduana   ? parseFloat(aduana)||0 : 0;
  const valorPortu  = activo.portuarios ? parseFloat(portuarios)||0 : 0;
  const valorTrans  = activo.transInterno ? parseFloat(transInterno)||0 : 0;

  const costoTotal  = valorFob + valorFlete + valorSeguro + valorArancel + valorIva + valorAduana + valorPortu + valorTrans;
  const ingresos    = qtyN * ventaKg;
  const ganancia    = ingresos - costoTotal;
  const margen      = ingresos > 0 ? ganancia / ingresos * 100 : 0;
  const costoUnitario = qtyN > 0 ? costoTotal / qtyN : 0;

  const fmt = v => `$${v >= 1000 ? (v/1000).toFixed(1)+"K" : v.toFixed(0)}`;
  const fmtKg = v => `$${v.toFixed(2)}/kg`;

  // ── Filas de la tabla dinámica ─────────────────────────────────
  const lineas = [
    { key:"fob",      icon:"📦", label:"Valor FOB (compra)",           val: valorFob,    color:"#0A2540", fixed:true,  tipo:"fob" },
    { key:"flete",    icon:"🚢", label:"Flete internacional",           val: valorFlete,  color:"#3B82F6", fixed:true,  tipo:"costo" },
    { key:"seguro",   icon:"🛡️", label:`Seguro (${seguroPct}% del CIF)`,val: valorSeguro, color:"#6366F1", fixed:false, tipo:"costo" },
    { key:"cif",      icon:"📊", label:"VALOR CIF (subtotal)",          val: valorCif,    color:"#0EA5A4", fixed:true,  tipo:"subtotal" },
    { key:"arancel",  icon:"⚖️", label:`Arancel (${arancelPct}%)`,      val: valorArancel,color:"#D4A24C", fixed:true,  tipo:"costo" },
    { key:"iva",      icon:"🧾", label:`IVA importación (${ivaBase}%)`, val: valorIva,    color:"#F59E0B", fixed:false, tipo:"costo" },
    { key:"aduana",   icon:"🏛️", label:"Agente de aduana (flat)",       val: valorAduana, color:"#8B5CF6", fixed:false, tipo:"costo" },
    { key:"portuarios",icon:"⚓",label:"Gastos portuarios / handling",   val: valorPortu,  color:"#EC4899", fixed:false, tipo:"costo" },
    { key:"trans",    icon:"🚛", label:"Transporte interno Colombia",    val: valorTrans,  color:"#10B981", fixed:false, tipo:"costo" },
    { key:"total",    icon:"🏷️", label:"COSTO TOTAL COLOMBIA",          val: costoTotal,  color:"#0A2540", fixed:true,  tipo:"total" },
  ];

  const inp = (val, set, ph, prefix="$") => (
    <input style={{...S.inp, padding:"6px 8px", fontSize:13, width:"100%", boxSizing:"border-box"}}
      placeholder={ph} value={val} onChange={e=>set(e.target.value)}/>
  );

  return (
    <div>
      {/* Banner datos reales */}
      {dianFobRef && (
        <div style={{background:"rgba(14,165,164,.1)",border:"1px solid rgba(14,165,164,.25)",borderRadius:10,padding:"10px 16px",marginBottom:16,fontSize:13,color:"#2DD4D3",display:"flex",gap:16,flexWrap:"wrap"}}>
          <span>🇨🇴 <strong>Referencia DIAN real:</strong> FOB promedio por operación: <strong>${dianFobRef.toLocaleString()}</strong></span>
          {dianData?.paisesOrigenTop?.[0] && <span>· Principal origen: <strong>{dianData.paisesOrigenTop[0].pais}</strong> ({dianData.paisesOrigenTop[0].porcentaje}%)</span>}
          {dianData?.resumen?.rangoFechas && <span>· Período: {dianData.resumen.rangoFechas}</span>}
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"340px 1fr",gap:16,alignItems:"start"}}>

        {/* Panel izquierdo: inputs */}
        <div style={S.box}>
          <div style={S.boxTit}>Parámetros de tu operación</div>
          <div style={S.boxSub}>Ajusta cada valor — los campos marcados con 🔒 son automáticos</div>

          <div style={{marginBottom:12}}>
            <label style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,.55)",display:"block",marginBottom:4}}>📦 Cantidad (kg)</label>
            {inp(qty, setQty, "Ej: 1000")}
          </div>
          <div style={{marginBottom:12}}>
            <label style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,.55)",display:"block",marginBottom:4}}>💵 Precio compra FOB (USD/kg)</label>
            {inp(precioFob, setPrecioFob, `Ref DIAN: $${precioKgRef.toFixed(2)}/kg`)}
          </div>
          <div style={{marginBottom:12}}>
            <label style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,.55)",display:"block",marginBottom:4}}>🏷️ Precio venta Colombia (USD/kg)</label>
            {inp(precioVenta, setPrecioVenta, `Estimado: $${(precioKgRef*1.4).toFixed(2)}/kg`)}
          </div>
          <div style={{marginBottom:12}}>
            <label style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,.55)",display:"block",marginBottom:4}}>🚢 Flete internacional (USD/ton)</label>
            {inp(flete, setFlete, `Ref: $${fleteRefTon}/ton`)}
          </div>

          <div style={{borderTop:"1px solid rgba(255,255,255,.1)",paddingTop:12,marginTop:4}}>
            <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,.55)",marginBottom:8}}>📋 Activa/desactiva costos adicionales:</div>
            {[
              {k:"seguro",    icon:"🛡️", label:"Seguro",           field:seguroPct,  set:setSeguroPct,  ph:"0.5", suffix:"%" },
              {k:"iva",       icon:"🧾", label:"IVA importación",  field:ivaBase,    set:setIvaBase,    ph:"19",  suffix:"%" },
              {k:"aduana",    icon:"🏛️", label:"Agente aduana",    field:aduana,     set:setAduana,     ph:"350", suffix:"USD flat" },
              {k:"portuarios",icon:"⚓", label:"Gastos portuarios", field:portuarios, set:setPortuarios, ph:"200", suffix:"USD flat" },
              {k:"transInterno",icon:"🚛",label:"Transporte interno",field:transInterno,set:setTransInterno,ph:"150",suffix:"USD flat"},
            ].map(({k,icon,label,field,set,ph,suffix})=>(
              <div key={k} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,opacity:activo[k]?1:0.4}}>
                <input type="checkbox" checked={activo[k]} onChange={()=>toggle(k)} style={{cursor:"pointer"}}/>
                <span style={{fontSize:12,flex:1}}>{icon} {label}</span>
                <input style={{...S.inp,padding:"4px 6px",fontSize:12,width:60,textAlign:"right"}}
                  disabled={!activo[k]} placeholder={ph} value={field} onChange={e=>set(e.target.value)}/>
                <span style={{fontSize:11,color:"rgba(255,255,255,.4)",width:50}}>{suffix}</span>
              </div>
            ))}
          </div>

          <div style={{background:"rgba(255,255,255,.04)",borderRadius:8,padding:"10px 12px",fontSize:11,color:"rgba(255,255,255,.4)",marginTop:8}}>
            🔒 Arancel: {arancelPct}% (fuente WTO/DIAN)
          </div>
        </div>

        {/* Panel derecho: tabla de costos + resultado */}
        <div>
          <div style={S.box}>
            <div style={S.boxTit}>Estructura de Costos — USD</div>
            <div style={S.boxSub}>Costo total para importar {(parseFloat(qty)||1000).toLocaleString()} kg de {tradeData?.resumen?.producto||"este producto"}</div>

            {lineas.map((l,i) => {
              if (!activo[l.key] && !l.fixed) return null;
              const isSubtotal = l.tipo==="subtotal";
              const isTotal    = l.tipo==="total";
              const isFob      = l.tipo==="fob";
              return (
                <div key={l.key} style={{
                  display:"flex", alignItems:"center", gap:10,
                  padding: isTotal?"12px 10px":"8px 10px",
                  borderRadius: isTotal||isSubtotal ? 8 : 0,
                  marginBottom: isTotal||isSubtotal ? 6 : 0,
                  borderBottom: !isTotal&&!isSubtotal ? "1px solid rgba(255,255,255,.06)" : "none",
                  background: isTotal?"rgba(14,165,164,.2)": isSubtotal?"rgba(14,165,164,.07)" : isFob?"rgba(255,255,255,.03)":"transparent",
                }}>
                  <span style={{fontSize:14,width:20}}>{l.icon}</span>
                  <span style={{flex:1,fontSize:13,fontWeight:isTotal||isSubtotal?700:400,color:isTotal?"#fff":l.color}}>
                    {l.label}
                  </span>
                  <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:13,fontWeight:isTotal?800:600,color:isTotal?"#fff":l.color}}>
                    {fmt(l.val)}
                  </span>
                  {/* Barra proporcional */}
                  {!isTotal&&!isSubtotal&&costoTotal>0&&(
                    <div style={{width:60,height:4,background:"rgba(255,255,255,.08)",borderRadius:2,overflow:"hidden",flexShrink:0}}>
                      <div style={{height:"100%",width:`${Math.min(l.val/costoTotal*100,100)}%`,background:l.color,borderRadius:2}}/>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Resultado final */}
          <div style={{...S.box,marginTop:14}}>
            <div style={S.boxTit}>💡 Resultado de la Operación</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:16}}>
              {[
                {lbl:"Costo unitario",  val:fmtKg(costoUnitario), color:"#fff"},
                {lbl:"Precio venta",    val:fmtKg(ventaKg),       color:COLORS.teal},
                {lbl:"Margen neto",     val:`${margen.toFixed(1)}%`, color:margen>15?COLORS.teal:margen>5?COLORS.amber:COLORS.mahogany},
              ].map(k=>(
                <div key={k.lbl} style={{textAlign:"center",background:"rgba(255,255,255,.04)",borderRadius:10,padding:"14px 8px"}}>
                  <div style={{fontSize:20,fontWeight:800,color:k.color,fontFamily:"'JetBrains Mono',monospace"}}>{k.val}</div>
                  <div style={{fontSize:11,color:COLORS.gray,marginTop:3}}>{k.lbl}</div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 16px",borderRadius:10,background:ganancia>=0?"rgba(16,185,129,.12)":"rgba(248,113,113,.1)"}}>
              <div>
                <div style={{fontWeight:800,fontSize:15,color:ganancia>=0?COLORS.teal:COLORS.mahogany}}>
                  {ganancia>=0?"✅ Operación rentable":"❌ Operación con pérdida"}
                </div>
                <div style={{fontSize:12,color:COLORS.gray,marginTop:2}}>
                  {margen>20?"🔥 Excelente margen":margen>12?"✅ Margen sólido":margen>5?"⚡ Margen ajustado — revisar costos":"⚠️ Margen insuficiente — considera renegociar"}
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:22,fontWeight:900,color:ganancia>=0?COLORS.teal:COLORS.mahogany,fontFamily:"'JetBrains Mono',monospace"}}>{fmt(ganancia)}</div>
                <div style={{fontSize:11,color:COLORS.gray}}>ganancia bruta</div>
              </div>
            </div>
            <div style={{marginTop:10,fontSize:11,color:COLORS.gray,lineHeight:1.6}}>
              ⚠️ Orientativo. Los valores reales dependen del agente de aduana, puerto de entrada, tipo de cambio y condiciones del proveedor. Valida con un agente certificado.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── BENTO CARD (homepage) ────────────────────────────────────────
function BentoCard({ product, idx, onSearch }) {
  const isLarge = idx === 0;
  const isPositive = product.change >= 0;
  const changeColor = !isPositive
    ? COLORS.mahogany
    : (product.bg === COLORS.navy || product.bg === COLORS.teal)
      ? COLORS.tealVibrant
      : COLORS.teal;

  const gridStyle =
    idx === 0 ? { gridColumn:"1 / 3", gridRow:"1 / 3" } :
    idx === 1 ? { gridColumn:"3",     gridRow:"1"     } :
    idx === 2 ? { gridColumn:"4",     gridRow:"1"     } :
    idx === 3 ? { gridColumn:"3",     gridRow:"2"     } :
    idx === 4 ? { gridColumn:"4",     gridRow:"2"     } :
                { gridColumn:"1 / 3", gridRow:"3"     };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSearch(product.code, product.name, "all", "all")}
      onKeyDown={e => e.key === "Enter" && onSearch(product.code, product.name, "all", "all")}
      style={{
        ...gridStyle,
        background: product.bg,
        border: product.bordered ? `1px solid ${COLORS.border}` : "none",
        borderRadius: 12,
        padding: isLarge ? 24 : 14,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        transition: "transform 0.15s, box-shadow 0.15s",
        overflow: "hidden",
        outline: "none",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 10px 28px rgba(0,0,0,0.18)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "";
      }}
    >
      <div>
        <div style={{fontFamily:"'JetBrains Mono','Courier New',monospace",fontSize:10,color:product.codeColor,letterSpacing:0.5,textTransform:"uppercase",marginBottom:4}}>
          HS {product.code}
        </div>
        <div style={{fontSize:isLarge?14:11,color:product.textColor,fontFamily:"'Inter',system-ui,sans-serif",fontWeight:500,lineHeight:1.3}}>
          {product.name}
        </div>
      </div>

      {isLarge && (
        <div style={{padding:"10px 0"}}>
          <svg width="84" height="30" viewBox="0 0 84 30" aria-hidden="true">
            <polyline points="0,6 14,9 28,8 42,14 56,19 70,22 84,26"
              stroke={COLORS.mahogany} strokeWidth="2.5" fill="none"
              strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )}

      <div>
        <div style={{fontFamily:"'JetBrains Mono','Courier New',monospace",fontSize:isLarge?28:17,fontWeight:500,color:product.textColor,lineHeight:1,marginBottom:4}}>
          {product.value}
        </div>
        <div style={{fontFamily:"'JetBrains Mono','Courier New',monospace",fontSize:isLarge?16:12,color:changeColor,fontWeight:500}}>
          {isPositive ? "↑" : "↓"} {Math.abs(product.change).toFixed(1)}%
        </div>
      </div>
    </div>
  );
}

// ─── Mapa rápido keyword → HS 4 dígitos ─────────────────────────
const KEYWORDS_HS = [
  { words:["carro","carros","auto","autos","automovil","automoviles","automóvil","automóviles","vehiculo","vehiculos","vehículo","vehículos","coche","coches","sedan","suv","pickup","jeep","camioneta","camionetas","turismo"],
    code:"8703", description:"Automóviles y vehículos de turismo", sector:"Transporte" },
  { words:["camion","camiones","camión","camiones","furgon","furgones","furgoneta","furgonetas","truck"],
    code:"8704", description:"Vehículos para transporte de mercancías", sector:"Transporte" },
  { words:["moto","motos","motocicleta","motocicletas","motociclo","bicicleta","bicicletas"],
    code:"8711", description:"Motocicletas y ciclomotores", sector:"Transporte" },
  { words:["cafe","café","coffee","cafe molido","cafe tostado"],
    code:"0901", description:"Café", sector:"Alimentos" },
  { words:["banano","bananos","platano","platanos","plátano","plátanos","banana"],
    code:"0803", description:"Bananos y plátanos frescos", sector:"Alimentos" },
  { words:["petroleo","petróleo","crudo","crude","oil","combustible"],
    code:"2709", description:"Aceites crudos de petróleo", sector:"Energía" },
  { words:["oro","gold","metal precioso"],
    code:"7108", description:"Oro y aleaciones de oro", sector:"Minerales" },
  { words:["computadora","computadoras","laptop","laptops","pc","ordenador","portatil","portátil"],
    code:"8471", description:"Computadoras y equipos informáticos", sector:"Tecnología" },
  { words:["celular","celulares","telefono","teléfono","telefonos","teléfonos","smartphone","movil","móvil"],
    code:"8517", description:"Teléfonos inteligentes", sector:"Tecnología" },
  { words:["medicamento","medicamentos","medicina","farmaco","farmaceutico","antibiotico","vacuna"],
    code:"3004", description:"Medicamentos", sector:"Salud" },
  { words:["azucar","azúcar","sugar","panela","melaza"],
    code:"1701", description:"Azúcar de caña o remolacha", sector:"Alimentos" },
  { words:["zapatos","tenis","botas","sandalias","calzado","zapatillas","shoes"],
    code:"6403", description:"Calzado con suela de caucho o plástico", sector:"Textil" },
  { words:["ropa","camisas","pantalones","vestidos","chaquetas","confecciones","prendas"],
    code:"6204", description:"Prendas de vestir y confecciones", sector:"Textil" },
  { words:["flores","rosas","claveles","orquideas","floricultura"],
    code:"0603", description:"Flores cortadas para ramos", sector:"Flores" },
  { words:["camaron","camarones","langosta","mariscos","pescado","tilapia","salmon","atun"],
    code:"0306", description:"Crustáceos y mariscos", sector:"Alimentos" },
  { words:["acero","hierro","varilla","lamina","acero inoxidable"],
    code:"7208", description:"Productos laminados planos de acero", sector:"Metales" },
  { words:["avion","aviones","aeronave","aircraft","helicoptero"],
    code:"8802", description:"Aeronaves y vehículos aéreos", sector:"Transporte" },
  { words:["maquinaria","maquinas","motor","motores","turbina","compresor","bomba"],
    code:"8431", description:"Partes de maquinaria industrial", sector:"Industria" },
  { words:["semiconductor","semiconductores","chip","chips","circuito integrado"],
    code:"8542", description:"Circuitos integrados electrónicos", sector:"Tecnología" },
  { words:["cacao","pasta de cacao","manteca de cacao","cocoa"],
    code:"1801", description:"Cacao en grano, entero o partido", sector:"Alimentos" },
  { words:["aceite de palma","palma","aceite vegetal"],
    code:"1511", description:"Aceite de palma y sus fracciones", sector:"Alimentos" },
  { words:["plastico","plasticos","pvc","polietileno","polipropileno"],
    code:"3901", description:"Polímeros de etileno y plásticos", sector:"Industria" },
];

function localLookup(query) {
  const q = query.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  for (const entry of KEYWORDS_HS) {
    if (entry.words.some(w => q.includes(w) || w.includes(q))) return entry;
  }
  return null;
}

// ─── Diccionario producto → HS ────────────────────────────────────
const CAPITULOS_HS = [
  { cap:'01', desc:'Animales vivos',                        palabras:['animales vivos','bovino','porcino','ovino','ganado','caballos','aves de corral'] },
  { cap:'02', desc:'Carne y despojos comestibles',          palabras:['carne','carne de res','carne de cerdo','carne de pollo','res','cerdo','despojos comestibles'] },
  { cap:'03', desc:'Pescados y crustáceos',                 palabras:['pescado','atun','salmon','camaron','langosta','tilapia','trucha','crustaceo','mariscos','langostino'] },
  { cap:'04', desc:'Leche y productos lácteos',             palabras:['leche','queso','mantequilla','yogur','lacteos','crema de leche','suero lacteo','huevos'] },
  { cap:'06', desc:'Plantas vivas y floricultura',          palabras:['flores','rosas','claveles','orquideas','plantas vivas','floricultura','follajes','bouquet'] },
  { cap:'07', desc:'Hortalizas y legumbres',                palabras:['zanahoria','papa','tomate','cebolla','ajo','lechuga','espinaca','brocoli','coliflor','pimenton','pepino','habichuela','arveja','lenteja','frijol','hortalizas','verduras','legumbres'] },
  { cap:'08', desc:'Frutas y frutos comestibles',           palabras:['mandarina','naranja','limon','lulo','mango','aguacate','uva','fresa','banano','platano','maracuya','uchuva','mora','guayaba','melon','sandia','papaya','pina','granadilla','gulupa','pitaya','fruta','manzana','pera','cereza','durazno','melocoton','citrico','kiwi','coco','tamarindo','tomate de arbol'] },
  { cap:'09', desc:'Café, té y especias',                   palabras:['cafe','te','canela','pimienta','vainilla','jengibre','curcuma','especias','cafe verde','cafe tostado','nuez moscada'] },
  { cap:'10', desc:'Cereales',                              palabras:['trigo','maiz','arroz','cebada','avena','sorgo','centeno','cereales','grano'] },
  { cap:'11', desc:'Productos de molinería',                palabras:['harina','almidon','fecula','gluten','harina de trigo','harina de maiz','salvado'] },
  { cap:'12', desc:'Semillas y frutos oleaginosos',         palabras:['soya','soja','girasol','palma','colza','ajonjoli','semillas','mani','copra'] },
  { cap:'15', desc:'Grasas y aceites animales o vegetales', palabras:['aceite de palma','aceite de soya','aceite de girasol','aceite vegetal','margarina','aceite de oliva','grasa animal'] },
  { cap:'17', desc:'Azúcar y artículos de confitería',      palabras:['azucar','cana de azucar','melaza','panela','miel','confiteria','dulces'] },
  { cap:'18', desc:'Cacao y sus preparaciones',             palabras:['cacao','pasta de cacao','manteca de cacao','chocolate','cocoa'] },
  { cap:'19', desc:'Preparaciones a base de cereales',      palabras:['galletas','pan','pasta','fideos','cereales preparados','tortillas','snacks','bizcocho'] },
  { cap:'20', desc:'Preparaciones de frutas y hortalizas',  palabras:['jugos de fruta','mermelada','conservas','pulpa de fruta','encurtidos','pure de tomate'] },
  { cap:'22', desc:'Bebidas y líquidos alcohólicos',        palabras:['ron','aguardiente','cerveza','vino','whisky','vodka','bebidas alcoholicas','agua embotellada','gaseosas','refrescos'] },
  { cap:'24', desc:'Tabaco y sucedáneos',                   palabras:['tabaco','cigarrillos','cigarros','tabaco sin elaborar'] },
  { cap:'25', desc:'Sal, yeso y minerales no metálicos',    palabras:['sal','yeso','cal','marmol','granito','arena','arcilla','calizas','cemento'] },
  { cap:'27', desc:'Combustibles y aceites minerales',      palabras:['petroleo','carbon','gas natural','gasolina','diesel','aceites minerales','hulla','coque','crudo'] },
  { cap:'28', desc:'Productos químicos inorgánicos',        palabras:['quimicos inorganicos','acido sulfurico','cloro','sosa caustica','amoniaco','fosfatos','peroxidos'] },
  { cap:'29', desc:'Productos químicos orgánicos',          palabras:['etanol','metanol','acetona','benceno','quimicos organicos','solventes','acidos organicos'] },
  { cap:'30', desc:'Productos farmacéuticos',               palabras:['medicamentos','vacunas','antibioticos','vitaminas','farmaceuticos','insulina','suero','medicina','drogas'] },
  { cap:'31', desc:'Fertilizantes',                         palabras:['fertilizantes','urea','abonos','nitrato','potasio','fosforo','agroquimicos','abono'] },
  { cap:'33', desc:'Aceites esenciales y cosméticos',       palabras:['perfumes','cosmeticos','cremas','maquillaje','jabon de tocador','desodorante','shampoo','protector solar'] },
  { cap:'34', desc:'Jabones y detergentes',                 palabras:['jabon','detergente','limpiadores','ceras','suavizante','blanqueador'] },
  { cap:'38', desc:'Productos químicos para agricultura',   palabras:['pesticidas','herbicidas','fungicidas','insecticidas','plaguicidas','agroquimicos'] },
  { cap:'39', desc:'Plásticos y sus manufacturas',          palabras:['plastico','pvc','polietileno','polipropileno','resinas','empaques plasticos','nylon'] },
  { cap:'40', desc:'Caucho y sus manufacturas',             palabras:['caucho','llantas','neumaticos','goma','latex','bandas de caucho'] },
  { cap:'41', desc:'Pieles y cueros en bruto',              palabras:['cuero','piel','cuero curtido','pieles en bruto','cuero bovino'] },
  { cap:'42', desc:'Manufacturas de cuero',                 palabras:['bolsos','maletas','carteras','articulos de cuero','cinturones'] },
  { cap:'44', desc:'Madera y sus manufacturas',             palabras:['madera','tableros','corcho','madera aserrada','chapas','listones'] },
  { cap:'48', desc:'Papel, cartón y sus manufacturas',      palabras:['papel','carton','cartulina','papel periodico','empaques de papel','cajas de carton'] },
  { cap:'52', desc:'Algodón',                               palabras:['algodon','hilo de algodon','tela de algodon','fibra de algodon'] },
  { cap:'61', desc:'Prendas de vestir de punto',            palabras:['ropa de punto','camisetas','medias','calcetines','tejidos de punto','sueteres'] },
  { cap:'62', desc:'Prendas de vestir excepto de punto',    palabras:['ropa','camisas','pantalones','vestidos','chaquetas','confecciones','prendas de vestir','uniformes'] },
  { cap:'64', desc:'Calzado',                               palabras:['zapatos','tenis','botas','sandalias','calzado','zapatillas'] },
  { cap:'71', desc:'Piedras y metales preciosos',           palabras:['esmeraldas','oro','plata','diamantes','joyas','platino','gemas','piedras preciosas'] },
  { cap:'72', desc:'Fundición, hierro y acero',             palabras:['acero','hierro','varilla','lamina de acero','acero inoxidable','ferroso','vigas'] },
  { cap:'76', desc:'Aluminio y sus manufacturas',           palabras:['aluminio','perfiles de aluminio','laminas de aluminio','aleaciones de aluminio'] },
  { cap:'84', desc:'Maquinaria y aparatos mecánicos',       palabras:['maquinaria','motores','bombas','compresores','turbinas','maquinas industriales','calderas','excavadoras'] },
  { cap:'85', desc:'Máquinas y aparatos eléctricos',        palabras:['electronicos','celulares','computadores','televisores','transformadores','cables electricos','electrodomesticos','paneles solares'] },
  { cap:'87', desc:'Vehículos automóviles',                 palabras:['carros','camiones','buses','motos','vehiculos','automoviles','tractores','remolques'] },
  { cap:'90', desc:'Instrumentos de óptica y precisión',    palabras:['lentes','instrumentos medicos','microscopios','camaras','equipos de medicion','escaner'] },
  { cap:'94', desc:'Muebles y sus partes',                  palabras:['muebles','sillas','camas','sofas','escritorios','estanterias'] },
  { cap:'95', desc:'Juguetes y artículos deportivos',       palabras:['juguetes','bicicletas','articulos deportivos','pelotas','juegos','patines'] },
];

const QUICK_BUTTONS = [
  { nombre:'Café',        hs:'09', capDesc:'Café, té y especias' },
  { nombre:'Mandarina',   hs:'08', capDesc:'Frutas y frutos comestibles' },
  { nombre:'Flores',      hs:'06', capDesc:'Plantas vivas y floricultura' },
  { nombre:'Cacao',       hs:'18', capDesc:'Cacao y sus preparaciones' },
  { nombre:'Medicamentos',hs:'30', capDesc:'Productos farmacéuticos' },
  { nombre:'Aluminio',    hs:'76', capDesc:'Aluminio y sus manufacturas' },
];

function buscarProductos(q) {
  if (!q || q.length < 2) return [];
  const norm = q.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const resultados = [];
  for (const cap of CAPITULOS_HS) {
    const descNorm = cap.desc.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const matchDesc = descNorm.includes(norm);
    const matchPal  = cap.palabras.find(p => p.normalize('NFD').replace(/[̀-ͯ]/g, '').includes(norm));
    if (matchDesc || matchPal) {
      const display = matchPal
        ? matchPal.charAt(0).toUpperCase() + matchPal.slice(1)
        : cap.desc;
      resultados.push({ nombre: display, hs: cap.cap, capDesc: cap.desc });
    }
  }
  return resultados.slice(0, 6);
}

// ─── RESET PASSWORD MODAL ─────────────────────────────────────────
function ResetPasswordModal({ onClose }) {
  const [pass, setPass] = useState('');
  const [msg,  setMsg]  = useState('');
  const [err,  setErr]  = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pass.length < 8) return setErr('La contraseña debe tener al menos 8 caracteres.');
    const { error } = await supabase.auth.updateUser({ password: pass });
    if (error) setErr(error.message);
    else { setMsg('¡Contraseña actualizada! Ya puede iniciar sesión.'); setTimeout(onClose, 2000); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:16, padding:32, width:360, boxShadow:'0 20px 60px rgba(0,0,0,.3)' }}>
        <div style={{ fontWeight:900, fontSize:20, color:'#0A2540', marginBottom:8 }}>Nueva contraseña</div>
        <p style={{ fontSize:13, color:'#64748B', marginBottom:20 }}>Ingrese su nueva contraseña para continuar.</p>
        {msg && <div style={{ background:'#d1fae5', color:'#065f46', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:12 }}>{msg}</div>}
        {err && <div style={{ background:'#fee2e2', color:'#991b1b', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:12 }}>{err}</div>}
        <form onSubmit={handleSubmit}>
          <input type="password" value={pass} onChange={e => setPass(e.target.value)}
            placeholder="Nueva contraseña (mín. 8 caracteres)"
            style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:14, marginBottom:16, boxSizing:'border-box', fontFamily:'inherit' }} />
          <button type="submit" style={{ width:'100%', padding:14, background:'#0A2540', color:'#fff', border:'none', borderRadius:10, fontSize:15, fontWeight:800, cursor:'pointer', fontFamily:'inherit' }}>
            Guardar contraseña
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── APP PRINCIPAL ────────────────────────────────────────────────
export default function App() {
  const { user, signOut, userPlan, fetchUserPlan } = useAuth();
  const isPaid = userPlan === 'pro';
  const [showLogin,   setShowLogin]   = useState(false);
  const [showReset,   setShowReset]   = useState(false);
  const [pagoOk,      setPagoOk]      = useState(false);
  const [pagoLoading, setPagoLoading] = useState(false);

  const onPagarPro = async (ciclo = 'monthly') => {
    if (!user) { setShowLogin(true); return; }
    setPagoLoading(true);
    try {
      const res = await fetch('/api/mp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, userId: user.id, ciclo }),
      });
      const data = await res.json();
      if (data.init_point) {
        window.location.href = data.init_point;
      } else {
        alert('Error al iniciar el pago. Intenta de nuevo.');
      }
    } catch {
      alert('Error de conexión. Intenta de nuevo.');
    } finally {
      setPagoLoading(false);
    }
  };

  useEffect(() => {
    // Detectar retorno exitoso de MercadoPago
    if (window.location.search.includes('pago=ok')) {
      setPagoOk(true);
      window.history.replaceState({}, '', '/');
      sessionStorage.setItem('kin_verify_pago', '1');
    }
    // Caso 1: evento llega después de que el componente monta
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setShowReset(true);
    });
    // Caso 2: Supabase ya procesó el hash antes de que React montara
    if (window.location.hash.includes('type=recovery')) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) setShowReset(true);
      });
    }
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    if (!sessionStorage.getItem('kin_verify_pago')) return;
    sessionStorage.removeItem('kin_verify_pago');
    fetch(`/api/mp?verify=${user.id}`)
      .then(r => r.json())
      .then(data => { if (data.plan === 'pro') fetchUserPlan(user.id); })
      .catch(() => {});
  }, [user, fetchUserPlan]);

  const [step,        setStep]        = useState(0);
  const [operation,   setOperation]   = useState('M'); // 'M'=Importar, 'X'=Exportar
  const [query,       setQuery]       = useState("");
  const [selHS,       setSelHS]       = useState(null);
  const [sugs,        setSugs]        = useState([]);
  const [loadHS,      setLoadHS]      = useState(false);
  const [origin,      setOrigin]      = useState(COUNTRIES[0]);
  const [dest,        setDest]        = useState(COUNTRIES[1]);
  const [tradeData,   setTradeData]   = useState(null);
  const [mktIntel,    setMktIntel]    = useState(null);
  const [realData,    setRealData]    = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [loadMsg,     setLoadMsg]     = useState("");
  const [error,       setError]       = useState(null);
  const [tab,         setTab]         = useState("resumen");
  const [dianData,    setDianData]    = useState(null);
  const [dianLoading, setDianLoading] = useState(false);
  const [liveStats,      setLiveStats]      = useState(null);
  const [staleBanner,    setStaleBanner]    = useState(null);
  const [showSourcePanel,setShowSourcePanel] = useState(false);
  const [companyNit,     setCompanyNit]     = useState(null);
  const [companyData,    setCompanyData]    = useState(null);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [perfilComprado, setPerfilComprado] = useState(false);
  const [perfilUnlocking,setPerfilUnlocking]= useState(false);
  const [countryName,         setCountryName]         = useState(null);
  const [countryBuscar,       setCountryBuscar]       = useState(null);
  const [countryData,         setCountryData]         = useState(null);
  const [countryLoading,      setCountryLoading]      = useState(false);
  const [countryYear,         setCountryYear]         = useState('');
  const [countryTipo,         setCountryTipo]         = useState('M'); // 'M'=importaciones 'X'=exportaciones
  const [countryDataAño,      setCountryDataAño]      = useState(null);
  const [countryLoadingAño,   setCountryLoadingAño]   = useState(false);
  const [countrySubpartida,   setCountrySubpartida]   = useState('');
  const [cmpInicio,      setCmpInicio]      = useState('2023-01');
  const [cmpFin,         setCmpFin]         = useState('2026-12');
  const [productHs,      setProductHs]      = useState(null);
  const [companyHsFilter,setCompanyHsFilter] = useState(null);
  const [productDesc,    setProductDesc]    = useState('');
  const [productData,    setProductData]    = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const [reqHs,          setReqHs]          = useState(null);
  const [reqDesc,        setReqDesc]        = useState('');
  const [reqData,        setReqData]        = useState(null);
  const [reqLoading,     setReqLoading]     = useState(false);
  const [reqOriginStep,  setReqOriginStep]  = useState(5);
  const [reqEsExportacion, setReqEsExportacion] = useState(false);
  const [reqSubSel,      setReqSubSel]      = useState('');
  const [analisisData,   setAnalisisData]   = useState(null);
  const [analisisLoading,setAnalisisLoading]= useState(false);
  const [wtoArancel,     setWtoArancel]     = useState(null);
  const [comtradeTop,    setComtradeTop]    = useState(null);
  const [comtradeWorld,  setComtradeWorld]  = useState(null);
  const [isDemo,         setIsDemo]         = useState(false);
  const [csvLoading,     setCsvLoading]     = useState(false);
  const [analisisMode,   setAnalisisMode]   = useState('producto');
  const [proveedorNombre,setProveedorNombre]= useState(null);
  const [proveedorData,  setProveedorData]  = useState(null);
  const [proveedorLoading,setProveedorLoading]= useState(false);


  // ── Logística ────────────────────────────────────────────────────
  const [logData,    setLogData]    = useState(null);
  const [logLoading, setLogLoading] = useState(false);
  const [logError,   setLogError]   = useState(null);
  const [logHs,      setLogHs]      = useState('');
  const [logBusca,   setLogBusca]   = useState('');
  const [logQ,       setLogQ]       = useState('');
  const [logSugs,    setLogSugs]    = useState([]);
  const [logSelected,setLogSelected]= useState(null);

  const reset = () => {
    setStep(0); setSelHS(null); setQuery(""); setSugs([]);
    setTradeData(null); setMktIntel(null); setRealData(null);
    setError(null); setTab("resumen"); setStaleBanner(null); setShowSourcePanel(false);
    setDianData(null); setDianLoading(false); // operation se conserva: al Volver el usuario mantiene su modo (M/X/P)
    setCompanyNit(null); setCompanyData(null); setCompanyLoading(false); setPerfilComprado(false);
    setCmpInicio('2023-01'); setCmpFin('2026-12');
    setProductHs(null); setProductDesc(''); setProductData(null); setProductLoading(false); setIsDemo(false);
    setReqHs(null); setReqDesc(''); setReqData(null); setReqLoading(false);
    setCountryTipo('M');
    setAnalisisData(null); setAnalisisLoading(false);
    setProveedorNombre(null); setProveedorData(null); setProveedorLoading(false);
  };

  const fetchTransportadoras = async (hsPrefix) => {
    setStep(10);
    setLogLoading(true);
    setLogError(null);
    setLogData(null);
    try {
      const res = await fetch('/api/empresas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'transportadoras', hsCode: hsPrefix || null }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      setLogData(d);
    } catch (err) {
      setLogError(err.message);
    } finally {
      setLogLoading(false);
    }
  };

  const fetchCompany = async (nit, inicio, fin, modeOverride, filterHs) => {
    const mi       = inicio || cmpInicio;
    const mf       = fin    || cmpFin;
    const effectiveMode = modeOverride || operation;
    const endpoint = effectiveMode === 'X' ? '/api/dian-exportaciones' : '/api/dian';
    const mfAdj    = effectiveMode === 'X' ? '2026-03' : mf;
    setCompanyNit(nit);
    setCompanyData(null);
    setCompanyLoading(true);
    setCompanyHsFilter(filterHs || null);
    setPerfilComprado(false);
    setStep(4);
    // Perfil suelto ($30, 1 año) — independiente del plan Pro. No bloquea la carga principal.
    if (user && supabase) {
      supabase.from('kin_perfiles_comprados')
        .select('fecha_vencimiento')
        .eq('nit', nit)
        .eq('user_id', user.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data?.fecha_vencimiento && new Date(data.fecha_vencimiento) > new Date()) {
            setPerfilComprado(true);
          }
        });
    }
    try {
      const body = { nit, mesInicio: mi, mesFin: mfAdj };
      if (filterHs) body.hsCode = filterHs;
      const r = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      setCompanyData(d);
    } catch {
      setCompanyData({ error: true });
    } finally {
      setCompanyLoading(false);
    }
  };

  const handleUnlockPerfil = async (nit, nombreEmpresa) => {
    if (!user) { setStep(0); return; } // sin sesión: que inicie sesión primero
    setPerfilUnlocking(true);
    try {
      const r = await fetch('/api/mp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'perfil', email: user.email, userId: user.id, nit, nombreEmpresa }),
      });
      const d = await r.json();
      if (d.init_point) window.location.href = d.init_point;
      else setPerfilUnlocking(false);
    } catch {
      setPerfilUnlocking(false);
    }
  };

  const fetchCountry = async (buscar, tipo = 'M', displayName) => {
    setCountryBuscar(buscar);
    setCountryName(displayName || buscar);
    setCountryData(null);
    setCountryDataAño(null);
    setCountryLoading(true);
    setCountryYear('');
    setCountrySubpartida('');
    setCountryTipo(tipo);
    setStep(8);
    try {
      const r = await fetch('/api/dian-pais', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paisOrigen: buscar, mesInicio: '2023-01', mesFin: '2026-12', tipo }),
      });
      const d = await r.json();
      setCountryData(d);
    } catch {
      setCountryData({ error: true });
    } finally {
      setCountryLoading(false);
    }
  };

  const fetchProduct = async (hsCode, hsDesc, retryAttempt = 0, opOverride = null, demoOverride = false) => {
    setProductHs(hsCode);
    setProductDesc(hsDesc || hsCode);
    setProductData(null);
    setProductLoading(true);
    setStep(5);
    const op       = opOverride || operation;
    const endpoint = op === 'X' ? '/api/dian-exportaciones' : '/api/dian';
    const mesInicio = op === 'X' ? '2023-01' : '2023-01';
    const mesFin    = op === 'X' ? '2026-03' : '2026-12';

    // Comtrade (precio/kg y mercado mundial) — se piden en paralelo con los datos
    // de DIAN, no después, para que "Mercado mundial" no aparezca tarde.
    if (retryAttempt === 0) {
      setComtradeTop(null);
      setComtradeWorld(null);

      fetch('/api/comtrade', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reporter: 'col', partner: 'wld', hsCode, flow: op === 'X' ? 'X' : 'M' }),
      }).then(r => r.json()).then(c => {
        if (!c?.data?.length) return;
        const totalVal = c.data.reduce((s, r) => s + (r.tradeValue_usd || 0), 0);
        const totalKg  = c.data.reduce((s, r) => s + (r.netWeight_kg  || 0), 0);
        if (totalVal > 0 && totalKg > 0) {
          const precio = totalVal / totalKg;
          const año    = c.data[0]?.year || '2024';
          setComtradeTop({ precioKg: precio, año });
        }
      }).catch(() => {});

      fetch('/api/comtrade-world', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hsCode }),
      }).then(r => r.json()).then(w => {
        if (w?.exporters?.top5?.length || w?.importers?.top5?.length) setComtradeWorld(w);
      }).catch(() => {});
    }

    try {
      const r = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hsCode, mesInicio, mesFin, ...((demoOverride || (isDemo && !user)) ? { demoMode: true } : {}) }),
      });
      if (!r.ok) { setProductData({ error: true }); return; }
      const d = await r.json();

      // Auto-fallback: si 0 resultados para subpartida de 6+ dígitos, intentar con los 4 dígitos
      if (!d.error && d.resumen?.totalOperaciones === 0) {
        const digits = hsCode.replace(/\D/g, '');
        if (digits.length > 4) {
          const heading4 = digits.slice(0, 4);
          const r2 = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ hsCode: heading4, mesInicio, mesFin }),
          });
          const d2 = await r2.json();
          if (!d2.error && d2.resumen?.totalOperaciones > 0) {
            setProductHs(heading4);
            setProductData({ ...d2, _fallbackFrom: hsCode });
            return;
          }
        }
      }

      // Reintento automático: si fobPorAño llega vacío PERO hay operaciones → el RPC falló
      // Si totalOperaciones === 0, no hay datos reales → no reintentar
      const rpcOk  = d.fobPorAño && d.fobPorAño.length > 0;
      const hasOps = (d.resumen?.totalOperaciones || 0) > 0;
      if (!rpcOk && hasOps && !d.error && retryAttempt === 0) {
        setProductData({ ...d, _retrying: true });
        await new Promise(res => setTimeout(res, 2500));
        return fetchProduct(hsCode, hsDesc, 1, opOverride, demoOverride);
      }

      setProductData({ ...d, _rpcFailed: !rpcOk && retryAttempt > 0 });

    } catch {
      setProductData({ error: true });
    } finally {
      setProductLoading(false);
    }
  };

  const fetchProveedor = (exportadorNombre) => {
    setProveedorNombre(exportadorNombre);
    setProveedorLoading(false);
    setStep(9);

    const detalle = productData?.detalleAgrupado || [];
    const rows = detalle.filter(r => r.exportador === exportadorNombre);

    if (rows.length === 0) {
      setProveedorData({ totalOps: 0, totalFob: 0 });
      return;
    }

    // Clientes colombianos por NIT
    const clientesMap = new Map();
    for (const row of rows) {
      const nit = row.nitImportador || 'SIN_NIT';
      if (!clientesMap.has(nit)) {
        clientesMap.set(nit, { nit, razonSocial: row.importador || nit, departamento: row.departamento || null, valorFobTotal: 0, numOps: 0, subpartidas: new Map(), ultimaCompra: null });
      }
      const c = clientesMap.get(nit);
      c.valorFobTotal += row.valorFob || 0;
      c.numOps        += row.numOps  || 1;
      if (!c.ultimaCompra || row.año > c.ultimaCompra) c.ultimaCompra = row.año;
      const sub = row.subpartida || 'N/A';
      if (!c.subpartidas.has(sub)) c.subpartidas.set(sub, { subpartida: sub, descripcion: row.descripcionOficial || null, fob: 0, ops: 0 });
      c.subpartidas.get(sub).fob += row.valorFob || 0;
      c.subpartidas.get(sub).ops += row.numOps   || 1;
    }

    const clientesTop = [...clientesMap.values()]
      .sort((a, b) => b.valorFobTotal - a.valorFobTotal)
      .map(c => ({
        nit: c.nit, razonSocial: c.razonSocial, departamento: c.departamento,
        valorFobTotal: Math.round(c.valorFobTotal * 100) / 100,
        numOps: c.numOps, ultimaCompra: c.ultimaCompra,
        subpartidas: [...c.subpartidas.values()].sort((a, b) => b.fob - a.fob)
          .map(s => ({ subpartida: s.subpartida, descripcion: s.descripcion, valorFob: Math.round(s.fob * 100) / 100, numOps: s.ops })),
      }));

    // Subpartidas agrupadas
    const subMap = new Map();
    for (const row of rows) {
      const sub = row.subpartida || 'N/A';
      if (!subMap.has(sub)) subMap.set(sub, { subpartida: sub, descripcion: row.descripcionOficial || null, fob: 0, ops: 0 });
      subMap.get(sub).fob += row.valorFob || 0;
      subMap.get(sub).ops += row.numOps   || 1;
    }
    const subpartidasTop = [...subMap.values()].sort((a, b) => b.fob - a.fob).slice(0, 10)
      .map(s => ({ subpartida: s.subpartida, descripcion: s.descripcion, valorFob: Math.round(s.fob * 100) / 100, numOps: s.ops }));

    // FOB por año
    const añoMap = new Map();
    for (const row of rows) {
      const año = row.año || 'N/A';
      if (!añoMap.has(año)) añoMap.set(año, { año, fob: 0, ops: 0 });
      añoMap.get(año).fob += row.valorFob || 0;
      añoMap.get(año).ops += row.numOps   || 1;
    }
    const fobPorAño = [...añoMap.values()].sort((a, b) => a.año.localeCompare(b.año))
      .map(a => ({ año: a.año, valorFobTotal: Math.round(a.fob * 100) / 100, numOperaciones: a.ops }));

    setProveedorData({
      exportador:   exportadorNombre,
      paisOrigen:   rows[0].pais || null,
      ciudadOrigen: rows[0].ciudadExportador || null,
      totalFob:     Math.round(rows.reduce((s, r) => s + (r.valorFob || 0), 0) * 100) / 100,
      totalOps:     rows.reduce((s, r) => s + (r.numOps || 1), 0),
      numClientes:  clientesMap.size,
      clientesTop,
      subpartidasTop,
      fobPorAño,
      tablaLimitada: false,
    });
  };

  const fetchAnalisisEmpresa = async () => {
    setAnalisisData(null);
    setAnalisisLoading(true);
    setAnalisisMode('empresa');
    setStep(7);
    try {
      const detalle = companyData?.detalleAgrupado || [];
      const partidasMap = {};
      detalle.forEach(r => {
        if (!r.subpartida) return;
        if (!partidasMap[r.subpartida]) partidasMap[r.subpartida] = { subpartida: r.subpartida, descripcion: r.descripcionOficial || '', valor: 0 };
        partidasMap[r.subpartida].valor += r.valorFob || 0;
      });
      const paisesMap = {};
      detalle.forEach(r => {
        if (!r.pais) return;
        if (!paisesMap[r.pais]) paisesMap[r.pais] = { pais: r.pais, valor: 0 };
        paisesMap[r.pais].valor += r.valorFob || 0;
      });
      const partidasTop = Object.values(partidasMap).sort((a, b) => b.valor - a.valor).slice(0, 8);
      const paisesTop   = Object.values(paisesMap).sort((a, b) => b.valor - a.valor).slice(0, 6);
      const r = await fetch('/api/analisis-empresa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nit:    companyNit,
          nombre: companyData?.empresasTop?.[0]?.razonSocial,
          resumen:         companyData?.resumen,
          fobPorAño:       companyData?.fobPorAño,
          partidasTop,
          paisesTop,
          esExportaciones: operation === 'X',
        }),
      });
      const d = await r.json();
      setAnalisisData(d);
    } catch {
      setAnalisisData({ error: true });
    } finally {
      setAnalisisLoading(false);
    }
  };

  const fetchAnalisis = (hsCode, hsDescription, data) => {
    setAnalisisMode('producto');

    const fobAnual      = (data?.fobPorAño || []).filter(r => (r.valorFobTotal || 0) > 0);
    const totalFob      = fobAnual.reduce((s, r) => s + (r.valorFobTotal || 0), 0);
    const totalOps      = fobAnual.reduce((s, r) => s + (r.numOperaciones  || 0), 0);
    const totalEmpresas = data?.resumen?.totalEmpresas || data?.empresasTop?.length || 0;

    const completos = fobAnual.filter(r => ['2023','2024','2025'].includes(r.año)).sort((a,b) => a.año.localeCompare(b.año));
    const mejorAño  = [...completos].sort((a,b) => (b.valorFobTotal||0) - (a.valorFobTotal||0))[0];
    const peorAño   = [...completos].sort((a,b) => (a.valorFobTotal||0) - (b.valorFobTotal||0))[0];
    const año2023   = completos.find(r => r.año === '2023');
    const año2025   = completos.find(r => r.año === '2025');

    let crecPct = null;
    if (año2023?.valorFobTotal > 0 && año2025?.valorFobTotal > 0)
      crecPct = ((año2025.valorFobTotal - año2023.valorFobTotal) / año2023.valorFobTotal * 100).toFixed(1);

    const tendencia = crecPct === null ? 'estable'
      : parseFloat(crecPct) > 10 ? 'creciente'
      : parseFloat(crecPct) < -10 ? 'decreciente' : 'estable';

    const paises   = (data?.paisesOrigenTop || data?.paisesTop || []).slice(0, 5).map(p => ({ nombre: p.pais || p.nombre, fob: p.valorFobTotal || p.fob || 0 }));
    const empresas = (data?.empresasTop || []).slice(0, 5).map(e => ({ nombre: e.nombre || e.razonSocial || '—', fob: e.valorFobTotal || e.fob || 0 }));
    const topPais  = paises[0];

    const esExp  = operation === 'X';
    const dirV   = esExp ? 'exportó' : 'importó';
    const dirProv = esExp ? 'destino' : 'proveedor';
    const dirEmp  = esExp ? 'exportadoras' : 'importadoras';
    const fmt    = v => `$${(v/1e6).toFixed(1)}M`;

    let resumen = `Colombia ${dirV} ${fmt(totalFob)} USD en ${hsDescription} (${hsCode}) entre 2023 y 2026`;
    if (totalOps > 0) resumen += `, en ${totalOps.toLocaleString()} declaraciones`;
    resumen += '.';
    if (mejorAño && peorAño && mejorAño.año !== peorAño.año)
      resumen += ` El año más alto fue ${mejorAño.año} (${fmt(mejorAño.valorFobTotal)}) y el más bajo ${peorAño.año} (${fmt(peorAño.valorFobTotal)}).`;
    if (crecPct !== null) {
      const signo = parseFloat(crecPct) >= 0 ? '+' : '';
      resumen += ` De 2023 a 2025 el mercado ${parseFloat(crecPct) >= 0 ? 'creció' : 'cayó'} un ${signo}${crecPct}%.`;
    }
    if (topPais) resumen += ` Principal ${dirProv}: ${topPais.nombre} (${fmt(topPais.fob)}).`;
    if (totalEmpresas > 0) resumen += ` Participan ${totalEmpresas} empresa${totalEmpresas !== 1 ? 's' : ''} ${dirEmp}.`;

    setAnalisisData({ resumen, tendencia, crecPct, paises, empresas, totalFob, totalOps, totalEmpresas, mejorAño, peorAño });
    setStep(7);
  };

  const fetchRequisitos = async (hsCode, hsDescription, originStep = 5, esExportacion = false) => {
    setReqOriginStep(originStep);
    setReqHs(hsCode);
    setReqDesc(hsDescription || hsCode);
    setReqData(null);
    setReqLoading(true);
    setReqEsExportacion(esExportacion);
    setStep(6);
    try {
      const r = await fetch('/api/requisitos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hsCode, hsDesc: hsDescription, esExportacion }),
      });
      const d = await r.json();
      setReqData(d);
    } catch {
      setReqData({ error: true });
    } finally {
      setReqLoading(false);
    }
  };

  // ── Cargar estadísticas live de World Bank en la homepage ─────────
  useEffect(() => {
    if (step !== 0) return;
    Promise.allSettled([
      fetch("/api/worldbank?country=WLD&indicator=NE.EXP.GNFS.CD").then(r => r.json()),
      fetch("/api/worldbank?country=WLD&indicator=NE.EXP.GNFS.KD.ZG").then(r => r.json()),
    ]).then(([expRes, growthRes]) => {
      const expData    = expRes.status    === "fulfilled" ? expRes.value    : null;
      const growthData = growthRes.status === "fulfilled" ? growthRes.value : null;

      const expVal    = expData?.data?.[0]?.value;
      const growthVal = growthData?.data?.[0]?.value;
      const lastYear  = expData?.data?.[0]?.year;

      if (expVal || growthVal) {
        setLiveStats({
          exports:    expVal    ? fmtUSD(expVal)  : null,
          growth:     growthVal ? fmtPct(growthVal) : null,
          lastYear,
          source:     "World Bank",
          sourceUrl:  "https://data.worldbank.org/indicator/NE.EXP.GNFS.CD",
          updatedOn:  expData?.lastUpdate,
        });
      }
    });
  }, [step]);

  const buildSugs = (s, q) => {
    const digits = q.replace(/\D/g, '');
    const directOption = digits.length >= 4
      ? [{ code: digits.slice(0, 4), description: `Partida ${digits.slice(0,4)} · todas las subpartidas`, sector: 'Búsqueda directa' }]
      : [];
    const filtered = s.filter(x => x.code.replace(/\D/g,'').slice(0,4) !== digits.slice(0,4));
    return [...directOption, ...filtered].slice(0, 5);
  };

  const autoSelect = (hs) => {
    setSelHS({ code: hs.code, description: hs.description || hs.desc, desc: hs.description || hs.desc });
    setStep(2);
    setLoadHS(false);
  };

  const searchHS = async () => {
    if(!query.trim()) return;
    // Primero intenta match local instantáneo
    const local = localLookup(query);
    if (local) { autoSelect(local); return; }
    setLoadHS(true); setSugs([]);
    try {
      const s = await getHSCodes(query);
      if (s.length > 0) { autoSelect(s[0]); return; }
      const local2 = localLookup(query);
      if (local2) { autoSelect(local2); return; }
      setSugs(buildSugs(SAMPLE_HS.slice(0,4), query));
    } catch {
      const local2 = localLookup(query);
      if (local2) { autoSelect(local2); return; }
      setSugs(buildSugs(SAMPLE_HS.slice(0,4), query));
    }
    setLoadHS(false);
  };

  const heroSearch = async () => {
    if(!query.trim()) return;
    const local = localLookup(query);
    if (local) { setStep(1); autoSelect(local); return; }
    setLoadHS(true); setSugs([]);
    setStep(1);
    try {
      const s = await getHSCodes(query);
      if (s.length > 0) { autoSelect(s[0]); return; }
      const local2 = localLookup(query);
      if (local2) { autoSelect(local2); return; }
      setSugs(buildSugs(SAMPLE_HS.slice(0,4), query));
    } catch {
      const local2 = localLookup(query);
      if (local2) { autoSelect(local2); return; }
      setSugs(buildSugs(SAMPLE_HS.slice(0,4), query));
    }
    setLoadHS(false);
  };

  const quickSearch = (hsCode, hsDesc, originIso, destIso) => {
    const sel = { code:hsCode, description:hsDesc, desc:hsDesc };
    const org = originIso === "all" ? TODOS : COUNTRIES.find(c=>c.iso===originIso)||TODOS;
    const dst = destIso   === "all" ? TODOS : COUNTRIES.find(c=>c.iso===destIso)  ||TODOS;
    setSelHS(sel);
    setOrigin(org);
    setDest(dst);
    setStep(3);
  };

  const runFetch = async () => {
    setLoading(true); setError(null); setTradeData(null); setMktIntel(null);
    setRealData(null); setStaleBanner(null);

    const msgs = [
      "🔌 Consultando UN Comtrade, WTO y World Bank...",
      "📊 Procesando estadísticas oficiales...",
      "🌍 Analizando mercados globales...",
      "⚖️ Verificando aranceles y acuerdos...",
      "🤖 Generando inteligencia de mercado con datos reales...",
    ];
    let i = 0; setLoadMsg(msgs[0]);
    const iv = setInterval(() => { i = (i+1) % msgs.length; setLoadMsg(msgs[i]); }, 2800);

    try {
      const exp = operation === 'X' ? origin : dest;
      const imp = operation === 'X' ? dest   : origin;

      const params = {
        hs:           selHS.code,
        hsDesc:       selHS.description || selHS.desc,
        exporterName: exp.iso === "all" ? "todos" : stripFlag(exp.name),
        importerName: imp.iso === "all" ? "todos" : stripFlag(imp.name),
      };

      // ── PASO 1: Datos reales en paralelo ─────────────────────────
      setLoadMsg("🔌 Consultando APIs oficiales: UN Comtrade · WTO · World Bank...");
      const rd = await fetchRealData({
        hs:          selHS.code,
        exporterIso: exp.iso,
        importerIso: imp.iso,
      });
      setRealData(rd);

      // Avisar si los datos son de caché viejo
      if (rd.comtrade?.stale) {
        setStaleBanner(`Datos UN Comtrade del ${rd.comtrade.lastUpdate} (caché — API temporalmente no disponible)`);
      }
      if (rd.wto?.stale) {
        setStaleBanner(prev => prev
          ? prev + " · WTO también en caché"
          : `Datos WTO en caché del ${rd.wto.lastUpdate}`);
      }

      // ── PASO 2: Análisis IA con datos reales de contexto ─────────
      setLoadMsg("🤖 Interpretando datos verificados con IA...");
      const [td, mi] = await Promise.all([
        getTradeData({ ...params, realData: rd }),
        getMarketIntel({ ...params, realData: rd }),
      ]);

      setTradeData(td);
      setMktIntel(mi);

      // ── PASO 3: Datos DIAN — siempre que haya HS code ───────────
      if (selHS?.code) {
        setDianLoading(true);
        fetch("/api/dian", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            hsCode:    selHS.code.slice(0, 4),
            mesInicio: "2024-01",
            mesFin:    "2026-02",
          }),
        })
          .then(r => r.json())
          .then(d => { setDianData(d); setDianLoading(false); })
          .catch(() => setDianLoading(false));
      }
    } catch(e) {
      setError(`Error al obtener datos: ${e.message}`);
    }

    clearInterval(iv);
    setLoading(false);
  };

  useEffect(() => { if (step === 3) runFetch(); }, [step]);

  // Cuando el usuario selecciona un año y/o un producto en step 8, re-consultar filtrado
  useEffect(() => {
    if (!countryYear && !countrySubpartida) { setCountryDataAño(null); return; }
    if (!countryBuscar) { setCountryDataAño(null); return; }
    const mesInicio = countryYear ? `${countryYear}-01` : '2023-01';
    const mesFin    = countryYear ? `${countryYear}-12` : '2026-12';
    setCountryLoadingAño(true);
    setCountryDataAño(null);
    fetch('/api/dian-pais', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paisOrigen: countryBuscar, mesInicio, mesFin, tipo: countryTipo, hsCode: countrySubpartida || undefined }),
    })
      .then(r => r.json())
      .then(d => setCountryDataAño(d))
      .catch(() => setCountryDataAño(null))
      .finally(() => setCountryLoadingAño(false));
  }, [countryYear, countrySubpartida, countryBuscar, countryTipo]);

  const exp = operation === 'X' ? origin : dest;
  const imp = operation === 'X' ? dest   : origin;
  const tabla     = tradeData?.tabla || [];
  const res       = tradeData?.resumen || {};
  const ana       = tradeData?.analisis || {};
  const score     = mktIntel?.opportunity_score || {};
  const tMkts     = mktIntel?.top_mercados || [];

  // Fuentes de datos para tooltips
  const comtradeUpdated = realData?.comtrade?.lastUpdate || "2024";
  const comtradeUrl     = realData?.comtrade?.sourceUrl  || "https://comtradeplus.un.org/";
  const wtoUpdated      = realData?.wto?.lastUpdate      || "2024";
  const wtoUrl          = realData?.wto?.sourceUrl       || "https://www.wto.org/english/res_e/statis_e/statis_e.htm";
  const hasRealComtrade = realData?.comtrade?.data?.length > 0;
  const hasRealWTO      = realData?.wto?.tariffs?.length  > 0;

  // Set of years with verified Comtrade data — must be before chartData
  const realYears = new Set((realData?.comtrade?.data || []).map(d => String(d.year)));

  const chartData = [...tabla].sort((a,b)=>a.año-b.año).map(r=>({
    año:     String(r.año),
    fob:     Math.round((r.fob_usd||0)/1e6*100)/100,
    fobReal: realYears.has(String(r.año)) ? Math.round((r.fob_usd||0)/1e6*100)/100 : null,
    precio:  r.precio_unitario_usd_kg||0,
  }));
  const topData = tMkts.slice(0,6).map(m=>({name:m.pais||"—",value:m.valor_importacion_musd||0}));

  // CAMBIO 3: Deep links to original data sources
  const comtradeDeepLink = hasRealComtrade
    ? `https://comtradeplus.un.org/TradeFlow?Frequency=A&Flows=${operation}&CommodityCodes=${selHS?.code || ""}&Partners=${realData?.comtrade?.partnerCode || "0"}&Reporters=${realData?.comtrade?.reporterCode || "0"}&period=${new Date().getFullYear() - 1}&AggregateBy=none&BreakdownMode=plus`
    : comtradeUrl;
  const wtoDeepLink = hasRealWTO && realData?.wto?.reporterCode
    ? `https://tariffdata.wto.org/ReportersAndProducts.aspx?Reporter=${realData.wto.reporterCode}&Product=${String(selHS?.code || "").slice(0, 4)}`
    : wtoUrl;
  const worldbankDeepLink = `https://data.worldbank.org/indicator/NE.EXP.GNFS.CD?locations=${exp.iso === "all" ? "1W" : exp.iso.toUpperCase()}`;

  const involucraCol        = exp.iso === "col" || imp.iso === "col";
  const colombiaEsImportador = imp.iso === "col";

  const TABS=[
    {id:"resumen",       icon:"📊", label:"Resumen"},
    {id:"competidores",  icon:"🏆", label:"Mis Competidores"},
    {id:"proveedores",   icon:"🌏", label:"Proveedores"},
    {id:"requisitos",    icon:"📁", label:"Requisitos"},
    {id:"analisis",      icon:"🤖", label:"Análisis IA", proOnly:true},
  ];

  // ── LANDING (PASO 0) ──────────────────────────────────────────────
  if(step===0) return (
    <>
      {showReset && <ResetPasswordModal onClose={() => setShowReset(false)} />}
      {showLogin && !showReset && <LoginPage onClose={() => setShowLogin(false)} />}
      {pagoOk && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:20, padding:'40px 48px', textAlign:'center', maxWidth:400, boxShadow:'0 24px 80px rgba(0,0,0,.3)' }}>
            <div style={{ fontSize:48, marginBottom:16 }}>🎉</div>
            <h2 style={{ fontSize:24, fontWeight:900, color:'#0A2540', marginBottom:8 }}>¡Pago exitoso!</h2>
            <p style={{ fontSize:15, color:'#64748B', marginBottom:24, lineHeight:1.6 }}>Tu plan Pro está siendo activado. En unos segundos tendrás acceso completo.</p>
            <button onClick={() => { setPagoOk(false); window.location.reload(); }} style={{ background:'#0EA5A4', color:'#fff', border:'none', borderRadius:10, padding:'12px 32px', fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
              Entrar a KIN →
            </button>
          </div>
        </div>
      )}
      <LandingPage
        query={query}
        setQuery={setQuery}
        operation={operation}
        setOperation={setOperation}
        isDemo={isDemo}
        onLoginClick={() => setShowLogin(true)}
        user={user}
        onSignOut={signOut}
        isPaid={isPaid}
        onSearch={(params) => {
          if (!user) { setShowLogin(true); return; }
          if (params && params.nit) {
            if (params.mode) setOperation(params.mode);
            fetchCompany(params.nit, null, null, params.mode);
          } else if (params && params.pais) {
            fetchCountry(params.pais, params.paisTipo || 'M', params.paisDisplay);
          } else if (params && params.hsCode) {
            fetchProduct(params.hsCode, params.hsName);
          } else {
            heroSearch();
          }
        }}
        onLogistica={() => { if (!user) { setShowLogin(true); return; } setStep(10); setLogData(null); setLogError(null); setLogHs(''); setLogBusca(''); setLogQ(''); setLogSugs([]); setLogSelected(null); }}
        onCalculadora={() => {}}
        onDemo={() => { setIsDemo(true); setOperation('X'); fetchProduct('0603.11', 'Rosas frescas · Flores', 0, 'X', true); }}
        onPagarPro={onPagarPro}
      />
    </>
  );


  // ── PASO 1: BÚSQUEDA DE PRODUCTO ──────────────────────────────────
  if(step===1) return (
    <div style={S.app}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={S.card}>
        <StepHdr step={1} total={3} onBack={()=>setStep(0)} tradeType={operation}/>
        <h2 style={S.title}>{operation==="X"?"¿Qué vas a exportar?":"¿Qué vas a importar?"}</h2>
        <p style={S.subt}>Describe tu producto con palabras normales. La IA lo clasifica con el código HS oficial para encontrar sus datos reales de comercio mundial.</p>
        <div style={S.srchRow}>
          <input style={S.inp} placeholder="Ej: café molido, mango fresco, calzado de cuero..." value={query} onChange={e=>setQuery(e.target.value)} onKeyDown={e=>e.key==="Enter"&&searchHS()}/>
          <button style={S.srchBtn} onClick={searchHS} disabled={loadHS||!query.trim()}>{loadHS?"Buscando…":"Buscar"}</button>
        </div>
        {!loadHS&&sugs.length===0&&(<><p style={S.orTxt}>— o elige un producto común —</p><div style={S.qGrid}>{SAMPLE_HS.map(hs=>(<button key={hs.code} style={S.qBtn} onClick={()=>{setSelHS(hs);setStep(2);}}>{hs.desc}<span style={S.hsTag}>HS {hs.code}</span></button>))}</div></>)}
        {loadHS&&<div style={S.ldRow}><div style={S.spin}/><span>Clasificando con IA...</span></div>}
        {sugs.length>0&&(<div style={S.sugBox}><p style={S.sugLbl}>¿Cuál describe mejor tu producto?</p>{sugs.map(hs=>(<button key={hs.code} style={S.sugBtn} onClick={()=>{setSelHS({...hs,description:hs.description||hs.desc});setStep(2);}}><span style={S.sugCode}>HS {hs.code}</span><div><div style={S.sugDesc}>{hs.description||hs.desc}</div>{hs.sector&&<div style={S.sugSec}>{hs.sector}</div>}</div><span style={S.sugArr}>→</span></button>))}</div>)}
      </div>
    </div>
  );

  // ── PASO 2: SELECCIÓN DE PAÍSES ───────────────────────────────────
  if(step===2) return (
    <div style={S.app}>
      <div style={S.card}>
        <StepHdr step={2} total={3} onBack={()=>setStep(1)} tradeType={operation}/>
        <h2 style={S.title}>¿Entre qué países?</h2>
        <p style={S.subt}>Selecciona los países. Consultaremos UN Comtrade, WTO y World Bank con datos reales.</p>
        <div style={S.prodBar}>
          <span>Producto:</span><strong>{selHS?.description||selHS?.desc}</strong>
          <span style={S.hsBdg}>HS {selHS?.code}</span>
          <button style={S.chgBtn} onClick={()=>setStep(1)}>Cambiar</button>
        </div>
        <div style={S.cGrid}>
          <div style={S.cCard}>
            <div style={S.cLbl}>{operation==="X"?"País EXPORTADOR (origen)":"País EXPORTADOR (proveedor)"}</div>
            <select style={S.cSel} value={origin.iso} onChange={e=>{
              if(e.target.value==="all") setOrigin(TODOS);
              else setOrigin(COUNTRIES.find(c=>c.iso===e.target.value)||COUNTRIES[0]);
            }}>
              <option value="all">Todos los países (análisis global)</option>
              <option disabled>──────────────</option>
              {COUNTRIES.map(c=><option key={c.iso} value={c.iso}>{c.name}</option>)}
            </select>
          </div>
          <div style={S.flowArr}>→</div>
          <div style={S.cCard}>
            <div style={S.cLbl}>{operation==="X"?"País IMPORTADOR (destino)":"País IMPORTADOR (tuyo)"}</div>
            <select style={S.cSel} value={dest.iso} onChange={e=>{
              if(e.target.value==="all") setDest(TODOS);
              else setDest(COUNTRIES.find(c=>c.iso===e.target.value)||COUNTRIES[1]);
            }}>
              <option value="all">Todos los países (análisis global)</option>
              <option disabled>──────────────</option>
              {COUNTRIES.map(c=><option key={c.iso} value={c.iso}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <button style={S.nextBtn} onClick={()=>setStep(3)} disabled={origin.iso===dest.iso && origin.iso!=="all"}>Generar Análisis Completo →</button>
        {origin.iso===dest.iso && origin.iso!=="all" &&<p style={S.warn}>⚠️ El país de origen y destino no pueden ser el mismo.</p>}
      </div>
    </div>
  );

  // ── PASO 5: PRODUCTO / PARTIDA ARANCELARIA ───────────────────────
  if (step === 5) {
    if (showLogin) return <LoginPage onClose={() => setShowLogin(false)} />;
    const detalle  = productData?.detalleAgrupado || [];
    const fobAño   = productData?.fobPorAño       || [];
    const totalFob = productData?.resumen?.totalValorFobUsd || 0;
    const maxFob   = fobAño.reduce((m, r) => Math.max(m, r.valorFobTotal), 1);

    // Clientes colombianos únicos por proveedor extranjero (calculado del detalle disponible)
    const clientesPorProveedor = new Map();
    detalle.forEach(r => {
      if (!r.exportador) return;
      if (!clientesPorProveedor.has(r.exportador)) clientesPorProveedor.set(r.exportador, new Set());
      clientesPorProveedor.get(r.exportador).add(r.nitImportador || r.importador || '?');
    });


    const años     = fobAño.length > 0
      ? fobAño.map(r => r.año).sort().reverse()
      : [...new Set(detalle.map(r => r.año))].sort().reverse();
    const paises   = [...new Set(detalle.map(r => r.pais))].sort();
    const empresas = [...new Set(detalle.map(r => r.importador).filter(Boolean))].sort();

    const ui    = productData?._ui || { año:'', mes:'', pais:'', empresa:'', subpartida:'', busca:'', col:'valorFob', dir:'desc', page:0 };
    const setUI = (patch) => setProductData(prev => ({ ...prev, _ui: { ...(prev._ui || ui), ...patch, page: patch.page ?? 0 } }));

    const subpartidasP = [...new Set(detalle.map(r => r.subpartida).filter(Boolean))].sort();

    let filas = detalle.filter(r => {
      if (ui.año        && r.año        !== ui.año)        return false;
      if (ui.mes        && r.mes        !== ui.mes)        return false;
      if (ui.pais       && r.pais       !== ui.pais)       return false;
      if (ui.empresa    && r.importador !== ui.empresa)    return false;
      if (ui.subpartida && r.subpartida !== ui.subpartida) return false;
      if (ui.busca) {
        const b = ui.busca.toLowerCase();
        if (!r.importador?.toLowerCase().includes(b) &&
            !r.exportador?.toLowerCase().includes(b) &&
            !r.subpartida?.toLowerCase().includes(b) &&
            !(r.descripcionOficial||'').toLowerCase().includes(b)) return false;
      }
      return true;
    });

    const col = ui.col || 'valorFob';
    const dir = ui.dir || 'desc';
    filas = [...filas].sort((a, b) => {
      const va = a[col] ?? 0, vb = b[col] ?? 0;
      if (typeof va === 'string') return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return dir === 'asc' ? va - vb : vb - va;
    });

    const PAGE_SIZE  = 50;
    const page       = ui.page || 0;
    const totalPags  = Math.ceil(filas.length / PAGE_SIZE);
    const filasVis   = isPaid
      ? filas.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
      : filas.slice(0, 5);
    // Cuando no hay filtros extra (país/empresa/etc.), usar fobPorAño para valores exactos
    const noExtraFilters = !ui.pais && !ui.empresa && !ui.subpartida && !ui.busca;
    const selAnio = noExtraFilters && ui.año ? fobAño.find(r => r.año === ui.año) : null;
    const kpiFob = selAnio
      ? selAnio.valorFobTotal
      : (noExtraFilters && !ui.año && fobAño.length > 0)
          ? fobAño.reduce((s, r) => s + r.valorFobTotal, 0)
          : filas.reduce((s, r) => s + r.valorFob, 0);
    const kpiOps = selAnio
      ? selAnio.numOperaciones
      : (noExtraFilters && !ui.año && fobAño.length > 0)
          ? fobAño.reduce((s, r) => s + r.numOperaciones, 0)
          : filas.reduce((s, r) => s + (r.numOps || 0), 0);
    const kpiPaises  = filas.length > 0
      ? [...new Set(filas.map(r => r.pais))]
      : (productData?.paisesOrigenTop || []).map(p => p.pais);
    const kpiPaisesCount = selAnio
      ? (selAnio.numPaises || kpiPaises.length)
      : (noExtraFilters && !ui.año && productData?.resumen?.totalPaises)
          ? productData.resumen.totalPaises
          : (noExtraFilters && fobAño.length > 0)
              ? Math.max(...fobAño.map(r => r.numPaises || 0))
              : kpiPaises.length;
    const kpiEmpresas = [...new Set(filas.map(r => r.importador).filter(Boolean))];
    const kpiEmpresasCount = (noExtraFilters && !ui.año && productData?.resumen?.totalEmpresas)
      ? productData.resumen.totalEmpresas
      : kpiEmpresas.length;

    const SortHdr = ({ label, col: c }) => {
      const active = col === c;
      return (
        <th onClick={() => setUI({ col: c, dir: active && dir === 'desc' ? 'asc' : 'desc' })}
          style={{ padding:'10px 12px', textAlign:'left', color: active ? COLORS.teal : COLORS.gray,
            fontWeight:600, borderBottom:`2px solid ${active ? COLORS.teal : '#e2e8f0'}`,
            cursor:'pointer', userSelect:'none', whiteSpace:'nowrap', fontSize:12 }}>
          {label} {active ? (dir === 'desc' ? '↓' : '↑') : '↕'}
        </th>
      );
    };

    const isExp = operation === 'X';

    const exportCSV = async () => {
      const q = v => `"${String(v??'').replace(/"/g,'""')}"`;

      if (!isExp) {
        // Importaciones: detalleAgrupado viene del RPC ya agrupado → completo
        const hdrs = ['Año','Empresa importadora','NIT','País origen','Exportador','Ciudad','Subpartida','Descripción','Ops','CIF USD'];
        const csvRows = filas.map(r => [
          r.año, q(r.importador), q(r.nitImportador||''), q(r.pais),
          q(r.exportador), q(r.ciudadExportador||''), q(r.subpartida),
          q(r.descripcionOficial||''), r.numOps||0, r.valorFob
        ]);
        const csv  = [hdrs, ...csvRows].map(r => r.join(',')).join('\n');
        const blob = new Blob(['﻿' + csv], { type:'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a'); a.href = url;
        a.download = `KIN_HS_${productHs}_${ui.año||'todos'}.csv`;
        a.click(); URL.revokeObjectURL(url);
        return;
      }

      // Exportaciones: API completa sin límite PER_YEAR
      setCsvLoading(true);
      try {
        const mesIni = ui.año ? `${ui.año}-01` : '2023-01';
        const mesFin = ui.año ? `${ui.año}-12` : '2026-03';
        const nitEmp = ui.empresa
          ? detalle.find(r => r.importador === ui.empresa)?.nitImportador || null
          : null;
        const resp = await fetch('/api/dian-exportaciones', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'exportar-csv',
            hsCode: ui.subpartida || productHs,
            nit: nitEmp,
            paisDestino: ui.pais || null,
            mesInicio: mesIni,
            mesFin,
          }),
        });
        if (!resp.ok) throw new Error('Error descargando datos');
        const { rows } = await resp.json();
        const hdrs = ['Año','Empresa exportadora','NIT','País destino','Destinatario','Depto. origen','Subpartida','Descripción','Ops','FOB USD'];
        const csvRows = rows.map(r => [
          r.año, q(r.empresa), q(r.nit), q(r.pais),
          q(r.destinatario), q(r.depto), q(r.subpartida),
          q(r.descripcion), r.ops, r.fob
        ]);
        const csv  = [hdrs, ...csvRows].map(r => r.join(',')).join('\n');
        const blob = new Blob(['﻿' + csv], { type:'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a'); a.href = url;
        a.download = `KIN_HS_${productHs}_${ui.año||'todos'}_completo.csv`;
        a.click(); URL.revokeObjectURL(url);
      } catch (err) {
        console.error('CSV export error:', err);
      } finally {
        setCsvLoading(false);
      }
    };

    return (
      <div style={{ minHeight:'100vh', background:'#040D1A', fontFamily:"'Inter',system-ui,sans-serif" }}>
        <style>{`
          @keyframes spin{to{transform:rotate(360deg)}}
          .tbl-row:hover td { background:rgba(14,165,164,.06) !important; }
          .flt-sel { padding:7px 10px; border:1.5px solid rgba(255,255,255,.12); border-radius:8px; font-size:13px; background:rgba(255,255,255,.06); cursor:pointer; outline:none; font-family:inherit; color:#fff; }
          .flt-sel:focus { border-color:${COLORS.teal}; }
        `}</style>

        <div style={{ background:COLORS.navy, padding:'14px 28px', display:'flex', alignItems:'center', gap:14, position:'sticky', top:0, zIndex:100 }}>
          <button onClick={() => isDemo && !user ? (setStep(0), setQuery(''), setSugs([])) : reset()} style={{ background:'rgba(255,255,255,.12)', border:'none', color:'#fff', padding:'7px 14px', borderRadius:7, cursor:'pointer', fontSize:13, fontFamily:'inherit', fontWeight:600 }}>
            ← Volver
          </button>
          <div style={{ color:'#fff', fontWeight:800, fontSize:17, letterSpacing:-0.5 }}>K<span style={{ color:COLORS.teal }}>IN</span></div>
          <div style={{ color:'rgba(255,255,255,.45)', fontSize:13 }}>{isExp ? 'Exportaciones' : 'Importaciones'} Colombia · Partida {productHs}</div>
          <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:10 }}>
            {user ? (
              <>
                <span style={{ color:'rgba(255,255,255,.5)', fontSize:12 }}>{user.email}</span>
                <button onClick={signOut} style={{ background:'rgba(255,255,255,.12)', border:'none', color:'#fff', padding:'7px 14px', borderRadius:7, cursor:'pointer', fontSize:12, fontFamily:'inherit', fontWeight:600 }}>
                  Cerrar sesión
                </button>
              </>
            ) : (
              <button onClick={() => setShowLogin(true)} style={{ background:COLORS.teal, border:'none', color:'#fff', padding:'8px 18px', borderRadius:8, cursor:'pointer', fontSize:13, fontFamily:'inherit', fontWeight:700 }}>
                Iniciar sesión
              </button>
            )}
          </div>
        </div>

        <div style={{ maxWidth:1200, margin:'24px auto', padding:'0 20px' }}>

          {productLoading && (
            <div style={{ textAlign:'center', padding:'80px 0' }}>
              <div style={{ width:48, height:48, border:'4px solid rgba(255,255,255,.1)', borderTop:`4px solid ${COLORS.teal}`, borderRadius:'50%', animation:'spin .9s linear infinite', margin:'0 auto 20px' }} />
              <p style={{ color:COLORS.gray, fontSize:15 }}>
                {productData?._retrying ? 'Reintentando consulta…' : `Consultando DIAN para partida ${productHs}…`}
              </p>
            </div>
          )}

          {!productLoading && productData?._rpcFailed && (
            <div style={{ background:'rgba(212,162,76,.1)', border:'1px solid rgba(212,162,76,.3)', borderRadius:12, padding:'16px 24px', marginBottom:16, display:'flex', alignItems:'center', justifyContent:'space-between', gap:16 }}>
              <div style={{ color:'rgba(255,255,255,.75)', fontSize:14 }}>⚠️ Los datos se cargaron parcialmente. Puede haber un error de conexión.</div>
              <button onClick={() => fetchProduct(productHs, productDesc)}
                style={{ background:COLORS.teal, color:'#fff', border:'none', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                ↺ Reintentar
              </button>
            </div>
          )}

          {!productLoading && productData?.error && (
            <div style={{ background:'rgba(248,113,113,.1)', border:'1px solid rgba(248,113,113,.2)', borderRadius:12, padding:24, textAlign:'center', color:'#f87171' }}>
              No se pudo obtener información. Intenta con otro código.
              {productData?.detalle && <div style={{ fontSize:11, marginTop:8, opacity:0.7, fontFamily:'monospace', wordBreak:'break-all' }}>{productData.detalle}</div>}
            </div>
          )}

          {!productLoading && productData && !productData.error && productData.resumen?.totalOperaciones === 0 && (() => {
            const heading4 = (productHs || '').replace(/\D/g,'').slice(0,4);
            return (
            <div style={{ background:'rgba(255,255,255,.04)', borderRadius:16, padding:40, textAlign:'center', border:'1px solid rgba(255,255,255,.08)' }}>
              <div style={{ fontSize:48, marginBottom:16 }}>🔍</div>
              <div style={{ fontSize:18, fontWeight:700, color:"#fff", marginBottom:8 }}>Sin {isExp ? 'exportaciones' : 'importaciones'} para partida {productHs} (2023–2026)</div>
              <div style={{ color:COLORS.gray, fontSize:14, marginBottom:20, maxWidth:420, margin:'0 auto 20px' }}>
                Es posible que este producto esté clasificado en una subpartida diferente. Puedes intentar con la partida principal o con otra subpartida.
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
                {heading4 && (
                  <button onClick={() => fetchProduct(heading4, productDesc)}
                    style={{ background:'rgba(255,255,255,.08)', color:"#fff", border:'1px solid rgba(255,255,255,.15)', borderRadius:10, padding:'10px 20px', fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                    Buscar en partida principal {heading4}
                  </button>
                )}
                <button onClick={reset} style={{ background:COLORS.teal, color:'#fff', border:'none', borderRadius:10, padding:'10px 20px', fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>← Nueva búsqueda</button>
              </div>
            </div>
            );
          })()}

          {!productLoading && productData && !productData.error && productData.resumen?.totalOperaciones > 0 && (<>

            {/* Aviso fallback */}
            {productData?._fallbackFrom && (
              <div style={{ background:'rgba(212,162,76,.1)', border:'1px solid rgba(212,162,76,.3)', borderRadius:10, padding:'10px 16px', marginBottom:12, fontSize:13, color:'rgba(255,255,255,.75)' }}>
                ⚠️ La subpartida <strong>{productData._fallbackFrom}</strong> no tiene datos en DIAN. Mostrando resultados para la partida principal <strong>{productHs}</strong>.
              </div>
            )}

            {/* HERO CARD */}
            <div style={{ background:`linear-gradient(135deg, ${COLORS.navy} 0%, ${COLORS.navyLight} 100%)`, borderRadius:16, padding:'28px 32px', marginBottom:20, border:'1px solid rgba(255,255,255,.06)' }}>
              <div style={{ fontSize:22, fontWeight:800, color:'#fff', letterSpacing:-.3, marginBottom:4 }}>{productDesc}</div>
              <div style={{ fontSize:13, color:COLORS.grayLight, marginBottom:20 }}>Partida {productHs} · {isExp ? 'Exportaciones' : 'Importaciones'} registradas en Colombia · DIAN</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:14 }}>
                {[
                  { lbl: operation === 'X' ? 'FOB TOTAL' : 'CIF TOTAL', val: fmtUSD(kpiFob), color: COLORS.tealVibrant },
                  { lbl: 'DECLARACIONES', val: kpiOps.toLocaleString(), color: '#fff' },
                  { lbl: operation === 'X' ? 'PAÍSES DESTINO' : 'PAÍSES ORIGEN', val: String(kpiPaisesCount), color: COLORS.amber },
                  { lbl: 'EMPRESAS', val: String(kpiEmpresasCount), color: '#fff' },
                ].map(({ lbl, val, color }) => (
                  <div key={lbl} style={{ background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.1)', borderRadius:10, padding:'14px 18px' }}>
                    <div style={{ fontSize:10, color:COLORS.grayLight, textTransform:'uppercase', letterSpacing:1, marginBottom:6, fontWeight:600 }}>{lbl}</div>
                    <div style={{ fontFamily:"'JetBrains Mono','Courier New',monospace", fontSize:22, fontWeight:700, color }}>{val}</div>
                  </div>
                ))}
                {comtradeTop?.precioKg != null && (
                  <div style={{ background:'rgba(139,92,246,.12)', border:'1px solid rgba(139,92,246,.3)', borderRadius:10, padding:'14px 18px' }}>
                    <div style={{ fontSize:10, color:'#a78bfa', textTransform:'uppercase', letterSpacing:1, marginBottom:6, fontWeight:600 }}>
                      Precio/kg <span style={{ background:COLORS.teal, color:'#fff', borderRadius:3, padding:'1px 5px', fontSize:8, fontWeight:800, marginLeft:4 }}>Comtrade</span>
                    </div>
                    <div style={{ fontFamily:"'JetBrains Mono','Courier New',monospace", fontSize:22, fontWeight:700, color:'#a78bfa' }}>${comtradeTop.precioKg.toFixed(2)}</div>
                    <div style={{ fontSize:10, color:'rgba(167,139,250,.6)', marginTop:4 }}>USD/kg · {comtradeTop.año} (último disp.)</div>
                  </div>
                )}
              </div>
            </div>

            {/* BARRA DE ACCIONES */}
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center', marginBottom:20 }}>
              <select value={ui.año||''} onChange={e => setUI({ año: e.target.value })}
                style={{ padding:'9px 14px', border:'1.5px solid rgba(255,255,255,.1)', borderRadius:9, fontSize:13, background:'rgba(255,255,255,.06)', cursor:'pointer', outline:'none', fontFamily:'inherit', fontWeight:600, color:"#fff" }}>
                <option value="">Todos los años</option>
                {años.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              <div style={{ width:1, height:26, background:'rgba(255,255,255,.15)', flexShrink:0 }} />
              {/* Requisitos e Informe IA: siempre gratis — decisión 2026-08-04, ya no dependen de isPaid */}
              <button onClick={() => fetchRequisitos(productHs, productDesc, 5, isExp)}
                style={{ background:COLORS.teal, color:'#fff', border:'none', borderRadius:9, padding:'9px 18px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap' }}>
                📋 {isExp ? 'Requisitos de exportación' : 'Requisitos de importación'}
              </button>
              <button onClick={() => fetchAnalisis(productHs, productDesc, productData)}
                style={{ background:'rgba(255,255,255,.08)', color:'#fff', border:'1px solid rgba(255,255,255,.15)', borderRadius:9, padding:'9px 18px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap' }}>
                📄 Informe IA
              </button>
            </div>

            {/* FOB POR AÑO */}
            {fobAño.length > 0 && (
              <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, marginBottom:16, border:'1px solid rgba(255,255,255,.1)', overflow:'hidden' }}>
                <div style={{ padding:'14px 24px', borderBottom:'1px solid rgba(255,255,255,.08)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <div style={{ fontWeight:700, fontSize:14, color:"#fff", display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2, flexShrink:0 }} />
                    {isExp ? 'FOB exportado' : 'CIF importado'} por año (USD)
                  </div>
                  <div style={{ fontSize:11, color:COLORS.grayLight }}>Fuente: DIAN · Datos públicos · Ley 1712/2014</div>
                </div>
                <div style={{ padding:'20px 24px', display:'flex', gap:20, alignItems:'flex-end' }}>
                  {fobAño.map((row, i) => {
                    const pct = Math.round(row.valorFobTotal / maxFob * 100);
                    return (
                      <div key={i} style={{ flex:1, textAlign:'center' }}>
                        <div style={{ fontSize:13, fontWeight:700, color:COLORS.teal, marginBottom:6 }}>{fmtUSD(row.valorFobTotal)}</div>
                        <div style={{ height:100, background:'rgba(255,255,255,.06)', borderRadius:6, display:'flex', alignItems:'flex-end', overflow:'hidden' }}>
                          <div style={{ width:'100%', height:`${pct}%`, background:COLORS.teal, borderRadius:'6px 6px 0 0', minHeight:4 }} />
                        </div>
                        <div style={{ fontSize:14, fontWeight:800, color:"#fff", marginTop:6 }}>{row.año}</div>
                        <div style={{ fontSize:11, color:COLORS.gray }}>{(row.numOperaciones || 0).toLocaleString()} decl.</div>
                        {row.numPaises > 0 && <div style={{ fontSize:11, color:COLORS.amber, fontWeight:600 }}>{row.numPaises} países</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* TOP PAÍSES DE ORIGEN — DIAN */}
            {(productData?.paisesOrigenTop || productData?.paisesTop || []).length > 0 && (() => {
              const paises = (productData?.paisesOrigenTop || productData?.paisesTop || []).slice(0, 8);
              const maxVal = paises[0]?.valorFobTotal || paises[0]?.fob || 1;
              const totalVal = paises.reduce((s, p) => s + (p.valorFobTotal || p.fob || 0), 0);
              const fmtV = v => v >= 1e9 ? `$${(v/1e9).toFixed(1)}B` : `$${(v/1e6).toFixed(1)}M`;
              return (
                <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, marginBottom:16, border:'1px solid rgba(255,255,255,.1)', overflow:'hidden' }}>
                  <div style={{ padding:'14px 24px', borderBottom:'1px solid rgba(255,255,255,.08)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <div style={{ fontWeight:700, fontSize:14, color:'#fff', display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:3, height:16, background:COLORS.amber, borderRadius:2, flexShrink:0 }}/>
                      {isExp ? 'Principales países de destino' : 'Principales países de origen'}
                    </div>
                    <span style={{ fontSize:11, color:COLORS.gray }}>Fuente: DIAN · 2023–2026</span>
                  </div>
                  <div style={{ padding:'16px 24px' }}>
                    {paises.map((p, i) => {
                      const val = p.valorFobTotal || p.fob || 0;
                      const pct = maxVal > 0 ? Math.round(val / maxVal * 100) : 0;
                      const pctTotal = totalVal > 0 ? (val / totalVal * 100).toFixed(0) : 0;
                      return (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
                          <div style={{ width:20, fontSize:12, color:COLORS.gray, textAlign:'center', flexShrink:0 }}>{i+1}</div>
                          <div style={{ width:180, fontSize:13, color:'rgba(255,255,255,.9)', fontWeight:500, flexShrink:0 }}>{p.pais || p.nombre}</div>
                          <div style={{ flex:1, height:8, background:'rgba(255,255,255,.08)', borderRadius:4, overflow:'hidden' }}>
                            <div style={{ width:pct+'%', height:'100%', background:COLORS.amber, borderRadius:4 }}/>
                          </div>
                          <div style={{ width:80, textAlign:'right', fontSize:13, color:COLORS.amber, fontWeight:700 }}>{fmtV(val)}</div>
                          <div style={{ width:36, textAlign:'right', fontSize:11, color:COLORS.gray }}>{pctTotal}%</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* PAÍSES DE ORIGEN — cuando no hay detalle completo */}
            {detalle.length === 0 && (productData?.paisesOrigenTop || []).length > 0 && (
              <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, padding:'20px 24px', marginBottom:16, border:'1px solid rgba(255,255,255,.1)' }}>
                <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginBottom:4, display:'flex', alignItems:'center', gap:10 }}><div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2, flexShrink:0 }}/>{operation === 'X' ? 'Países destino' : 'Países origen'}</div>
                <div style={{ fontSize:12, color:COLORS.gray, marginBottom:16 }}>Fuente: DIAN · Datos públicos · Ley 1712/2014</div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {(productData.paisesOrigenTop).map((p, i) => {
                    const pct = productData.paisesOrigenTop[0]?.valorFobTotal > 0
                      ? Math.round(p.valorFobTotal / productData.paisesOrigenTop[0].valorFobTotal * 100) : 0;
                    return (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:24, fontSize:12, color:COLORS.gray, textAlign:'right' }}>{i+1}</div>
                        <div style={{ flex:1, fontSize:13, fontWeight:600, color:"#fff" }}>{p.pais}</div>
                        <div style={{ width:120, height:8, background:'rgba(255,255,255,.06)', borderRadius:4, overflow:'hidden' }}>
                          <div style={{ width:`${pct}%`, height:'100%', background:COLORS.teal, borderRadius:4 }} />
                        </div>
                        <div style={{ width:100, fontSize:13, fontWeight:700, color:COLORS.teal, textAlign:'right' }}>{fmtUSD(p.valorFobTotal)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* MERCADO MUNDIAL */}
            {comtradeWorld && (() => {
              const exp = comtradeWorld.exporters;
              const imp = comtradeWorld.importers;
              const año = exp?.year || imp?.year || '—';
              const fmtB = v => v >= 1e9 ? `$${(v/1e9).toFixed(1)}B` : v >= 1e6 ? `$${(v/1e6).toFixed(0)}M` : `$${v.toLocaleString()}`;
              const maxExp = exp?.top5?.[0]?.value || 1;
              const maxImp = imp?.top5?.[0]?.value || 1;
              // Para importaciones Colombia: contexto relevante es exportadores mundiales
              // Para exportaciones Colombia: contexto relevante es importadores mundiales
              // Usar el valor de Colombia en Comtrade (mismo año/fuente que el total mundial)
              const colComtrade = operation === 'X' ? exp?.colombia?.value : imp?.colombia?.value;
              return (
                <div style={{ background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:14, overflow:'hidden', marginBottom:16 }}>
                  {/* Header */}
                  <div style={{ padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,.08)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, fontWeight:700, fontSize:14, color:'#fff' }}>
                      <div style={{ width:3, height:16, background:'#a78bfa', borderRadius:2, flexShrink:0 }} />
                      Mercado mundial · {productHs} · {año}
                      <span style={{ background:COLORS.teal, color:'#fff', borderRadius:4, padding:'2px 7px', fontSize:9, fontWeight:800, letterSpacing:.5 }}>Comtrade</span>
                    </div>
                    <div style={{ fontSize:11, color:COLORS.gray }}>Fuente: <span style={{ color:COLORS.teal }}>UN Comtrade</span> · último año disponible</div>
                  </div>

                  {/* KPI strip */}
                  {(() => {
                    const wTotal = exp?.worldTotal > 0 ? exp.worldTotal : imp?.worldTotal;
                    const wLabel = exp?.worldTotal > 0 ? 'exportado por los principales mercados' : 'importado por los principales mercados';
                    const colRef = colComtrade || exp?.colombia?.value || imp?.colombia?.value;
                    if (!wTotal) return null;
                    return (
                      <div style={{ background:'rgba(139,92,246,.08)', borderBottom:'1px solid rgba(139,92,246,.2)', padding:'12px 20px', display:'flex', alignItems:'center', gap:24, flexWrap:'wrap' }}>
                        <div style={{ display:'flex', alignItems:'baseline', gap:8 }}>
                          <span style={{ fontFamily:"'JetBrains Mono','Courier New',monospace", fontSize:22, fontWeight:800, color:'#a78bfa' }}>{fmtB(wTotal)}</span>
                          <span style={{ fontSize:12, color:COLORS.grayLight }}>{wLabel} · {año}</span>
                        </div>
                        {colRef > 0 && (
                          <div style={{ fontSize:12, color:COLORS.grayLight }}>
                            Colombia {operation === 'X' ? 'exporta' : 'importa'} <strong style={{ color:'#2DD4D3' }}>{(colRef / wTotal * 100).toFixed(1)}%</strong> de ese total
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Rankings */}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0 }}>
                    {/* Top exportadores */}
                    {exp?.top5?.length > 0 && (
                      <div style={{ padding:'16px 20px', borderRight:'1px solid rgba(255,255,255,.06)' }}>
                        <div style={{ fontSize:10, color:COLORS.gray, textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:12 }}>🌍 Top exportadores mundiales</div>
                        {exp.top5.map((p, i) => (
                          <div key={i} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:7 }}>
                            <div style={{ width:18, fontSize:11, color:COLORS.gray, textAlign:'center', flexShrink:0 }}>{p.rank}</div>
                            <div style={{ width:130, fontSize:12, fontWeight:500, color: p.name.includes('Colombia') ? '#2DD4D3' : 'rgba(255,255,255,.85)', flexShrink:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</div>
                            <div style={{ flex:1, height:6, background:'rgba(255,255,255,.06)', borderRadius:3, overflow:'hidden' }}>
                              <div style={{ width:`${Math.round(p.value/maxExp*100)}%`, height:'100%', background:COLORS.teal, borderRadius:3 }} />
                            </div>
                            <div style={{ width:50, textAlign:'right', fontSize:11, color:COLORS.grayLight, fontWeight:600, flexShrink:0 }}>{fmtB(p.value)}</div>
                          </div>
                        ))}
                        {exp.colombia && !exp.top5.some(p => p.name.includes('Colombia')) && (
                          <>
                            <div style={{ borderTop:'1px solid rgba(255,255,255,.06)', margin:'8px 0' }} />
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                              <div style={{ width:18, fontSize:11, color:COLORS.gray, textAlign:'center', flexShrink:0 }}>#{exp.colombia.rank}</div>
                              <div style={{ width:130, fontSize:12, fontWeight:700, color:'#2DD4D3', flexShrink:0 }}>🇨🇴 Colombia</div>
                              <div style={{ flex:1, height:6, background:'rgba(255,255,255,.06)', borderRadius:3, overflow:'hidden' }}>
                                <div style={{ width:`${Math.max(Math.round(exp.colombia.value/maxExp*100),2)}%`, height:'100%', background:'#2DD4D3', borderRadius:3 }} />
                              </div>
                              <div style={{ width:50, textAlign:'right', fontSize:11, color:'#2DD4D3', fontWeight:600, flexShrink:0 }}>{fmtB(exp.colombia.value)}</div>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Top importadores */}
                    {imp?.top5?.length > 0 && (
                      <div style={{ padding:'16px 20px' }}>
                        <div style={{ fontSize:10, color:COLORS.gray, textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:12 }}>🛒 Top importadores mundiales</div>
                        {imp.top5.map((p, i) => (
                          <div key={i} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:7 }}>
                            <div style={{ width:18, fontSize:11, color:COLORS.gray, textAlign:'center', flexShrink:0 }}>{p.rank}</div>
                            <div style={{ width:130, fontSize:12, fontWeight:500, color: p.name.includes('Colombia') ? '#2DD4D3' : 'rgba(255,255,255,.85)', flexShrink:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</div>
                            <div style={{ flex:1, height:6, background:'rgba(255,255,255,.06)', borderRadius:3, overflow:'hidden' }}>
                              <div style={{ width:`${Math.round(p.value/maxImp*100)}%`, height:'100%', background:COLORS.amber, borderRadius:3 }} />
                            </div>
                            <div style={{ width:50, textAlign:'right', fontSize:11, color:COLORS.grayLight, fontWeight:600, flexShrink:0 }}>{fmtB(p.value)}</div>
                          </div>
                        ))}
                        {imp.colombia && !imp.top5.some(p => p.name.includes('Colombia')) && (
                          <>
                            <div style={{ borderTop:'1px solid rgba(255,255,255,.06)', margin:'8px 0' }} />
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                              <div style={{ width:18, fontSize:11, color:COLORS.gray, textAlign:'center', flexShrink:0 }}>#{imp.colombia.rank}</div>
                              <div style={{ width:130, fontSize:12, fontWeight:700, color:'#2DD4D3', flexShrink:0 }}>🇨🇴 Colombia</div>
                              <div style={{ flex:1, height:6, background:'rgba(255,255,255,.06)', borderRadius:3, overflow:'hidden' }}>
                                <div style={{ width:`${Math.max(Math.round(imp.colombia.value/maxImp*100),2)}%`, height:'100%', background:'#2DD4D3', borderRadius:3 }} />
                              </div>
                              <div style={{ width:50, textAlign:'right', fontSize:11, color:'#2DD4D3', fontWeight:600, flexShrink:0 }}>{fmtB(imp.colombia.value)}</div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{ padding:'0 20px 12px', fontSize:10, color:'#334155' }}>
                    * Datos UN Comtrade · {año} (último disponible) · Muestra: los 28 principales países que reportan a la ONU, no el mundo entero.
                  </div>
                </div>
              );
            })()}

            {/* RESUMEN AGRUPADO — rankings completos sin tope, paginados en SQL */}
            {detalle.length > 0 && !isDemo && (
              <RankingBlock
                tipo={isExp ? 'exp' : 'imp'}
                hsCode={productHs}
                anio={ui.año || null}
                pais={ui.pais || null}
                subpartida={ui.subpartida || null}
                isPaid={isPaid}
                onUpgrade={() => { setStep(0); setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior:'smooth' }), 100); }}
              />
            )}

            {/* TABLA */}
            {detalle.length > 0 && (
              <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, border:'1px solid rgba(255,255,255,.1)', overflow:'hidden', marginBottom:24 }}>
                <div style={{ padding:'12px 20px', borderBottom:'1px solid rgba(255,255,255,.08)', background:'rgba(255,255,255,.04)', display:'flex', gap:10, flexWrap:'nowrap', alignItems:'center', overflowX:'auto' }}>
                  <select className="flt-sel" value={ui.año} onChange={e => setUI({ año: e.target.value })} disabled={!isPaid} title={!isPaid ? 'Disponible en Plan Pro' : undefined} style={{ flexShrink:0, opacity: isPaid ? 1 : 0.4, cursor: isPaid ? 'pointer' : 'not-allowed' }}>
                    <option value="">Todos los años</option>
                    {años.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>

                  <select className="flt-sel" value={ui.pais} onChange={e => setUI({ pais: e.target.value })} disabled={!isPaid} title={!isPaid ? 'Disponible en Plan Pro' : undefined} style={{ flexShrink:0, opacity: isPaid ? 1 : 0.4, cursor: isPaid ? 'pointer' : 'not-allowed' }}>
                    <option value="">{isExp ? 'Todos los países destino' : 'Todos los países origen'}</option>
                    {paises.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>

                  <select className="flt-sel" value={ui.empresa||''} onChange={e => setUI({ empresa: e.target.value })} disabled={!isPaid} title={!isPaid ? 'Disponible en Plan Pro' : undefined} style={{ flexShrink:0, opacity: isPaid ? 1 : 0.4, cursor: isPaid ? 'pointer' : 'not-allowed' }}>
                    <option value="">Todas las empresas</option>
                    {empresas.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>

                  <select className="flt-sel" value={ui.subpartida||''} onChange={e => setUI({ subpartida: e.target.value })} disabled={!isPaid} title={!isPaid ? 'Disponible en Plan Pro' : undefined} style={{ flexShrink:0, opacity: isPaid ? 1 : 0.4, cursor: isPaid ? 'pointer' : 'not-allowed' }}>
                    <option value="">Todas las subpartidas</option>
                    {subpartidasP.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>

                  <input value={ui.busca} onChange={e => isPaid && setUI({ busca: e.target.value })}
                    placeholder={isPaid ? (isExp ? 'Buscar empresa o destinatario…' : 'Buscar empresa o proveedor…') : '🔒 Filtros disponibles en Plan Pro'}
                    disabled={!isPaid}
                    title={!isPaid ? 'Disponible en Plan Pro' : undefined}
                    style={{ flex:1, minWidth:120, padding:'7px 12px', border:'1.5px solid rgba(255,255,255,.1)', borderRadius:8, fontSize:13, fontFamily:'inherit', outline:'none', opacity: isPaid ? 1 : 0.4, cursor: isPaid ? 'text' : 'not-allowed' }} />

                  <button onClick={() => isPaid && setUI({ año:'', mes:'', pais:'', empresa:'', subpartida:'', busca:'', col:'valorFob', dir:'desc', page:0 })}
                    disabled={!isPaid}
                    style={{ flexShrink:0, padding:'7px 14px', background:'rgba(255,255,255,.06)', border:'1.5px solid rgba(255,255,255,.1)', borderRadius:8, fontSize:13, cursor: isPaid ? 'pointer' : 'not-allowed', fontFamily:'inherit', color:COLORS.gray, opacity: isPaid ? 1 : 0.4 }}>
                    Limpiar
                  </button>

                  {isPaid && (
                    <button onClick={exportCSV} disabled={csvLoading}
                      style={{ flexShrink:0, padding:'7px 14px', background:csvLoading?COLORS.tealDark:COLORS.teal, color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:csvLoading?'wait':'pointer', fontFamily:'inherit', opacity:csvLoading?0.8:1, display:'flex', alignItems:'center', gap:6 }}>
                      {csvLoading ? '⏳ Descargando…' : '⬇ Exportar CSV'}
                    </button>
                  )}
                </div>

                <div style={{ padding:'8px 20px', background:'rgba(255,255,255,.03)', borderBottom:'1px solid rgba(255,255,255,.05)', fontSize:12, color:COLORS.gray, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span>
                    <strong style={{ color:"#fff" }}>{filas.length}</strong> filas
                    {ui.año || ui.pais || ui.empresa || ui.busca ? ` (filtradas de ${detalle.length})` : ''}
                    {isExp ? ' · FOB visible: ' : ' · CIF visible: '}<strong style={{ color:COLORS.teal }}>{fmtUSD(kpiFob)}</strong>
                  </span>
                  <span>Pág. {page + 1} de {totalPags || 1}</span>
                </div>

                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                    <thead style={{ background:'rgba(255,255,255,.04)', position:'sticky', top:0 }}>
                      <tr>
                        <SortHdr label="Año"                col="año"          />
                        <SortHdr label={isExp ? 'Empresa exportadora' : 'Empresa importadora'} col="importador"   />
                        <SortHdr label={isExp ? 'País destino'         : 'País origen'}         col="pais"         />
                        <SortHdr label={isExp ? 'Destinatario'         : 'Proveedor'}           col="exportador"   />
                        <SortHdr label="Ops" col="numOps" />
                        <SortHdr label="Subpartida"          col="subpartida"   />
                        <th style={{ padding:'10px 12px', color:COLORS.gray, fontWeight:600, borderBottom:`2px solid #e2e8f0`, fontSize:12, textAlign:'left' }}>Descripción</th>
                        <SortHdr label={isExp ? 'FOB USD' : 'CIF USD'} col="valorFob" />
                      </tr>
                    </thead>
                    <tbody>
                      {filasVis.length === 0 && (
                        <tr><td colSpan={8} style={{ padding:32, textAlign:'center', color:COLORS.gray }}>Sin resultados para los filtros seleccionados.</td></tr>
                      )}
                      {filasVis.map((r, i) => (
                        <tr key={i} className="tbl-row">
                          <td style={{ padding:'9px 12px', fontWeight:700, color:"#fff", whiteSpace:'nowrap' }}>{r.año}</td>
                          <td style={{ padding:'9px 12px', fontWeight:600, color:"#fff", maxWidth:200 }}>
                            <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.importador||''}>{r.importador || '—'}</div>
                            {r.nitImportador && <div style={{ fontSize:11, color:COLORS.gray }}>{r.nitImportador}</div>}
                          </td>
                          <td style={{ padding:'9px 12px' }}>
                            <span style={{ background:'rgba(14,165,164,.15)', color:'#2DD4D3', padding:'2px 8px', borderRadius:5, fontWeight:600, fontSize:12 }}>{r.pais}</span>
                          </td>
                          <td style={{ padding:'9px 12px', color:'rgba(255,255,255,.75)', maxWidth:200 }}>
                            {r.exportador ? (() => {
                              const nClientes = clientesPorProveedor.get(r.exportador)?.size || 1;
                              const multi = nClientes > 1;
                              const tooltipLabel = isExp
                                ? `Ver perfil · ${nClientes} exportadores colombianos`
                                : `Ver perfil · ${nClientes} clientes en Colombia`;
                              const badgeLabel = isExp
                                ? `${nClientes} exportadores colombianos`
                                : `${nClientes} clientes colombianos`;
                              return (
                                <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                                  {multi ? (
                                    <div
                                      onClick={() => isDemo && !user ? setShowLogin(true) : fetchProveedor(r.exportador)}
                                      title={isDemo && !user ? 'Crea una cuenta para ver este proveedor' : tooltipLabel}
                                      style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', cursor:'pointer',
                                        color:COLORS.amber, textDecoration:'underline', fontWeight:700, flex:1 }}
                                    >{r.exportador}</div>
                                  ) : (
                                    <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'rgba(255,255,255,.75)', flex:1 }}>
                                      {r.exportador}
                                    </div>
                                  )}
                                  {multi && (
                                    <span title={badgeLabel}
                                      style={{ flexShrink:0, background:COLORS.amber, color:'#fff', borderRadius:10, padding:'1px 7px', fontSize:11, fontWeight:800, lineHeight:'18px' }}>
                                      {nClientes}
                                    </span>
                                  )}
                                </div>
                              );
                            })() : (
                              <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.exportador || '—'}</div>
                            )}
                          </td>
                          <td style={{ padding:'9px 12px', color:COLORS.gray, fontSize:12, textAlign:'center' }}>{r.numOps || 1}</td>
                          <td style={{ padding:'9px 12px', fontFamily:'monospace', fontWeight:700, color:COLORS.teal, whiteSpace:'nowrap' }}>{r.subpartida}</td>
                          <td style={{ padding:'9px 12px', color:'rgba(255,255,255,.75)', maxWidth:220 }}>
                            <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.descripcionOficial||''}>
                              {r.descripcionOficial || '—'}
                            </div>
                          </td>
                          <td style={{ padding:'9px 12px', fontWeight:800, color:"#fff", whiteSpace:'nowrap', textAlign:'right' }}>
                            {fmtUSD(r.valorFob)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPags > 1 && isPaid && (
                  <div style={{ padding:'12px 20px', borderTop:`1px solid #f1f5f9`, display:'flex', gap:8, justifyContent:'center', alignItems:'center' }}>
                    <button onClick={() => setUI({ page: 0 })} disabled={page === 0} style={{ padding:'5px 10px', border:'1.5px solid rgba(255,255,255,.1)', borderRadius:6, background:'rgba(255,255,255,.05)', cursor: page===0?'default':'pointer', color: page===0?'rgba(255,255,255,.2)':'#fff', fontSize:13 }}>«</button>
                    <button onClick={() => setUI({ page: page-1 })} disabled={page === 0} style={{ padding:'5px 10px', border:'1.5px solid rgba(255,255,255,.1)', borderRadius:6, background:'rgba(255,255,255,.05)', cursor: page===0?'default':'pointer', color: page===0?'rgba(255,255,255,.2)':'#fff', fontSize:13 }}>‹</button>
                    <span style={{ fontSize:13, color:COLORS.gray }}>Página {page+1} de {totalPags}</span>
                    <button onClick={() => setUI({ page: page+1 })} disabled={page>=totalPags-1} style={{ padding:'5px 10px', border:'1.5px solid rgba(255,255,255,.1)', borderRadius:6, background:'rgba(255,255,255,.05)', cursor: page>=totalPags-1?'default':'pointer', color: page>=totalPags-1?'rgba(255,255,255,.2)':'#fff', fontSize:13 }}>›</button>
                    <button onClick={() => setUI({ page: totalPags-1 })} disabled={page>=totalPags-1} style={{ padding:'5px 10px', border:'1.5px solid rgba(255,255,255,.1)', borderRadius:6, background:'rgba(255,255,255,.05)', cursor: page>=totalPags-1?'default':'pointer', color: page>=totalPags-1?'rgba(255,255,255,.2)':'#fff', fontSize:13 }}>»</button>
                  </div>
                )}

                {!isPaid && (
                  <div style={{ padding:'28px 20px', borderTop:'1px solid rgba(255,255,255,.08)', background:'linear-gradient(to bottom, rgba(4,13,26,0), rgba(4,13,26,0.97) 40%)', textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', gap:12 }}>
                    {isDemo && !user ? (
                      <>
                        <div style={{ fontSize:14, fontWeight:700, color:'#fff' }}>Vista demo · Busca cualquier producto con una cuenta gratis</div>
                        <button onClick={() => setShowLogin(true)} style={{ background:COLORS.teal, color:'#fff', border:'none', borderRadius:9, padding:'10px 28px', fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:'inherit' }}>
                          Crear cuenta gratis →
                        </button>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize:14, fontWeight:700, color:'#fff' }}>{filas.length > 10 ? `${filas.length - 10} filas más disponibles` : 'Exporta y analiza con IA'} · Plan Pro</div>
                        <button onClick={() => { setStep(0); setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior:'smooth' }), 100); }} style={{ background:COLORS.teal, color:'#fff', border:'none', borderRadius:9, padding:'10px 28px', fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:'inherit' }}>
                          Ver Plan Pro →
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            <div style={{ fontSize:11, color:COLORS.gray, textAlign:'center', paddingBottom:32 }}>
              Fuente: DIAN · Datos públicos · Ley 1712/2014 · Última actualización: {productData.ultimaActualizacion}
            </div>
          </>)}
        </div>
      </div>
    );
  }

  // ── PASO 7: CONCLUSIÓN / ANÁLISIS EJECUTIVO ─────────────────────
  if (step === 7 && analisisMode === 'empresa') {
    const a = analisisData;
    const fmtM = v => v ? `$${(v/1e6).toFixed(1)}M USD` : '—';
    const empNombre  = companyData?.empresasTop?.[0]?.razonSocial || `NIT ${companyNit}`;
    const totalFob   = companyData?.resumen?.totalValorFobUsd || 0;
    const totalOps   = companyData?.resumen?.totalOperaciones || 0;
    const totalPaises= companyData?.resumen?.totalPaises || 0;
    const fobPorAño  = companyData?.fobPorAño || [];
    const detalle    = companyData?.detalleAgrupado || [];
    const numPartidas= new Set(detalle.map(r => r.subpartida).filter(Boolean)).size;
    const tendenciaColor = a?.tendencia?.startsWith('creciente') ? '#15803d'
      : a?.tendencia?.startsWith('decreciente') ? '#dc2626' : '#92400e';

    return (
      <div style={{ minHeight:'100vh', background:'#040D1A', fontFamily:"'Inter',system-ui,sans-serif" }}>
        <style>{`@media print { .no-print { display: none !important; } * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; } html, body { background: #040D1A !important; } .print-page { box-shadow: none !important; } .print-section { page-break-inside: avoid !important; break-inside: avoid !important; } }`}</style>
        <div className="no-print" style={{ background:COLORS.navy, padding:'14px 24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <button onClick={() => setStep(4)} style={{ background:'rgba(255,255,255,.12)', color:'#fff', border:'none', borderRadius:8, padding:'7px 14px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>← Volver</button>
            <div style={{ color:'#fff' }}>
              <div style={{ fontWeight:800, fontSize:16 }}>KIN</div>
              <div style={{ fontSize:12, color:'rgba(255,255,255,.45)' }}>{operation === 'X' ? 'Perfil Exportador' : 'Perfil Importador'} · NIT {companyNit}</div>
            </div>
          </div>
          <button onClick={() => window.print()} style={{ background:COLORS.navy, color:'#fff', border:'none', borderRadius:8, padding:'9px 20px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:8 }}>Descargar PDF</button>
        </div>
        <div className="print-page" style={{ maxWidth:860, margin:'0 auto', padding:'32px 20px' }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:28, borderBottom:'3px solid #0A2540', paddingBottom:20 }}>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:COLORS.teal, letterSpacing:1, textTransform:'uppercase', marginBottom:6 }}>Informe de Inteligencia Comercial · KIN</div>
              <h1 style={{ fontSize:26, fontWeight:800, color:"#fff", margin:0 }}>{empNombre}</h1>
              <p style={{ color:COLORS.gray, fontSize:13, margin:'6px 0 0' }}>NIT {companyNit} · Colombia · Datos DIAN 2023–2026</p>
            </div>
            <div style={{ textAlign:'right', flexShrink:0 }}>
              <div style={{ fontSize:22, fontWeight:800, color:"#fff" }}>K<span style={{ color:COLORS.teal }}>IN</span></div>
              <div style={{ fontSize:11, color:COLORS.gray, marginTop:2 }}>trade-intel-rho.vercel.app</div>
              <div style={{ fontSize:11, color:COLORS.gray }}>{new Date().toLocaleDateString('es-CO', { year:'numeric', month:'long', day:'numeric' })}</div>
            </div>
          </div>

          {analisisLoading && (
            <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, padding:60, textAlign:'center', border:'1px solid rgba(255,255,255,.1)' }}>
              <div style={{ fontSize:40, marginBottom:16 }}>🤖</div>
              <div style={{ fontSize:16, fontWeight:600, color:"#fff", marginBottom:8 }}>Generando análisis con IA…</div>
              <div style={{ fontSize:13, color:COLORS.gray }}>Analizando perfil {operation === 'X' ? 'exportador' : 'importador'}, tendencias y oportunidades</div>
            </div>
          )}

          {!analisisLoading && (<>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:24 }}>
              {[
                [operation === 'X' ? 'FOB Total' : 'CIF Total', fmtM(totalFob), COLORS.teal],
                ['Declaraciones',  totalOps.toLocaleString(),   COLORS.navy],
                [operation === 'X' ? 'Países destino' : 'Países origen', totalPaises, COLORS.amber],
                ['Subpartidas',    numPartidas,                  COLORS.navy],
              ].map(([label, val, color]) => (
                <div key={label} style={{ background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:12, padding:'16px 12px', textAlign:'center' }}>
                  <div style={{ fontSize:18, fontWeight:800, color }}>{val}</div>
                  <div style={{ fontSize:11, color:COLORS.gray }}>{label}</div>
                </div>
              ))}
            </div>

            {fobPorAño.length > 0 && (
              <div style={{ background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:12, padding:'18px 20px', marginBottom:20 }}>
                <div style={{ fontWeight:700, fontSize:14, color:"#fff", display:'flex', alignItems:'center', gap:10, marginBottom:12 }}><div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2, flexShrink:0 }}/>Evolución FOB por año</div>
                <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                  {fobPorAño.map(r => (
                    <div key={r.año} style={{ background:'rgba(255,255,255,.04)', borderRadius:8, padding:'10px 16px', textAlign:'center', minWidth:100 }}>
                      <div style={{ fontSize:15, fontWeight:800, color:COLORS.teal }}>{fmtM(r.valorFobTotal)}</div>
                      <div style={{ fontSize:12, color:COLORS.gray }}>{r.año === '2026' ? `2026 (ene–${operation === 'X' ? 'mar' : 'feb'})` : r.año}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {a && !a.error && (<>
              <div style={{ background:'#0A2540', borderRadius:14, padding:'20px 24px', marginBottom:20, color:'#fff' }}>
                <div style={{ fontSize:13, fontWeight:700, color:COLORS.teal, marginBottom:8, textTransform:'uppercase', letterSpacing:.5 }}>Resumen ejecutivo</div>
                <p style={{ fontSize:14, lineHeight:1.8, margin:0 }}>{a.resumenEjecutivo}</p>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
                <div style={{ background:'rgba(14,165,164,.08)', border:'1px solid rgba(14,165,164,.2)', borderRadius:12, padding:'18px 20px' }}>
                  <div style={{ fontWeight:700, fontSize:14, color:'#2DD4D3', display:'flex', alignItems:'center', gap:10, marginBottom:8 }}><div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2, flexShrink:0 }}/>Perfil {operation === 'X' ? 'exportador' : 'importador'}</div>
                  <p style={{ fontSize:13, color:'rgba(255,255,255,.75)', margin:0, lineHeight:1.7 }}>{a.perfilImportador}</p>
                </div>
                <div style={{ background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:12, padding:'18px 20px' }}>
                  <div style={{ fontWeight:700, fontSize:14, color:"#fff", display:'flex', alignItems:'center', gap:10, marginBottom:8 }}><div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2, flexShrink:0 }}/>Actividad</div>
                  <div style={{ fontSize:13, marginBottom:6 }}>
                    <span style={{ fontWeight:600 }}>Tendencia: </span>
                    <span style={{ color:tendenciaColor, fontWeight:700 }}>{a.tendencia}</span>
                  </div>
                  <div style={{ fontSize:13 }}>
                    <span style={{ fontWeight:600 }}>Concentración productos: </span>
                    <span style={{ color:COLORS.gray }}>{a.concentracionProductos}</span>
                  </div>
                </div>
              </div>

              {a.productosEstrategicos && (
                <div style={{ background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.1)', borderRadius:12, padding:'18px 20px', marginBottom:20 }}>
                  <div style={{ fontWeight:700, fontSize:14, color:"#fff", display:'flex', alignItems:'center', gap:10, marginBottom:8 }}><div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2, flexShrink:0 }}/>Productos estratégicos</div>
                  <p style={{ fontSize:13, color:"rgba(255,255,255,.65)", margin:0, lineHeight:1.7 }}>{a.productosEstrategicos}</p>
                </div>
              )}

              {a.estrategiaAbastecimiento && (
                <div style={{ background:'rgba(16,185,129,.08)', border:'1px solid rgba(16,185,129,.2)', borderRadius:12, padding:'18px 20px', marginBottom:20 }}>
                  <div style={{ fontWeight:700, fontSize:14, color:'#15803d', display:'flex', alignItems:'center', gap:10, marginBottom:8 }}><div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2, flexShrink:0 }}/>{operation === 'X' ? 'Estrategia de exportación' : 'Estrategia de abastecimiento'}</div>
                  <p style={{ fontSize:13, color:'rgba(255,255,255,.75)', margin:0, lineHeight:1.7 }}>{a.estrategiaAbastecimiento}</p>
                </div>
              )}

              {a.oportunidades && (
                <div style={{ background:'rgba(16,185,129,.08)', border:'1px solid rgba(16,185,129,.2)', borderRadius:12, padding:'18px 20px', marginBottom:20 }}>
                  <div style={{ fontWeight:700, fontSize:14, color:'#15803d', display:'flex', alignItems:'center', gap:10, marginBottom:8 }}><div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2, flexShrink:0 }}/>Oportunidad para ti</div>
                  <p style={{ fontSize:13, color:'rgba(255,255,255,.75)', margin:0, lineHeight:1.7 }}>{a.oportunidades}</p>
                </div>
              )}

              {a.riesgos?.length > 0 && (
                <div style={{ background:'rgba(248,113,113,.1)', border:'1px solid rgba(248,113,113,.2)', borderRadius:12, padding:'18px 20px', marginBottom:20 }}>
                  <div style={{ fontWeight:700, fontSize:14, color:'#f87171', display:'flex', alignItems:'center', gap:10, marginBottom:10 }}><div style={{ width:3, height:16, background:'#f87171', borderRadius:2, flexShrink:0 }}/>Riesgos a considerar</div>
                  <ul style={{ margin:0, paddingLeft:18 }}>
                    {a.riesgos.map((r, i) => <li key={i} style={{ fontSize:13, color:'#f87171', marginBottom:6, lineHeight:1.6 }}>{r}</li>)}
                  </ul>
                </div>
              )}

              <div style={{ background:`linear-gradient(135deg,${COLORS.navy},${COLORS.navyLight})`, borderRadius:14, padding:'22px 24px', marginBottom:20, color:'#fff' }}>
                <div style={{ fontSize:13, fontWeight:700, color:COLORS.tealVibrant, marginBottom:8, textTransform:'uppercase', letterSpacing:.5 }}>Conclusión</div>
                <p style={{ fontSize:15, lineHeight:1.8, margin:0, fontWeight:500 }}>{a.recomendacion}</p>
              </div>

              {a.pasosSiguientes?.length > 0 && (
                <div style={{ background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:12, padding:'18px 20px', marginBottom:20 }}>
                  <div style={{ fontWeight:700, fontSize:14, color:"#fff", display:'flex', alignItems:'center', gap:10, marginBottom:12 }}><div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2, flexShrink:0 }}/>Próximos pasos recomendados</div>
                  {a.pasosSiguientes.map((p, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:8 }}>
                      <span style={{ background:COLORS.teal, color:'#fff', borderRadius:'50%', width:22, height:22, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, flexShrink:0 }}>{i+1}</span>
                      <span style={{ fontSize:13, color:"rgba(255,255,255,.65)", lineHeight:1.6 }}>{p}</span>
                    </div>
                  ))}
                </div>
              )}

              {a.datosQueFlatan?.length > 0 && (
                <div style={{ background:'rgba(212,162,76,.1)', border:'1px solid rgba(212,162,76,.25)', borderRadius:12, padding:'18px 20px', marginBottom:24 }}>
                  <div style={{ fontWeight:700, fontSize:14, color:'rgba(255,255,255,.75)', display:'flex', alignItems:'center', gap:10, marginBottom:10 }}><div style={{ width:3, height:16, background:COLORS.amber, borderRadius:2, flexShrink:0 }}/>Información que mejoraría este análisis</div>
                  <ul style={{ margin:0, paddingLeft:18 }}>
                    {a.datosQueFlatan.map((d, i) => <li key={i} style={{ fontSize:13, color:'rgba(255,255,255,.75)', marginBottom:5 }}>{d}</li>)}
                  </ul>
                </div>
              )}
            </>)}

            <div style={{ borderTop:'1px solid rgba(255,255,255,.08)', paddingTop:16, textAlign:'center', fontSize:11, color:COLORS.gray }}>
              Reporte generado por KIN · kin-map.com · Datos DIAN Colombia 2023–2026 · Análisis IA por Claude (Anthropic).<br/>
              Este informe es orientativo. Verifique datos con fuentes oficiales antes de tomar decisiones de inversión.
            </div>
            <div className="no-print" style={{ textAlign:'center', marginTop:28 }}>
              <button onClick={() => window.print()} style={{ background:COLORS.navy, color:'#fff', border:'none', borderRadius:12, padding:'14px 36px', fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>Descargar PDF</button>
            </div>
          </>)}
        </div>
      </div>
    );
  }

  if (step === 7) {
    const a = analisisData;
    const hsFormatted = (productHs || '').replace(/\D/g,'').replace(/^(\d{4})(\d{0,6})/, '$1.$2').replace(/\.$/, '');
    const fmtM = v => v ? `$${(v/1e6).toFixed(1)}M` : '—';
    const fobPorAño = productData?.fobPorAño || [];
    const totalFob  = a?.totalFob  || 0;
    const totalOps  = a?.totalOps  || 0;
    const numEmp    = a?.totalEmpresas || 0;
    const numPaises = a?.paises?.length || 0;

    const tendenciaColor = a?.tendencia === 'creciente' ? '#4ade80'
      : a?.tendencia === 'decreciente' ? '#f87171' : COLORS.amber;
    const tendenciaIcon = a?.tendencia === 'creciente' ? '↑' : a?.tendencia === 'decreciente' ? '↓' : '→';
    const esExp = operation === 'X';

    const handlePrint = () => window.print();

    return (
      <div style={{ minHeight:'100vh', background:'#040D1A', fontFamily:"'Inter',system-ui,sans-serif" }}>

        <style>{`
          @media print {
            .no-print { display: none !important; }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
            html, body { background: #040D1A !important; }
            .print-page { box-shadow: none !important; }
            .print-section { page-break-inside: avoid !important; break-inside: avoid !important; }
          }
        `}</style>

        {/* Barra superior */}
        <div className="no-print" style={{ background:COLORS.navy, padding:'14px 24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <button onClick={() => setStep(5)} style={{ background:'rgba(255,255,255,.12)', color:'#fff', border:'none', borderRadius:8, padding:'7px 14px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>← Volver</button>
            <div style={{ color:'#fff' }}>
              <div style={{ fontWeight:800, fontSize:16 }}>KIN</div>
              <div style={{ fontSize:12, color:'rgba(255,255,255,.45)' }}>Resumen de mercado · Partida {hsFormatted}</div>
            </div>
          </div>
          <button onClick={handlePrint} style={{ background:COLORS.navy, color:'#fff', border:'1px solid rgba(255,255,255,.3)', borderRadius:8, padding:'9px 20px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
            Descargar PDF
          </button>
        </div>

        <div className="print-page" style={{ maxWidth:860, margin:'0 auto', padding:'32px 20px' }}>

          {/* Encabezado */}
          <div className="print-section" style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:28, borderBottom:'2px solid rgba(14,165,164,.4)', paddingBottom:20 }}>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:COLORS.teal, letterSpacing:1, textTransform:'uppercase', marginBottom:6 }}>Resumen de mercado · KIN</div>
              <h1 style={{ fontSize:26, fontWeight:800, color:'#fff', margin:0 }}>{productDesc}</h1>
              <p style={{ color:COLORS.gray, fontSize:13, margin:'6px 0 0' }}>Partida HS {hsFormatted} · Colombia · Datos DIAN 2023–2026</p>
            </div>
            <div style={{ textAlign:'right', flexShrink:0 }}>
              <div style={{ fontSize:22, fontWeight:800, color:'#fff' }}>K<span style={{ color:COLORS.teal }}>IN</span></div>
              <div style={{ fontSize:11, color:COLORS.gray, marginTop:2 }}>kin-map.com</div>
              <div style={{ fontSize:11, color:COLORS.gray }}>{new Date().toLocaleDateString('es-CO', { year:'numeric', month:'long', day:'numeric' })}</div>
            </div>
          </div>

          {/* KPIs */}
          <div className="print-section" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
            {[
              [esExp ? 'FOB Total' : 'CIF Total', `$${(totalFob/1e6).toFixed(1)}M USD`, COLORS.teal],
              ['Declaraciones', totalOps.toLocaleString(), '#fff'],
              [esExp ? 'Países destino' : 'Países origen', numPaises, COLORS.amber],
              [esExp ? 'Exportadores' : 'Importadores', numEmp, '#fff'],
            ].map(([label, val, color]) => (
              <div key={label} style={{ background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:12, padding:'16px 12px', textAlign:'center' }}>
                <div style={{ fontSize:18, fontWeight:800, color }}>{val}</div>
                <div style={{ fontSize:11, color:COLORS.gray, marginTop:4 }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Resumen en texto */}
          <div className="print-section" style={{ background:'rgba(14,165,164,.1)', border:'1px solid rgba(14,165,164,.3)', borderRadius:14, padding:'20px 24px', marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:COLORS.teal, marginBottom:10, textTransform:'uppercase', letterSpacing:.5 }}>Resumen</div>
            <p style={{ fontSize:14, lineHeight:1.9, margin:0, color:'rgba(255,255,255,.9)' }}>{a?.resumen}</p>
          </div>

          {/* Tendencia */}
          <div className="print-section" style={{ background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:12, padding:'16px 24px', marginBottom:20, display:'flex', alignItems:'center', gap:20 }}>
            <div>
              <div style={{ fontSize:11, color:COLORS.gray, marginBottom:4, textTransform:'uppercase', letterSpacing:.5 }}>Tendencia 2023–2025</div>
              <div style={{ fontSize:22, fontWeight:800, color:tendenciaColor }}>{tendenciaIcon} {a?.tendencia}</div>
            </div>
            {a?.crecPct !== null && (
              <div style={{ borderLeft:'1px solid rgba(255,255,255,.1)', paddingLeft:20 }}>
                <div style={{ fontSize:11, color:COLORS.gray, marginBottom:4 }}>Variación 2023 → 2025</div>
                <div style={{ fontSize:22, fontWeight:800, color:tendenciaColor }}>
                  {parseFloat(a.crecPct) >= 0 ? '+' : ''}{a.crecPct}%
                </div>
              </div>
            )}
            {a?.mejorAño && (
              <div style={{ borderLeft:'1px solid rgba(255,255,255,.1)', paddingLeft:20 }}>
                <div style={{ fontSize:11, color:COLORS.gray, marginBottom:4 }}>Año más alto</div>
                <div style={{ fontSize:16, fontWeight:700, color:'#fff' }}>{a.mejorAño.año} · {fmtM(a.mejorAño.valorFobTotal)}</div>
              </div>
            )}
          </div>

          {/* Gráfico barras */}
          {fobPorAño.length > 0 && (() => {
            const maxVal = Math.max(...fobPorAño.map(r => r.valorFobTotal || 0));
            return (
              <div className="print-section" style={{ background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:12, padding:'20px 24px', marginBottom:20 }}>
                <div style={{ fontWeight:700, fontSize:14, color:'#fff', display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
                  <div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2 }}/>
                  Evolución {esExp ? 'FOB' : 'CIF'} por año (USD)
                </div>
                <div style={{ display:'flex', alignItems:'flex-end', gap:12 }}>
                  {fobPorAño.map(r => {
                    const barH = maxVal > 0 ? Math.max(6, Math.round((r.valorFobTotal || 0) / maxVal * 100)) : 6;
                    const isPartial = r.año === '2026';
                    return (
                      <div key={r.año} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:COLORS.teal, textAlign:'center' }}>{fmtM(r.valorFobTotal)}</div>
                        <div style={{ width:'100%', height:barH+'px', background: isPartial ? `repeating-linear-gradient(45deg,${COLORS.teal},${COLORS.teal} 4px,rgba(14,165,164,.3) 4px,rgba(14,165,164,.3) 8px)` : COLORS.teal, borderRadius:'4px 4px 0 0' }}/>
                        <div style={{ fontSize:11, color: isPartial ? COLORS.amber : COLORS.gray, fontWeight: isPartial ? 700 : 400, marginTop:4 }}>{isPartial ? '2026*' : r.año}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize:11, color:COLORS.gray, marginTop:10 }}>* 2026: datos parciales ene–{esExp ? 'mar' : 'feb'}</div>
              </div>
            );
          })()}

          {/* Top países */}
          {a?.paises?.length > 0 && (
            <div className="print-section" style={{ background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:12, padding:'20px 24px', marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:14, color:'#fff', display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                <div style={{ width:3, height:16, background:COLORS.amber, borderRadius:2 }}/>
                {esExp ? 'Principales destinos' : 'Principales proveedores'}
              </div>
              {a.paises.map((p, i) => {
                const pct = a.totalFob > 0 ? (p.fob / a.totalFob * 100).toFixed(1) : 0;
                return (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
                    <div style={{ width:20, fontSize:12, color:COLORS.gray, flexShrink:0 }}>{i+1}</div>
                    <div style={{ flex:1, fontSize:13, color:'rgba(255,255,255,.85)', fontWeight:500 }}>{p.nombre}</div>
                    <div style={{ flex:2, height:8, background:'rgba(255,255,255,.08)', borderRadius:4, overflow:'hidden' }}>
                      <div style={{ width:pct+'%', height:'100%', background:COLORS.amber, borderRadius:4 }}/>
                    </div>
                    <div style={{ width:80, textAlign:'right', fontSize:13, color:COLORS.amber, fontWeight:700 }}>{fmtM(p.fob)}</div>
                    <div style={{ width:40, textAlign:'right', fontSize:12, color:COLORS.gray }}>{pct}%</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Top empresas */}
          {a?.empresas?.length > 0 && (
            <div className="print-section" style={{ background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:12, padding:'20px 24px', marginBottom:24 }}>
              <div style={{ fontWeight:700, fontSize:14, color:'#fff', display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                <div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2 }}/>
                {esExp ? 'Principales exportadores' : 'Principales importadores'}
              </div>
              {a.empresas.map((e, i) => {
                const pct = a.totalFob > 0 ? (e.fob / a.totalFob * 100).toFixed(1) : 0;
                return (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
                    <div style={{ width:20, fontSize:12, color:COLORS.gray, flexShrink:0 }}>{i+1}</div>
                    <div style={{ flex:1, fontSize:13, color:'rgba(255,255,255,.85)', fontWeight:500, textTransform:'capitalize', textOverflow:'ellipsis', overflow:'hidden', whiteSpace:'nowrap' }}>{(e.nombre||'').toLowerCase()}</div>
                    <div style={{ flex:2, height:8, background:'rgba(255,255,255,.08)', borderRadius:4, overflow:'hidden' }}>
                      <div style={{ width:pct+'%', height:'100%', background:COLORS.teal, borderRadius:4 }}/>
                    </div>
                    <div style={{ width:80, textAlign:'right', fontSize:13, color:COLORS.teal, fontWeight:700 }}>{fmtM(e.fob)}</div>
                    <div style={{ width:40, textAlign:'right', fontSize:12, color:COLORS.gray }}>{pct}%</div>
                  </div>
                );
              })}
              {numEmp > 5 && <div style={{ fontSize:12, color:COLORS.gray, marginTop:8 }}>Se muestran las 5 principales de {numEmp} empresas registradas.</div>}
            </div>
          )}

          {/* Pie */}
          <div className="print-section" style={{ borderTop:'1px solid rgba(255,255,255,.08)', paddingTop:16, textAlign:'center', fontSize:11, color:COLORS.gray }}>
            Reporte generado por KIN · kin-map.com · Fuente: DIAN Colombia · Datos 2023–2026
          </div>

          <div className="no-print" style={{ textAlign:'center', marginTop:28 }}>
            <button onClick={handlePrint} style={{ background:COLORS.teal, color:'#fff', border:'none', borderRadius:12, padding:'14px 36px', fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
              Descargar PDF
            </button>
          </div>

        </div>
      </div>
    );
  }

  // ── PASO 6: FICHA DE REQUISITOS DE IMPORTACIÓN ──────────────────
  if (step === 6) {
    const C6 = COLORS;
    const hsFormatted = (reqHs || '').replace(/\D/g,'').replace(/^(\d{4})(\d{0,6})/, '$1.$2').replace(/\.$/, '');

    const Tag6 = ({ children }) => (
      <span style={{ display:'inline-block', background:'rgba(14,165,164,.15)', color:'#2DD4D3', borderRadius:6, padding:'4px 12px', fontSize:13, fontWeight:600, marginRight:6, marginBottom:6 }}>
        {children}
      </span>
    );

    const Card = ({ title, children }) => (
      <div style={{ background:'rgba(255,255,255,.06)', borderRadius:14, border:'1px solid rgba(255,255,255,.1)', marginBottom:16, overflow:'hidden' }}>
        <div style={{ background:C6.teal, padding:'12px 20px' }}>
          <span style={{ fontWeight:700, fontSize:14, color:'#fff' }}>{title}</span>
        </div>
        <div style={{ padding:'20px 24px' }}>{children}</div>
      </div>
    );

    return (
      <div style={{ minHeight:'100vh', background:'#040D1A', fontFamily:"'Inter',system-ui,sans-serif" }}>
        {/* Barra superior */}
        <div style={{ background:C6.navy, padding:'14px 24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <button onClick={() => setStep(reqOriginStep)}
              style={{ background:'rgba(255,255,255,.12)', color:'#fff', border:'none', borderRadius:8, padding:'7px 14px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
              ← Volver
            </button>
            <div style={{ color:'#fff' }}>
              <div style={{ fontWeight:800, fontSize:16 }}>KIN</div>
              <div style={{ fontSize:12, color:'rgba(255,255,255,.45)' }}>{reqEsExportacion ? 'Exportaciones' : 'Importaciones'} Colombia · Partida {hsFormatted}</div>
            </div>
          </div>
          <button onClick={reset}
            style={{ background:'transparent', color:'rgba(255,255,255,.45)', border:'1px solid rgba(255,255,255,.15)', borderRadius:8, padding:'7px 14px', fontSize:12, cursor:'pointer', fontFamily:'inherit' }}>
            Nueva búsqueda
          </button>
        </div>

        <div style={{ maxWidth:860, margin:'0 auto', padding:'28px 20px' }}>

          {/* Título */}
          <div style={{ marginBottom:24 }}>
            <h1 style={{ fontSize:24, fontWeight:800, color:'#fff', margin:0 }}>
              Requisitos de {reqEsExportacion ? 'exportación' : 'importación'}
            </h1>
            <p style={{ color:'rgba(255,255,255,.5)', fontSize:14, margin:'6px 0 0' }}>
              Partida {hsFormatted} · {reqDesc}
            </p>
          </div>

          {reqLoading && (
            <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, padding:60, textAlign:'center', border:'1px solid rgba(255,255,255,.1)' }}>
              <div style={{ fontSize:40, marginBottom:16 }}>⏳</div>
              <div style={{ fontSize:16, fontWeight:600, color:'#fff', marginBottom:8 }}>Consultando requisitos…</div>
              <div style={{ fontSize:13, color:C6.gray }}>{reqEsExportacion ? 'Analizando requisitos de exportación, certificados y TLCs vigentes' : 'Cruzando los anexos del MinCIT y las declaraciones DIAN de esta subpartida'}</div>
            </div>
          )}

          {!reqLoading && reqData?.error && (
            <div style={{ background:'rgba(248,113,113,.1)', border:'1px solid rgba(248,113,113,.2)', borderRadius:14, padding:32, textAlign:'center', color:'#f87171' }}>
              No se pudieron obtener los requisitos. Intenta de nuevo.
              <br />
              <button onClick={() => fetchRequisitos(reqHs, reqDesc, reqOriginStep, reqEsExportacion)}
                style={{ marginTop:16, background:C6.teal, color:'#fff', border:'none', borderRadius:8, padding:'8px 18px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                Reintentar
              </button>
            </div>
          )}

          {!reqLoading && reqData && !reqData.error && (<>

            {/* De dónde sale cada dato: lo verificado se distingue de lo redactado por IA */}
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:18 }}>
              {reqData.verificado && (
                <span title={reqData.verificado.fuente}
                  style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'6px 12px', borderRadius:8, fontSize:12, fontWeight:700, color:C6.teal, background:'rgba(14,165,164,.1)', border:'1px solid rgba(14,165,164,.3)' }}>
                  ✓ Verificado · Circular 018 de 2020 del MinCIT
                  {reqData.verificado.anexos?.length > 0 && `, anexo${reqData.verificado.anexos.length > 1 ? 's' : ''} ${reqData.verificado.anexos.join(', ')}`}
                </span>
              )}
              {reqData.estadisticas && (
                <span style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'6px 12px', borderRadius:8, fontSize:12, fontWeight:700, color:C6.teal, background:'rgba(14,165,164,.1)', border:'1px solid rgba(14,165,164,.3)' }}>
                  ✓ DIAN · {reqData.estadisticas.declaraciones.toLocaleString('es-CO')} declaraciones en 12 meses
                </span>
              )}
              <span style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'6px 12px', borderRadius:8, fontSize:12, fontWeight:700, color:'#a78bfa', background:'rgba(124,58,237,.1)', border:'1px solid rgba(124,58,237,.3)' }}>
                ◇ {reqEsExportacion ? 'Todo el contenido es generado por IA' : 'Documentos, tiempos y notas: generados por IA'}
              </span>
            </div>

            {/* Con menos de 10 dígitos no se puede afirmar nada: se ofrecen las candidatas */}
            {reqData.nivelConsulta === 'partida' && reqData.candidatas?.length > 0 && (
              <div style={{ background:'rgba(212,162,76,.08)', border:'1px solid rgba(212,162,76,.25)', borderRadius:14, padding:'16px 20px', marginBottom:18 }}>
                <div style={{ fontSize:14, fontWeight:700, color:COLORS.amber, marginBottom:6 }}>
                  Esta consulta es de partida, no de subpartida
                </div>
                <div style={{ fontSize:13, color:'rgba(255,255,255,.6)', marginBottom:12 }}>
                  Dentro de esta partida hay subpartidas con requisitos distintos. Estas {reqData.candidatas.length} están reguladas —
                  escoge la tuya para ver los requisitos exactos.
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {reqData.candidatas.map(c => (
                    <button key={c.subpartida} onClick={() => fetchRequisitos(c.subpartida, c.descripcion, reqOriginStep, reqEsExportacion)}
                      style={{ display:'flex', alignItems:'baseline', gap:10, textAlign:'left', width:'100%', background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:8, padding:'9px 12px', cursor:'pointer', fontFamily:'inherit' }}>
                      <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:12.5, fontWeight:700, color:C6.teal, flexShrink:0 }}>{c.subpartida}</span>
                      <span style={{ fontSize:12.5, color:'rgba(255,255,255,.65)', flex:1 }}>{c.descripcion}</span>
                      <span style={{ fontSize:11, color:'rgba(255,255,255,.4)', flexShrink:0 }}>{c.entidades.join(' · ')}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Resumen rápido */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:20 }}>
              {(reqEsExportacion ? [
                ['🔖', 'Régimen', reqData.regimen || '—', '#fef9c3', '#854d0e'],
                ['🌐', 'Cert. Origen', reqData.certificadoOrigen || '—', '#dbeafe', '#1e40af'],
                ['⏱️', 'Tiempo trámite', reqData.tiempoPromedio || '—', '#f3e8ff', '#7e22ce'],
                ['📋', 'Declaración', 'DEX - DIAN', '#dcfce7', '#15803d'],
              ] : [
                ['⚖️', 'Gravamen', reqData.gravamen || '—', '#dbeafe', '#1e40af'],
                ['💰', 'IVA', reqData.iva || '—', '#dcfce7', '#15803d'],
                ['🔖', 'Régimen', reqData.regimen || '—', '#fef9c3', '#854d0e'],
                ['⏱️', 'Nacionalización', reqData.tiempoPromedio || '—', '#f3e8ff', '#7e22ce'],
              ]).map(([icon, label, val, bg, color]) => (
                <div key={label} style={{ background:bg, borderRadius:12, border:'none', padding:'16px', textAlign:'center' }}>
                  <div style={{ fontSize:22, marginBottom:6 }}>{icon}</div>
                  <div style={{ fontSize:15, fontWeight:800, color, lineHeight:1.3 }}>{val}</div>
                  <div style={{ fontSize:11, color:'#64748b', marginTop:4, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Otros impuestos */}
            {reqData.otrosImpuestos?.length > 0 && (
              <Card title="Otros impuestos y cargas">
                <div>
                  {reqData.otrosImpuestos.map((imp, i) => (
                    <Tag6 key={i}>{imp}</Tag6>
                  ))}
                </div>
              </Card>
            )}

            {/* Aranceles por TLC — solo importación */}
            {!reqEsExportacion && (
              <Card title="Arancel según país de origen">
                <div style={{ fontSize:12, color:'rgba(255,255,255,.5)', marginBottom:14 }}>
                  Tasas preferenciales vigentes para Colombia según acuerdos comerciales. Verifica la tasa exacta para esta subpartida en{' '}
                  <a href="https://muisca.dian.gov.co/WebArancel/DefMenuConsultas.faces" target="_blank" rel="noopener noreferrer" style={{ color:COLORS.teal, textDecoration:'none' }}>MUISCA DIAN</a>.
                </div>
                {[
                  { grupo:'Comunidad Andina (CAN)', tasa:'0%', paises:['Bolivia','Ecuador','Perú'] },
                  { grupo:'Alianza del Pacífico', tasa:'0%', paises:['Chile','México'] },
                  { grupo:'TLC con EEUU y Canadá', tasa:'0%', paises:['Estados Unidos','Canadá','Puerto Rico'] },
                  { grupo:'Acuerdo con UE', tasa:'0%', paises:['Alemania','Francia','España','Italia','Países Bajos','Bélgica','Portugal','Polonia','Austria','Suecia','y 17 países más de la UE'] },
                  { grupo:'Otros TLCs (0%)', tasa:'0%', paises:['Suiza','Noruega','Islandia','Corea del Sur','Costa Rica','Israel'] },
                  { grupo:'MERCOSUR (tasa reducida)', tasa:'Reducida', paises:['Argentina','Brasil','Paraguay','Uruguay'], reducida:true },
                  { grupo:'Sin acuerdo comercial', tasa:'Gravamen general', paises:['China','Japón','India','Turquía','Rusia','y demás países'], sinTlc:true },
                ].map((g, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:14, padding:'10px 14px', borderRadius:10, marginBottom:8, background: g.sinTlc ? 'rgba(248,113,113,.08)' : g.reducida ? 'rgba(212,162,76,.08)' : 'rgba(14,165,164,.08)', border: `1px solid ${g.sinTlc ? 'rgba(248,113,113,.2)' : g.reducida ? 'rgba(212,162,76,.2)' : 'rgba(14,165,164,.2)'}` }}>
                    <div style={{ flexShrink:0, marginTop:2, fontSize:16 }}>{g.sinTlc ? '🔴' : g.reducida ? '🟡' : '🟢'}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                        <span style={{ fontSize:13, fontWeight:700, color:'#fff' }}>{g.grupo}</span>
                        <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:4, background: g.sinTlc ? 'rgba(248,113,113,.2)' : g.reducida ? 'rgba(212,162,76,.2)' : 'rgba(14,165,164,.2)', color: g.sinTlc ? '#f87171' : g.reducida ? COLORS.amber : COLORS.teal }}>
                          {g.tasa}
                        </span>
                      </div>
                      <div style={{ fontSize:12, color:'rgba(255,255,255,.5)' }}>{g.paises.join(' · ')}</div>
                    </div>
                  </div>
                ))}
              </Card>
            )}

            {/* Vistos buenos */}
            {reqData.vistoBueno?.length > 0 && (
              <Card title="Vistos buenos y autorizaciones">
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {reqData.vistoBueno.map((vb, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'12px 16px', background:'rgba(255,255,255,.05)', borderRadius:10, border:'1px solid rgba(255,255,255,.1)' }}>
                      <span style={{ fontSize:16, flexShrink:0 }}>{vb.obligatorio ? '🔴' : '🟡'}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:'flex', flexWrap:'wrap', alignItems:'baseline', gap:8, marginBottom:3 }}>
                          <span style={{ fontWeight:700, fontSize:14, color:'#fff' }}>{vb.entidad}</span>
                          {vb.control && (
                            <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11.5, fontWeight:700, color:C6.teal, background:'rgba(14,165,164,.12)', border:'1px solid rgba(14,165,164,.28)', borderRadius:5, padding:'2px 7px' }}>{vb.control}</span>
                          )}
                          {vb.anexo && <span style={{ fontSize:11, color:'rgba(255,255,255,.35)' }}>Anexo {vb.anexo}</span>}
                        </div>
                        <div style={{ fontSize:13, color:'rgba(255,255,255,.6)' }}>{vb.descripcion}</div>
                        {/* La nota marginal RESTRINGE el alcance: es la diferencia entre
                            "requiere permiso" y "requiere permiso solo si…". Va textual. */}
                        {vb.nota && (
                          <div style={{ marginTop:8, borderLeft:`2px solid ${COLORS.amber}`, background:'rgba(212,162,76,.07)', borderRadius:'0 6px 6px 0', padding:'8px 12px' }}>
                            <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:COLORS.amber, marginBottom:3 }}>
                              Condición del anexo{vb.grupo ? ` · grupo: ${vb.grupo}` : ''}
                            </div>
                            <div style={{ fontSize:12.5, color:'rgba(255,255,255,.8)' }}>«{vb.nota}»</div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Documentos */}
            {reqData.documentos?.length > 0 && (
              <Card title="Documentos requeridos">
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:10 }}>
                  {reqData.documentos.map((doc, i) => (
                    <div key={i} style={{ background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:10, padding:'12px 14px' }}>
                      <div style={{ fontWeight:700, fontSize:13, color:'#fff', marginBottom:4 }}>{doc.nombre}</div>
                      <div style={{ fontSize:13, color:'rgba(255,255,255,.6)' }}>{doc.descripcion}</div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Normas técnicas */}
            {reqData.normasTecnicas?.length > 0 && (
              <Card title="Normas técnicas">
                <div>
                  {reqData.normasTecnicas.map((n, i) => (
                    <Tag6 key={i}>{n}</Tag6>
                  ))}
                </div>
              </Card>
            )}

            {/* Movimiento real: lo que ningún tarifario puede mostrar */}
            {reqData.estadisticas && (
              <Card title="Movimiento real de esta subpartida">
                <div style={{ fontSize:12, color:'rgba(255,255,255,.5)', marginBottom:14 }}>
                  Declaraciones de importación DIAN entre {reqData.estadisticas.desde} y {reqData.estadisticas.hasta}.
                  {!reqData.estadisticas.exacto && ' Cifras sumadas de varias subpartidas de esta partida.'}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12 }}>
                  {[
                    ['Declaraciones', reqData.estadisticas.declaraciones?.toLocaleString('es-CO')],
                    ['Importadores', reqData.estadisticas.importadores?.toLocaleString('es-CO') || '—'],
                    ['Valor CIF', reqData.estadisticas.cifUsd ? `US$ ${(reqData.estadisticas.cifUsd/1e6).toFixed(1)}M` : '—'],
                  ].map(([label, val]) => (
                    <div key={label} style={{ background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:10, padding:'14px 16px' }}>
                      <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:21, fontWeight:700, color:'#fff' }}>{val}</div>
                      <div style={{ fontSize:11, color:'rgba(255,255,255,.45)', marginTop:3, fontWeight:600, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* TLC */}
            {reqData.tlc?.length > 0 && (
              <Card title="Tratados de libre comercio aplicables">
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {reqData.tlc.map((t, i) => (
                    <div key={i} style={{ padding:'10px 14px', background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:8, fontSize:13, color:'rgba(255,255,255,.8)' }}>
                      {t}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Restricciones */}
            {reqData.restricciones && reqData.restricciones !== 'null' && (
              <Card title="Restricciones y cuotas">
                <p style={{ fontSize:13, color:'rgba(255,255,255,.8)', margin:0, lineHeight:1.6 }}>{reqData.restricciones}</p>
              </Card>
            )}

            {/* Notas */}
            {reqData.notas && (
              <Card title="Notas importantes">
                <p style={{ fontSize:13, color:'rgba(255,255,255,.8)', margin:0, lineHeight:1.7 }}>{reqData.notas}</p>
              </Card>
            )}

            {/* Aviso: separa lo verificado de lo generado, en vez de marcarlo todo como IA */}
            <div style={{ background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.1)', borderRadius:12, padding:'14px 18px', fontSize:12, color:C6.gray, lineHeight:1.7 }}>
              {reqEsExportacion ? (
                <>ℹ️ Contenido generado por IA con base en la normativa colombiana de exportaciones. Verifica con un agente de aduana o en la VUCE antes de exportar.</>
              ) : (
                <>
                  ✓ <b style={{ color:'rgba(255,255,255,.75)' }}>Entidades, régimen, arancel e IVA</b> salen de los anexos de la Circular 018 de 2020 del MinCIT
                  y de las declaraciones DIAN reales.<br />
                  ◇ <b style={{ color:'rgba(255,255,255,.75)' }}>Documentos, tiempos, normas y notas</b> los redacta la IA y son orientativos.<br />
                  {reqData.aviso}
                </>
              )}
            </div>

          </>)}
        </div>
      </div>
    );
  }

  // ── PASO 8: PERFIL POR PAÍS ──────────────────────────────────────
  if (step === 8) {
    const isExpPais = countryTipo === 'X';
    const fmtM   = v => v ? `$${(v/1e6).toFixed(1)}M USD` : '—';
    const fmtUSD = v => v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K` : `$${(v||0).toFixed(0)}`;
    const fobPorAño       = countryData?.fobPorAño       || [];
    const añosDisponibles = fobPorAño.map(r => r.año);

    // Para tablas: usar datos filtrados si hay año y/o producto seleccionado
    const fuenteAño    = (countryYear || countrySubpartida) && countryDataAño && !countryDataAño.error;
    const detalle      = (countryLoadingAño ? [] : fuenteAño ? countryDataAño.detalleAgrupado : countryData?.detalleAgrupado) || [];
    const empresasAño  = fuenteAño ? (countryDataAño.empresasTop || []) : [];
    const empresasTop  = (countryLoadingAño ? [] : empresasAño.length > 0 ? empresasAño : countryData?.empresasTop) || [];
    // Opciones del filtro de producto: siempre desde el top-20 sin filtrar (no se recalcula al elegir uno)
    const subpartidaOptions = countryData?.detalleAgrupado || [];
    const productoFiltrado  = fuenteAño ? countryDataAño?.productoFiltrado : null;

    const fobFiltrado  = countryYear ? fobPorAño.filter(r => r.año === countryYear) : fobPorAño;
    const totalFob     = fobFiltrado.reduce((s, r) => s + (r.valorFobTotal || 0), 0);
    const totalOps     = fobFiltrado.reduce((s, r) => s + (r.numOperaciones || 0), 0);
    const maxFob       = fobPorAño.reduce((m, r) => Math.max(m, r.valorFobTotal || 0), 0);

    // Agregar por subpartida para top productos
    const partidasMap = {};
    detalle.forEach(r => {
      if (!r.subpartida) return;
      if (!partidasMap[r.subpartida]) partidasMap[r.subpartida] = { subpartida: r.subpartida, descripcion: r.descripcionOficial || '', valor: 0, ops: 0 };
      partidasMap[r.subpartida].valor += r.valorFob || 0;
      partidasMap[r.subpartida].ops   += r.numOps   || 0;
    });
    const partidasTop = Object.values(partidasMap).sort((a, b) => b.valor - a.valor).slice(0, 10);
    const numPartidas = Object.keys(partidasMap).length;
    const numEmpresas = empresasTop.length;

    const COUNTRY_FLAGS = { 'China':'🇨🇳','Estados Unidos':'🇺🇸','Brasil':'🇧🇷','México':'🇲🇽','Alemania':'🇩🇪','India':'🇮🇳','Japón':'🇯🇵','Corea del Sur':'🇰🇷','Vietnam':'🇻🇳','Italia':'🇮🇹','Francia':'🇫🇷','España':'🇪🇸','Argentina':'🇦🇷','Chile':'🇨🇱','Ecuador':'🇪🇨','Países Bajos':'🇳🇱','Taiwán':'🇹🇼','Reino Unido':'🇬🇧','Indonesia':'🇮🇩','Canadá':'🇨🇦' };
    const flagEmoji = COUNTRY_FLAGS[countryName] || '🌍';

    return (
      <div style={{ minHeight:'100vh', background:'#040D1A', fontFamily:"'Inter',system-ui,sans-serif" }}>
        {/* Header */}
        <div style={{ background:COLORS.navy, padding:'14px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <button onClick={() => { setStep(0); }}
              style={{ background:'rgba(255,255,255,.12)', color:'#fff', border:'none', borderRadius:8, padding:'7px 14px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
              ← Volver
            </button>
            <div style={{ color:'#fff' }}>
              <div style={{ fontWeight:800, fontSize:16 }}>KIN</div>
              <div style={{ fontSize:12, color:'rgba(255,255,255,.45)' }}>
                {isExpPais ? `Exportaciones Colombia hacia ${countryName}` : `Importaciones Colombia desde ${countryName}`}
              </div>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            {/* Toggle importaciones / exportaciones */}
            <div style={{ display:'flex', background:'rgba(255,255,255,.1)', borderRadius:10, padding:3, gap:3 }}>
              {[['M','Importa desde'],['X','Exporta hacia']].map(([t, label]) => (
                <button key={t} onClick={() => fetchCountry(countryBuscar || countryName, t, countryName)}
                  style={{ background: countryTipo===t ? '#fff' : 'transparent', color: countryTipo===t ? COLORS.navy : '#fff',
                    border:'none', borderRadius:8, padding:'6px 12px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit', transition:'all .2s' }}>
                  {label}
                </button>
              ))}
            </div>
            {countryData && !countryData.error && totalOps > 0 && (<>
              <select value={countryYear} onChange={e => setCountryYear(e.target.value)}
                style={{ background:'rgba(255,255,255,.12)', color:'#fff', border:'1px solid rgba(255,255,255,.3)', borderRadius:8, padding:'7px 12px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit', outline:'none' }}>
                <option value="" style={{ background:COLORS.navy }}>Todos los años</option>
                {añosDisponibles.map(a => (
                  <option key={a} value={a} style={{ background:COLORS.navy }}>{a === '2026' ? '2026 (ene–mar)' : a}</option>
                ))}
              </select>
              {subpartidaOptions.length > 0 && (
                <select value={countrySubpartida} onChange={e => setCountrySubpartida(e.target.value)}
                  style={{ background:'rgba(255,255,255,.12)', color:'#fff', border:'1px solid rgba(255,255,255,.3)', borderRadius:8, padding:'7px 12px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit', outline:'none', maxWidth:220 }}>
                  <option value="" style={{ background:COLORS.navy }}>Todos los productos</option>
                  {subpartidaOptions.map(p => (
                    <option key={p.subpartida} value={p.subpartida} style={{ background:COLORS.navy }}>{p.subpartida} · {(p.descripcionOficial||'').slice(0,30)}</option>
                  ))}
                </select>
              )}
            </>)}
          </div>
        </div>

        <div style={{ maxWidth:960, margin:'0 auto', padding:'28px 20px' }}>

          {/* Título */}
          <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, padding:'20px 24px', marginBottom:16, border:'1px solid rgba(255,255,255,.1)', boxShadow:'0 2px 8px rgba(0,0,0,.05)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:4 }}>
              <div style={{ fontSize:28, fontWeight:800, color:"#fff" }}>{flagEmoji} {countryName}</div>
              {countrySubpartida && (
                <div style={{ display:'flex', alignItems:'center', gap:6, background:'rgba(14,165,164,.15)', color:COLORS.tealVibrant, borderRadius:20, padding:'4px 10px 4px 14px', fontSize:13, fontWeight:700 }}>
                  {productoFiltrado?.descripcionOficial || countrySubpartida}
                  <button onClick={() => setCountrySubpartida('')}
                    style={{ background:'rgba(255,255,255,.15)', border:'none', color:'#fff', width:18, height:18, borderRadius:'50%', cursor:'pointer', fontSize:12, lineHeight:1, display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
                </div>
              )}
            </div>
            <div style={{ fontSize:13, color:COLORS.gray }}>
              {isExpPais ? `Todo lo que Colombia exporta hacia este país` : `Todo lo que Colombia importa desde este país`} · Período: {countryData?.resumen?.rangoFechas || '2023-01 a 2026-12'}
            </div>
          </div>

          {countryLoading && (
            <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, padding:60, textAlign:'center', border:'1px solid rgba(255,255,255,.1)' }}>
              <div style={{ width:48, height:48, border:'4px solid rgba(255,255,255,.1)', borderTop:`4px solid ${COLORS.teal}`, borderRadius:'50%', animation:'spin .9s linear infinite', margin:'0 auto 20px' }} />
              <p style={{ color:COLORS.gray, fontSize:15 }}>Consultando DIAN para {countryName}…</p>
            </div>
          )}

          {!countryLoading && countryData?.error && (
            <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, padding:40, textAlign:'center', color:COLORS.gray }}>No se pudo obtener información. Intenta de nuevo.</div>
          )}

          {!countryLoading && countryData && !countryData.error && totalOps === 0 && (
            <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, padding:40, textAlign:'center', color:COLORS.gray }}>
              <div style={{ fontSize:32, marginBottom:12 }}>🔍</div>
              <div style={{ fontWeight:700, color:"#fff", marginBottom:8 }}>Sin resultados para "{countryName}"</div>
              <div style={{ fontSize:13 }}>Prueba con el nombre exacto del país tal como aparece en la DIAN.</div>
            </div>
          )}

          {!countryLoading && countryData && !countryData.error && totalOps > 0 && (<>

            {/* KPIs */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:16 }}>
              {[
                [isExpPais ? (countryYear ? `FOB ${countryYear}` : 'FOB Total') : (countryYear ? `CIF ${countryYear}` : 'CIF Total'), fmtM(totalFob), COLORS.teal],
                [countryYear ? `Decl. ${countryYear}` : 'Declaraciones', totalOps.toLocaleString(), COLORS.navy],
                [isExpPais ? 'Exportadores' : 'Importadores', numEmpresas + '+', COLORS.navy],
                ['Subpartidas', numPartidas > 0 ? numPartidas + (numPartidas >= 20 ? '+' : '') : '—', COLORS.amber],
              ].map(([label, val, color]) => (
                <div key={label} style={{ background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:12, padding:'16px 12px', textAlign:'center' }}>
                  <div style={{ fontSize:18, fontWeight:800, color }}>{val}</div>
                  <div style={{ fontSize:11, color:COLORS.gray }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Evolución por año */}
            {fobPorAño.length > 0 && (
              <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, padding:'20px 24px', marginBottom:16, border:'1px solid rgba(255,255,255,.1)' }}>
                <div style={{ fontWeight:700, fontSize:14, color:"#fff", display:'flex', alignItems:'center', gap:10, marginBottom:4 }}><div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2, flexShrink:0 }}/>{isExpPais ? 'FOB exportado' : 'CIF importado'} por año (USD)</div>
                <div style={{ fontSize:12, color:COLORS.gray, marginBottom:16 }}>Fuente: DIAN · Datos públicos · Ley 1712/2014</div>
                <div style={{ display:'flex', gap:20, alignItems:'flex-end' }}>
                  {fobPorAño.map((row, i) => {
                    const pct = Math.round((row.valorFobTotal || 0) / maxFob * 100);
                    const isSelected = countryYear === row.año;
                    const dimmed = countryYear && !isSelected;
                    return (
                      <div key={i} onClick={() => setCountryYear(countryYear === row.año ? '' : row.año)}
                        style={{ flex:1, textAlign:'center', cursor:'pointer', opacity: dimmed ? 0.35 : 1, transition:'opacity .2s' }}>
                        <div style={{ fontSize:13, fontWeight:700, color: isSelected ? COLORS.tealDark : COLORS.teal, marginBottom:6 }}>{fmtUSD(row.valorFobTotal)}</div>
                        <div style={{ height:100, background:'rgba(255,255,255,.06)', borderRadius:6, display:'flex', alignItems:'flex-end', overflow:'hidden' }}>
                          <div style={{ width:'100%', height:`${pct}%`, background: isSelected ? `linear-gradient(to top,${COLORS.tealDark},${COLORS.teal})` : `linear-gradient(to top,${COLORS.teal},${COLORS.tealVibrant})`, borderRadius:'5px 5px 0 0', boxShadow: isSelected ? `0 0 0 3px ${COLORS.teal}44` : 'none' }} />
                        </div>
                        <div style={{ fontSize:14, fontWeight:800, color: isSelected ? COLORS.teal : COLORS.navy, marginTop:6 }}>{row.año === '2026' ? '2026*' : row.año}</div>
                        <div style={{ fontSize:11, color:COLORS.gray }}>{(row.numOperaciones||0).toLocaleString()} decl.</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize:11, color:COLORS.gray, marginTop:8 }}>* 2026 = solo enero–febrero</div>
              </div>
            )}

            {/* Loading año */}
            {countryLoadingAño && (
              <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, border:'2px solid #e2e8f0', padding:'48px 24px', textAlign:'center', marginBottom:16 }}>
                <div style={{ fontSize:26, fontWeight:900, color:'#fff', letterSpacing:1 }}>CARGANDO…</div>
                <div style={{ fontSize:13, color:COLORS.gray, marginTop:8 }}>Buscando datos de {countryYear} en {(6800000).toLocaleString()} registros</div>
              </div>
            )}

            {/* Top productos — no aplica si ya se filtró por un producto específico */}
            {!countrySubpartida && partidasTop.length > 0 && (
              <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, border:'1px solid rgba(255,255,255,.1)', overflow:'hidden', marginBottom:16 }}>
                <div style={{ padding:'16px 20px', borderBottom:'1px solid rgba(255,255,255,.08)', background:'rgba(255,255,255,.04)' }}>
                  <div style={{ fontWeight:700, fontSize:14, color:"#fff", display:'flex', alignItems:'center', gap:10 }}><div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2, flexShrink:0 }}/>Top productos {isExpPais ? `exportados hacia ${countryName}` : `importados desde ${countryName}`}</div>
                  <div style={{ fontSize:12, color:COLORS.gray, marginTop:2 }}>Top 20 por valor FOB · {countryYear || '2023–2026'}{countryLoadingAño ? <span style={{ color:'#fff', fontWeight:900, fontSize:15 }}> · CARGANDO…</span> : ''}</div>
                </div>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                    <thead style={{ background:'rgba(255,255,255,.04)' }}>
                      <tr>
                        {['#','Subpartida','Descripción','Ops','FOB Total'].map(h => (
                          <th key={h} style={{ padding:'10px 14px', color:COLORS.gray, fontWeight:600, borderBottom:'2px solid #e2e8f0', fontSize:12, textAlign: h==='#' || h==='Ops' || h==='FOB Total' ? 'center' : 'left' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {partidasTop.map((r, i) => (
                        <tr key={i} className="tbl-row" style={{ background: i%2===0 ? 'transparent' : 'rgba(255,255,255,.03)' }}>
                          <td style={{ padding:'9px 14px', textAlign:'center', fontWeight:700, color:COLORS.gray }}>{i+1}</td>
                          <td style={{ padding:'9px 14px', fontFamily:'monospace', fontWeight:700, color:COLORS.teal }}>{r.subpartida}</td>
                          <td style={{ padding:'9px 14px', color:'rgba(255,255,255,.75)', maxWidth:280 }}>
                            <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.descripcion || '—'}</div>
                          </td>
                          <td style={{ padding:'9px 14px', textAlign:'center', color:COLORS.gray }}>{r.ops.toLocaleString()}</td>
                          <td style={{ padding:'9px 14px', textAlign:'right', fontWeight:800, color:"#fff" }}>{fmtUSD(r.valor)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Top importadores colombianos */}
            {empresasTop.length > 0 && (
              <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, border:'1px solid rgba(255,255,255,.1)', overflow:'hidden', marginBottom:24 }}>
                <div style={{ padding:'16px 20px', borderBottom:'1px solid rgba(255,255,255,.08)', background:'rgba(255,255,255,.04)' }}>
                  <div style={{ fontWeight:700, fontSize:14, color:"#fff", display:'flex', alignItems:'center', gap:10 }}><div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2, flexShrink:0 }}/>Top empresas colombianas que {isExpPais ? `exportan` : `importan`}{countrySubpartida ? ` ${productoFiltrado?.descripcionOficial || countrySubpartida}` : ''} {isExpPais ? `hacia ${countryName}` : `desde ${countryName}`}</div>
                  <div style={{ fontSize:12, color:COLORS.gray, marginTop:2 }}>Por valor {isExpPais ? 'FOB' : 'CIF'} total · {countryYear || '2023–2026'}{countryLoadingAño ? <span style={{ color:'#fff', fontWeight:900, fontSize:15 }}> · CARGANDO…</span> : ''}</div>
                </div>
                <div style={{ overflowX:'auto', position:'relative' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                    <thead style={{ background:'rgba(255,255,255,.04)' }}>
                      <tr>
                        {['#','Empresa','Producto principal','NIT','Decl.', isExpPais ? 'FOB Total' : 'CIF Total'].map(h => (
                          <th key={h} style={{ padding:'10px 14px', color:COLORS.gray, fontWeight:600, borderBottom:'2px solid #e2e8f0', fontSize:12, textAlign: h==='#' || h==='Decl.' || h==='FOB Total' ? 'center' : 'left' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {empresasTop.slice(0, 15).map((e, i) => {
                        const locked = !isPaid && i >= 5;
                        return (
                          <tr key={i} className="tbl-row" style={{ background: i%2===0 ? 'transparent' : 'rgba(255,255,255,.03)', filter: locked ? 'blur(4px)' : 'none', userSelect: locked ? 'none' : 'auto' }}>
                            <td style={{ padding:'9px 14px', textAlign:'center', fontWeight:700, color:COLORS.gray }}>{i+1}</td>
                            <td style={{ padding:'9px 14px', fontWeight:700, color:"#fff", maxWidth:200 }}>
                              <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{locked ? '████████████████' : e.razonSocial || '—'}</div>
                            </td>
                            <td style={{ padding:'9px 14px', color:'rgba(255,255,255,.55)', fontSize:12, maxWidth:220 }}>
                              <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{locked ? '████████' : e.topProducto || '—'}</div>
                            </td>
                            <td style={{ padding:'9px 14px', color:COLORS.gray, fontFamily:'monospace', fontSize:12 }}>
                              {locked ? <span style={{ color:COLORS.gray }}>███████</span> : (
                                <span
                                  onClick={() => { const m = isExpPais ? 'X' : 'M'; setOperation(m); fetchCompany(e.nit, null, null, m); }}
                                  style={{ cursor:'pointer', color:COLORS.teal, fontWeight:600, textDecoration:'underline' }}
                                >{e.nit || '—'}</span>
                              )}
                            </td>
                            <td style={{ padding:'9px 14px', textAlign:'center', color:COLORS.gray }}>{locked ? '███' : (e.numOperaciones||0).toLocaleString()}</td>
                            <td style={{ padding:'9px 14px', textAlign:'right', fontWeight:800, color:"#fff" }}>{locked ? '$██.█M' : fmtUSD(e.valorFobTotal || 0)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {!isPaid && empresasTop.length > 5 && (
                    <div style={{position:'absolute',bottom:0,left:0,right:0,height:180,background:'linear-gradient(to bottom, transparent, rgba(255,255,255,0.97) 40%)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'flex-end',paddingBottom:20,gap:8}}>
                      <div style={{fontSize:14,fontWeight:700,color:"#fff"}}>Ver las {empresasTop.slice(5,15).length} empresas restantes</div>
                      <button onClick={()=>setShowLogin(true)} style={{background:COLORS.teal,color:'#fff',border:'none',borderRadius:8,padding:'9px 24px',fontWeight:700,fontSize:13,cursor:'pointer'}}>
                        {user ? 'Actualizar plan →' : 'Iniciar sesión para ver →'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={{ fontSize:11, color:COLORS.gray, textAlign:'center', paddingBottom:32 }}>
              Fuente: DIAN · Datos públicos · Ley 1712/2014 · Última actualización: {countryData.ultimaActualizacion}
            </div>
          </>)}
        </div>
      </div>
    );
  }

  // ── PASO 4: EMPRESA POR NIT ───────────────────────────────────────
  if (step === 4) {
    const isExp    = operation === 'X';
    const emp      = companyData?.empresasTop?.[0] || null;
    const fobAño   = companyData?.fobPorAño        || [];
    const detalle  = companyData?.detalleAgrupado   || [];
    const totalFob = companyData?.resumen?.totalValorFobUsd || 0;
    const maxFob   = fobAño.reduce((m, r) => Math.max(m, r.valorFobTotal), 1);

    // ── opciones únicas para filtros ──
    // Años desde fobPorAño (tiene todos los años, no solo los de las 1000 filas de tabla)
    const años   = fobAño.length > 0
      ? fobAño.map(r => r.año).sort().reverse()
      : [...new Set(detalle.map(r => r.año))].sort().reverse();
    const meses  = [];
    const paises = [...new Set(detalle.map(r => r.pais))].sort();

    // ── estado filtros (guardado en closures para este render) ──
    // usamos companyNit como señal de que estamos aquí; los filtros viven en state externo
    // Para evitar añadir 4 states más, los codificamos en un solo objeto via un ref trick:
    // simplificamos: usamos variables de componente con useState ya declarados arriba.
    // Los states que necesitamos: tblAño, tblPais, tblBusca, tblSort, tblPage
    // Ya los declaramos abajo con nombres de tab/loading/etc existentes — usaremos tab para sort key,
    // liveStats para page, y los nuevos que añadimos. Para esto el código más limpio es añadirlos.
    // NOTA: los states tbl* se declaran antes del if(step===4) en el cuerpo del componente App.
    // Los accedemos aquí directamente.

    const tblAño   = tab === 'resumen' ? '' : tab.startsWith('año:') ? tab.slice(4) : '';
    // Solución: usamos los 5 estados ya declarados (tab, companyNit existente + 3 nuevos tblXxx)
    // Declarados al inicio: tblFiltros = { año, pais, busca, sortCol, sortDir, page }
    // los leemos desde companyData._ui si existe, si no defaults
    const ui = companyData?._ui || { año:'', mes:'', pais:'', subpartida:'', busca:'', col:'valorFob', dir:'desc', page:0 };

    const setUI = (patch) => {
      setCompanyData(prev => ({ ...prev, _ui: { ...(prev._ui || ui), ...patch, page: patch.page ?? 0 } }));
    };

    const subpartidas = [...new Set(detalle.map(r => r.subpartida).filter(Boolean))].sort();

    // filtrar + ordenar
    let filas = detalle.filter(r => {
      if (ui.año        && r.año       !== ui.año)        return false;
      if (ui.mes        && r.mes       !== ui.mes)        return false;
      if (ui.pais       && r.pais      !== ui.pais)       return false;
      if (ui.subpartida && r.subpartida !== ui.subpartida) return false;
      if (ui.busca) {
        const b = ui.busca.toLowerCase();
        const contraparte = isExp ? r.importador : r.exportador;
        if (!contraparte?.toLowerCase().includes(b) &&
            !r.subpartida?.toLowerCase().includes(b) &&
            !(r.descripcionOficial || '').toLowerCase().includes(b)) return false;
      }
      return true;
    });

    const col = ui.col || 'valorFob';
    const dir = ui.dir || 'desc';
    filas = [...filas].sort((a, b) => {
      const va = a[col] ?? 0, vb = b[col] ?? 0;
      if (typeof va === 'string') return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return dir === 'asc' ? va - vb : vb - va;
    });

    const PAGE_SIZE = 50;
    const page      = ui.page || 0;
    const totalPags = Math.ceil(filas.length / PAGE_SIZE);
    const empresaDesbloqueada = isPaid || perfilComprado;
    const filasVis  = empresaDesbloqueada
      ? filas.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
      : filas.slice(0, 5);

    const totalFiltrado  = filas.reduce((s, r) => s + r.valorFob, 0);

    // KPIs exactos: si hay filtro de año, usar fobPorAño (datos del resumen completo)
    // Si no hay filtro de año, usar el resumen total (también exacto)
    const fobAñoSel  = ui.año ? fobAño.find(r => r.año === ui.año) : null;
    const kpiFob     = fobAñoSel
      ? fobAñoSel.valorFobTotal
      : fobAño.length > 0
        ? fobAño.reduce((s, r) => s + (r.valorFobTotal || 0), 0)
        : (companyData?.resumen?.totalValorFobUsd || 0);
    const kpiOps     = fobAñoSel
      ? fobAñoSel.numOperaciones
      : fobAño.length > 0
        ? fobAño.reduce((s, r) => s + (r.numOperaciones || 0), 0)
        : (companyData?.resumen?.totalOperaciones || 0);
    const kpiNPaises = fobAñoSel
      ? (fobAñoSel.numPaises || 0)
      : (companyData?.resumen?.totalPaises || [...new Set(detalle.map(r => r.pais))].length);
    const kpiPesoKgs = ui.año
      ? (fobAñoSel?.pesoTotalKgs || filas.reduce((s, r) => s + (r.pesoKgs || 0), 0))
      : (detalle.reduce((s, r) => s + (r.pesoKgs || 0), 0) || companyData?.resumen?.totalPesoKgs || 0);

    // Perfil de empresa: gratis solo como demo (cifras de muestra). Real con plan Pro
    // o con la compra suelta de esta NIT ($30, 1 año) — decisión 2026-08-04.
    // (empresaDesbloqueada ya se definió arriba, junto a filasVis)
    const DEMO_FOB = 450000000, DEMO_OPS = 1200, DEMO_PAISES = 20, DEMO_PESO_KGS = 50000000;

    const fmtTon = kg => {
      const t = kg / 1000;
      if (t >= 1000) return `${(t/1000).toLocaleString('es-CO', { maximumFractionDigits:1 })} kton`;
      return `${Math.round(t).toLocaleString('es-CO')} ton`;
    };

    const SortHdr = ({ label, col: c }) => {
      const active = col === c;
      return (
        <th onClick={() => setUI({ col: c, dir: active && dir === 'desc' ? 'asc' : 'desc' })}
          style={{ padding:'10px 12px', textAlign:'left', color: active ? COLORS.teal : COLORS.gray,
            fontWeight:600, borderBottom:`2px solid ${active ? COLORS.teal : '#e2e8f0'}`,
            cursor:'pointer', userSelect:'none', whiteSpace:'nowrap', fontSize:12 }}>
          {label} {active ? (dir === 'desc' ? '↓' : '↑') : '↕'}
        </th>
      );
    };

    const exportCSV = () => {
      const q    = v => `"${String(v??'').replace(/"/g,'""')}"`;
      const qtxt = v => `="` + String(v??'').replace(/"/g, '""') + '"'; // fuerza texto en Excel
      const hdrs = isExp
        ? ['Año','Subpartida','Descripción','Importador','País importador','Ops','FOB USD']
        : ['Año','Subpartida','Descripción','Exportador','Ciudad','País origen','Ops','Peso kg','CIF USD'];
      const rows  = filas.map(r => isExp
        ? [r.año, qtxt(r.subpartida), q(r.descripcionOficial||''), q(r.exportador||''), q(r.pais), r.numOps||0, r.valorFob]
        : [r.año, qtxt(r.subpartida), q(r.descripcionOficial||''), q(r.exportador||''), q(r.ciudadExportador||''), q(r.pais), r.numOps||0, q(r.pesoKgs||0), r.valorFob]
      );
      const csv   = [hdrs, ...rows].map(r => r.join(',')).join('\n');
      const blob  = new Blob(['﻿' + csv], { type:'text/csv;charset=utf-8;' });
      const url   = URL.createObjectURL(blob);
      const a     = document.createElement('a'); a.href = url;
      a.download  = `KIN_NIT_${companyNit}_${ui.año||'todos'}.csv`;
      a.click(); URL.revokeObjectURL(url);
    };

    return (
      <div style={{ minHeight:'100vh', background:'#040D1A', fontFamily:"'Inter',system-ui,sans-serif" }}>
        <style>{`
          @keyframes spin{to{transform:rotate(360deg)}}
          .tbl-row:hover td { background:rgba(14,165,164,.06) !important; }
          .flt-sel { padding:7px 10px; border:1.5px solid rgba(255,255,255,.12); border-radius:8px; font-size:13px; background:rgba(255,255,255,.06); cursor:pointer; outline:none; font-family:inherit; color:#fff; }
          .flt-sel:focus { border-color:${COLORS.teal}; }
        `}</style>

        {/* Nav */}
        <div style={{ background:COLORS.navy, padding:'14px 28px', display:'flex', alignItems:'center', gap:14, position:'sticky', top:0, zIndex:100 }}>
          <button onClick={() => companyHsFilter ? setStep(5) : reset()}
            style={{ background:'rgba(255,255,255,.12)', border:'none', color:'#fff', padding:'7px 14px', borderRadius:7, cursor:'pointer', fontSize:13, fontFamily:'inherit', fontWeight:600 }}>
            ← Volver
          </button>
          <div style={{ color:'#fff', fontWeight:800, fontSize:17, letterSpacing:-0.5 }}>
            K<span style={{ color:COLORS.teal }}>IN</span>
          </div>
          <div style={{ color:'rgba(255,255,255,.45)', fontSize:13 }}>
            {isExp ? 'Perfil exportador' : 'Perfil importador'} · NIT {companyNit}
            {companyHsFilter && <span style={{ marginLeft:8, background:'rgba(14,165,164,.25)', color:COLORS.tealVibrant, borderRadius:5, padding:'2px 8px', fontSize:11, fontWeight:700 }}>HS {companyHsFilter}</span>}
          </div>
        </div>

        <div style={{ maxWidth:1200, margin:'24px auto', padding:'0 20px' }}>

          {/* ── LOADING ── */}
          {companyLoading && (
            <div style={{ textAlign:'center', padding:'80px 0' }}>
              <div style={{ width:48, height:48, border:'4px solid rgba(255,255,255,.1)', borderTop:`4px solid ${COLORS.teal}`, borderRadius:'50%', animation:'spin .9s linear infinite', margin:'0 auto 20px' }} />
              <p style={{ color:COLORS.gray, fontSize:15 }}>Consultando DIAN para NIT {companyNit}…</p>
            </div>
          )}

          {/* ── ERROR ── */}
          {!companyLoading && companyData?.error && (
            <div style={{ background:'rgba(248,113,113,.1)', border:'1px solid rgba(248,113,113,.2)', borderRadius:12, padding:24, textAlign:'center', color:'#f87171' }}>
              No se pudo obtener información de la empresa. Verifica que el NIT sea correcto.
            </div>
          )}

          {/* ── SIN DATOS ── */}
          {!companyLoading && companyData && !companyData.error && companyData.resumen?.totalOperaciones === 0 && (
            <div style={{ background:'rgba(255,255,255,.05)', borderRadius:16, padding:40, textAlign:'center', border:'1px solid rgba(255,255,255,.1)' }}>
              <div style={{ fontSize:48, marginBottom:16 }}>🔍</div>
              <div style={{ fontSize:18, fontWeight:700, color:"#fff", marginBottom:8 }}>
                Sin {isExp ? 'exportaciones' : 'importaciones'} para NIT {companyNit}
              </div>
              <p style={{ color:COLORS.gray, fontSize:14, maxWidth:480, margin:'0 auto 20px', lineHeight:1.6 }}>
                Este NIT no aparece en nuestra base de datos DIAN (2023–2026). Puede ser que la empresa {isExp ? 'solo importe' : 'solo exporte'}, use un NIT diferente, o no tenga actividad {isExp ? 'exportadora' : 'importadora'} en este período.
              </p>
              <button onClick={reset} style={{ background:COLORS.teal, color:'#fff', border:'none', borderRadius:10, padding:'10px 20px', fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                ← Buscar otro NIT
              </button>
            </div>
          )}

          {/* ── CON DATOS ── */}
          {!companyLoading && companyData && !companyData.error && companyData.resumen?.totalOperaciones > 0 && (<>

            {/* HEADER */}
            <div style={{ background:`linear-gradient(135deg,${COLORS.navy} 0%,${COLORS.navyLight} 100%)`, borderRadius:16, padding:'28px 32px', marginBottom:16, color:'#fff' }}>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:10, flexWrap:'wrap' }}>
                <div>
                  <div style={{ fontSize:22, fontWeight:800, marginBottom:4 }}>
                    {emp?.razonSocial || `Empresa NIT ${companyNit}`}
                  </div>
                  <div style={{ color:'rgba(255,255,255,.45)', fontSize:14, marginBottom:20 }}>
                    NIT {companyNit}{emp?.ciudad ? ` · ${emp.ciudad}` : ''}
                  </div>
                </div>
                {!empresaDesbloqueada && (
                  <span style={{ background:'rgba(232,163,61,.14)', color:'#e8a33d', fontSize:11, fontWeight:700, letterSpacing:.5, padding:'4px 10px', borderRadius:999, whiteSpace:'nowrap' }}>◇ MUESTRA</span>
                )}
                {empresaDesbloqueada && perfilComprado && !isPaid && (
                  <span style={{ background:'rgba(45,212,196,.14)', color:COLORS.tealVibrant, fontSize:11, fontWeight:700, letterSpacing:.5, padding:'4px 10px', borderRadius:999, whiteSpace:'nowrap' }}>✓ Comprado</span>
                )}
              </div>
              {!empresaDesbloqueada && (
                <div style={{ display:'flex', alignItems:'center', gap:8, background:'rgba(232,163,61,.1)', border:'1px solid rgba(232,163,61,.25)', borderRadius:10, padding:'10px 14px', fontSize:12.5, color:'#e8a33d', marginBottom:16, lineHeight:1.5 }}>
                  <span>Estas cifras son ilustrativas, no son las de {emp?.razonSocial || 'esta empresa'}. Desbloquea el perfil para ver los datos reales.</span>
                </div>
              )}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:16, opacity: empresaDesbloqueada ? 1 : 0.55 }}>
                {[
                  { lbl: isExp ? 'FOB EXPORTADO (USD)' : 'CIF IMPORTADO (USD)', val: empresaDesbloqueada ? fmtUSD(kpiFob) : fmtUSD(DEMO_FOB) },
                  { lbl: 'DECLARACIONES', val: empresaDesbloqueada ? kpiOps.toLocaleString() : DEMO_OPS.toLocaleString() },
                  { lbl: isExp ? 'PAÍSES DESTINO' : 'PAÍSES ORIGEN', val: empresaDesbloqueada ? String(kpiNPaises) : String(DEMO_PAISES) },
                  ...(!isExp && empresaDesbloqueada && companyData?.paisesTop?.[0] ? [{ lbl: 'PAÍS PRINCIPAL', val: companyData.paisesTop[0].pais }] : []),
                  ...(!isExp && (empresaDesbloqueada ? kpiPesoKgs > 0 : true) ? [{ lbl: 'PESO IMPORTADO', val: empresaDesbloqueada ? fmtTon(kpiPesoKgs) : fmtTon(DEMO_PESO_KGS) }] : []),
                  ...(isExp && (empresaDesbloqueada ? kpiPesoKgs > 0 : true) ? [{ lbl: 'PESO EXPORTADO', val: empresaDesbloqueada ? fmtTon(kpiPesoKgs) : fmtTon(DEMO_PESO_KGS) }] : []),
                ].map(({ lbl, val }) => (
                  <div key={lbl} style={{ background:'rgba(255,255,255,.08)', borderRadius:10, padding:'14px 18px' }}>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,.45)', textTransform:'uppercase', letterSpacing:.5, marginBottom:4 }}>{lbl}</div>
                    <div style={{ fontSize:22, fontWeight:800, fontFamily:"'JetBrains Mono','Courier New',monospace" }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Barra de acciones */}
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center', marginBottom:16 }}>
              <select
                value={ui.año || ''}
                onChange={e => setUI({ año: e.target.value })}
                style={{ padding:'8px 12px', border:'1.5px solid rgba(255,255,255,.12)', borderRadius:8, fontSize:13, background:'rgba(255,255,255,.06)', cursor:'pointer', outline:'none', fontFamily:'inherit', fontWeight:600, color:"#fff" }}>
                <option value="">Todos los años</option>
                {años.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              <div style={{ width:1, height:26, background:'rgba(255,255,255,.15)', flexShrink:0 }} />
              {(() => {
                const subsMap = {};
                detalle.forEach(r => {
                  if (!r.subpartida) return;
                  if (!subsMap[r.subpartida]) subsMap[r.subpartida] = { subpartida: r.subpartida, descripcion: r.descripcionOficial || '', valor: 0 };
                  subsMap[r.subpartida].valor += r.valorFob || 0;
                });
                const subs = Object.values(subsMap).sort((a, b) => b.valor - a.valor);
                if (!subs.length) return null;
                const sel = subs.find(s => s.subpartida === reqSubSel) || subs[0];
                const fmtSub = s => {
                  const cod = (s.subpartida || '').replace(/^(\d{4})(\d+)/, '$1.$2');
                  return s.descripcion ? `${cod} · ${s.descripcion.slice(0, 32)}${s.descripcion.length > 32 ? '…' : ''}` : cod;
                };
                {/* Requisitos: siempre gratis — decisión 2026-08-04, ya no depende de isPaid */}
                return (
                  <>
                    <select value={sel.subpartida} onChange={e => setReqSubSel(e.target.value)}
                      style={{ padding:'8px 12px', border:'1.5px solid rgba(255,255,255,.12)', borderRadius:8, fontSize:13, background:'rgba(255,255,255,.06)', cursor:'pointer', outline:'none', fontFamily:'inherit', fontWeight:600, color:'#fff', maxWidth:300 }}>
                      {subs.map(s => <option key={s.subpartida} value={s.subpartida}>{fmtSub(s)}</option>)}
                    </select>
                    <button onClick={() => fetchRequisitos(sel.subpartida, sel.descripcion, 4, isExp)}
                      style={{ background:COLORS.teal, color:'#fff', border:'none', borderRadius:9, padding:'9px 18px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap' }}>
                      📋 {isExp ? 'Requisitos de exportación' : 'Requisitos de importación'}
                    </button>
                  </>
                );
              })()}
              {/* Informe IA: siempre gratis — decisión 2026-08-04 */}
              <button onClick={fetchAnalisisEmpresa}
                style={{ background:'rgba(255,255,255,.08)', color:'#fff', border:'1px solid rgba(255,255,255,.15)', borderRadius:9, padding:'9px 18px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap' }}>
                📄 Informe IA
              </button>
              {!empresaDesbloqueada && (
                <button onClick={() => handleUnlockPerfil(companyNit, emp?.razonSocial)} disabled={perfilUnlocking}
                  style={{ background:'#8b6bf0', color:'#fff', border:'none', borderRadius:9, padding:'9px 18px', fontSize:13, fontWeight:700, cursor: perfilUnlocking ? 'default' : 'pointer', fontFamily:'inherit', whiteSpace:'nowrap', opacity: perfilUnlocking ? 0.7 : 1 }}>
                  {perfilUnlocking ? 'Abriendo pago…' : '🔓 Desbloquear perfil real — $30 USD'}
                </button>
              )}
            </div>

            {/* FOB POR AÑO — barras */}
            {fobAño.length > 0 && (
              <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, marginBottom:16, border:'1px solid rgba(255,255,255,.1)', overflow:'hidden' }}>
                <div style={{ padding:'14px 24px', borderBottom:'1px solid rgba(255,255,255,.08)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <div style={{ fontWeight:700, fontSize:14, color:"#fff", display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2, flexShrink:0 }} />
                    {isExp ? 'FOB exportado' : 'CIF importado'} por año (USD){!empresaDesbloqueada ? ' — muestra' : ''}
                  </div>
                  <div style={{ fontSize:11, color:COLORS.grayLight }}>Fuente: DIAN · Datos públicos · Ley 1712/2014</div>
                </div>
                <div style={{ padding:'20px 24px', display:'flex', gap:20, alignItems:'flex-end', opacity: empresaDesbloqueada ? 1 : 0.45 }}>
                  {fobAño.map((row, i) => {
                    const pct = empresaDesbloqueada ? Math.round(row.valorFobTotal / maxFob * 100) : 50;
                    const barColor = empresaDesbloqueada ? `linear-gradient(to top,${COLORS.teal},${COLORS.tealVibrant})` : 'rgba(255,255,255,.25)';
                    return (
                      <div key={i} style={{ flex:1, textAlign:'center' }}>
                        <div style={{ fontSize:13, fontWeight:700, color: empresaDesbloqueada ? COLORS.teal : 'rgba(255,255,255,.35)', marginBottom:6 }}>
                          {empresaDesbloqueada ? fmtUSD(row.valorFobTotal) : '$•••M'}
                        </div>
                        <div style={{ height:100, background:'rgba(255,255,255,.06)', borderRadius:6, display:'flex', alignItems:'flex-end', overflow:'hidden' }}>
                          <div style={{ width:'100%', height:`${pct}%`, background:barColor, borderRadius:'5px 5px 0 0' }} />
                        </div>
                        <div style={{ fontSize:14, fontWeight:800, color:"#fff", marginTop:6 }}>{row.año}</div>
                        {empresaDesbloqueada && <div style={{ fontSize:11, color:COLORS.gray }}>{(row.numOperaciones || 0).toLocaleString()} decl.</div>}
                        {empresaDesbloqueada && row.numPaises > 0 && <div style={{ fontSize:11, color:COLORS.amber, fontWeight:600 }}>{row.numPaises} países</div>}
                        {empresaDesbloqueada && isExp && row.pesoTotalKgs > 0 && <div style={{ fontSize:11, color:'rgba(255,255,255,.45)', fontWeight:500 }}>{fmtTon(row.pesoTotalKgs)}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* RESUMEN AGRUPADO — perfil de empresa: compradores/proveedores, países, productos */}
            {detalle.length > 0 && (
              <RankingBlock
                tipo={isExp ? 'exp' : 'imp'}
                nit={companyNit}
                anio={ui.año || null}
                pais={ui.pais || null}
                subpartida={ui.subpartida || null}
                isPaid={empresaDesbloqueada}
                onUpgrade={() => { setStep(0); setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior:'smooth' }), 100); }}
              />
            )}

            {/* TABLA DETALLE */}
            {detalle.length > 0 && (
              <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, border:'1px solid rgba(255,255,255,.1)', overflow:'hidden', marginBottom:24 }}>

                {/* Encabezado sección */}
                <div style={{ padding:'14px 24px', borderBottom:'1px solid rgba(255,255,255,.08)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <div style={{ fontWeight:700, fontSize:14, color:"#fff", display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2, flexShrink:0 }} />
                    {isExp ? 'Detalle de exportaciones' : 'Detalle de importaciones'}
                  </div>
                </div>

                {/* Barra de filtros */}
                <div style={{ padding:'12px 20px', borderBottom:'1px solid rgba(255,255,255,.08)', background:'rgba(255,255,255,.04)', display:'flex', gap:10, flexWrap:'nowrap', alignItems:'center', overflowX:'auto' }}>
                  <select className="flt-sel" value={ui.año} onChange={e => setUI({ año: e.target.value })} disabled={!empresaDesbloqueada} title={!empresaDesbloqueada ? 'Disponible en Plan Pro' : undefined} style={{ flexShrink:0, opacity: empresaDesbloqueada ? 1 : 0.4, cursor: empresaDesbloqueada ? 'pointer' : 'not-allowed' }}>
                    <option value="">Todos los años</option>
                    {años.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>

                  <select className="flt-sel" value={ui.pais} onChange={e => setUI({ pais: e.target.value })} disabled={!empresaDesbloqueada} title={!empresaDesbloqueada ? 'Disponible en Plan Pro' : undefined} style={{ flexShrink:0, opacity: empresaDesbloqueada ? 1 : 0.4, cursor: empresaDesbloqueada ? 'pointer' : 'not-allowed' }}>
                    <option value="">Todos los países</option>
                    {paises.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>

                  <select className="flt-sel" value={ui.subpartida||''} onChange={e => setUI({ subpartida: e.target.value })} disabled={!empresaDesbloqueada} title={!empresaDesbloqueada ? 'Disponible en Plan Pro' : undefined} style={{ flexShrink:0, opacity: empresaDesbloqueada ? 1 : 0.4, cursor: empresaDesbloqueada ? 'pointer' : 'not-allowed' }}>
                    <option value="">Todas las subpartidas</option>
                    {subpartidas.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>

                  <input
                    value={ui.busca}
                    onChange={e => empresaDesbloqueada && setUI({ busca: e.target.value })}
                    disabled={!empresaDesbloqueada}
                    placeholder={empresaDesbloqueada ? 'Buscar proveedor o producto…' : '🔒 Filtros disponibles en Plan Pro'}
                    title={!empresaDesbloqueada ? 'Disponible en Plan Pro' : undefined}
                    style={{ flex:1, minWidth:120, padding:'7px 12px', border:'1.5px solid rgba(255,255,255,.1)', borderRadius:8, fontSize:13, fontFamily:'inherit', outline:'none', opacity: empresaDesbloqueada ? 1 : 0.4, cursor: empresaDesbloqueada ? 'text' : 'not-allowed' }}
                  />

                  <button onClick={() => empresaDesbloqueada && setUI({ año:'', mes:'', pais:'', subpartida:'', busca:'', col:'valorFob', dir:'desc', page:0 })} disabled={!empresaDesbloqueada}
                    style={{ flexShrink:0, padding:'7px 14px', background:'rgba(255,255,255,.06)', border:'1.5px solid rgba(255,255,255,.1)', borderRadius:8, fontSize:13, cursor: empresaDesbloqueada ? 'pointer' : 'not-allowed', fontFamily:'inherit', color:COLORS.gray, opacity: empresaDesbloqueada ? 1 : 0.4 }}>
                    Limpiar
                  </button>

                  {empresaDesbloqueada && (
                    <button onClick={exportCSV}
                      style={{ flexShrink:0, padding:'7px 14px', background:COLORS.teal, color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:6 }}>
                      ⬇ Exportar CSV
                    </button>
                  )}
                </div>

                {/* Contador */}
                <div style={{ padding:'8px 20px', background:'rgba(255,255,255,.03)', borderBottom:'1px solid rgba(255,255,255,.05)', fontSize:12, color:COLORS.gray, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span>
                    <strong style={{ color:"#fff" }}>{filas.length}</strong> filas
                    {ui.año || ui.pais || ui.subpartida || ui.busca ? ` (filtradas de ${detalle.length})` : ''}
                    {isExp ? ' · FOB total visible: ' : ' · CIF total visible: '}
                    <strong style={{ color:COLORS.teal }}>{fmtUSD(totalFiltrado)}</strong>
                  </span>
                  <span>Pág. {page + 1} de {totalPags || 1}</span>
                </div>

                {/* Tabla */}
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                    <thead style={{ background:'rgba(255,255,255,.04)', position:'sticky', top:0 }}>
                      <tr>
                        <SortHdr label="Año"         col="año"        />
                        <SortHdr label="Subpartida"  col="subpartida" />
                        <th style={{ padding:'10px 12px', color:COLORS.gray, fontWeight:600, borderBottom:`2px solid #e2e8f0`, fontSize:12, textAlign:'left' }}>Descripción</th>
                        <SortHdr label={isExp ? 'Importador' : 'Exportador'}       col="exportador" />
                        <SortHdr label={isExp ? 'País importador' : 'País origen'} col="pais"       />
                        <SortHdr label="Ops"         col="numOps"     />
                        <SortHdr label="Peso"        col="pesoKgs"    />
                        <SortHdr label={isExp ? 'FOB total' : 'CIF total'} col="valorFob" />
                      </tr>
                    </thead>
                    <tbody>
                      {filasVis.length === 0 && (
                        <tr><td colSpan={8} style={{ padding:32, textAlign:'center', color:COLORS.gray }}>Sin resultados para los filtros seleccionados.</td></tr>
                      )}
                      {filasVis.map((r, i) => (
                        <tr key={i} className="tbl-row">
                          <td style={{ padding:'9px 12px', fontWeight:700, color:"#fff", whiteSpace:'nowrap' }}>{r.año}</td>
                          <td style={{ padding:'9px 12px', fontFamily:'monospace', fontWeight:700, color:COLORS.teal, whiteSpace:'nowrap' }}>{r.subpartida}</td>
                          <td style={{ padding:'9px 12px', color:'rgba(255,255,255,.75)', maxWidth:200 }}>
                            <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.descripcionOficial || ''}>
                              {r.descripcionOficial || '—'}
                            </div>
                          </td>
                          <td style={{ padding:'9px 12px', fontWeight:600, color:"#fff", maxWidth:180 }}>
                            <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.exportador || '—'}</div>
                          </td>
                          <td style={{ padding:'9px 12px' }}>
                            <span style={{ background:'rgba(14,165,164,.15)', color:'#2DD4D3', padding:'2px 8px', borderRadius:5, fontWeight:600, fontSize:12 }}>{r.pais}</span>
                          </td>
                          <td style={{ padding:'9px 12px', color:COLORS.gray, textAlign:'center', whiteSpace:'nowrap' }}>{(r.numOps||0).toLocaleString()}</td>
                          <td style={{ padding:'9px 12px', color:COLORS.gray, whiteSpace:'nowrap', textAlign:'right' }}>{r.pesoKgs > 0 ? fmtTon(r.pesoKgs) : '—'}</td>
                          <td style={{ padding:'9px 12px', fontWeight:800, color:"#fff", whiteSpace:'nowrap', textAlign:'right' }}>
                            {fmtUSD(r.valorFob)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Paginación */}
                {totalPags > 1 && empresaDesbloqueada && (
                  <div style={{ padding:'12px 20px', borderTop:`1px solid #f1f5f9`, display:'flex', gap:8, justifyContent:'center', alignItems:'center' }}>
                    <button onClick={() => setUI({ page: 0 })} disabled={page === 0}
                      style={{ padding:'5px 10px', border:'1.5px solid rgba(255,255,255,.1)', borderRadius:6, background:'rgba(255,255,255,.05)', cursor: page === 0 ? 'default' : 'pointer', color: page === 0 ? '#cbd5e1' : '#fff', fontSize:13 }}>«</button>
                    <button onClick={() => setUI({ page: page - 1 })} disabled={page === 0}
                      style={{ padding:'5px 10px', border:'1.5px solid rgba(255,255,255,.1)', borderRadius:6, background:'rgba(255,255,255,.05)', cursor: page === 0 ? 'default' : 'pointer', color: page === 0 ? '#cbd5e1' : '#fff', fontSize:13 }}>‹</button>
                    <span style={{ fontSize:13, color:COLORS.gray }}>Página {page + 1} de {totalPags}</span>
                    <button onClick={() => setUI({ page: page + 1 })} disabled={page >= totalPags - 1}
                      style={{ padding:'5px 10px', border:'1.5px solid rgba(255,255,255,.1)', borderRadius:6, background:'rgba(255,255,255,.05)', cursor: page >= totalPags-1 ? 'default' : 'pointer', color: page >= totalPags-1 ? '#cbd5e1' : '#fff', fontSize:13 }}>›</button>
                    <button onClick={() => setUI({ page: totalPags - 1 })} disabled={page >= totalPags - 1}
                      style={{ padding:'5px 10px', border:'1.5px solid rgba(255,255,255,.1)', borderRadius:6, background:'rgba(255,255,255,.05)', cursor: page >= totalPags-1 ? 'default' : 'pointer', color: page >= totalPags-1 ? '#cbd5e1' : '#fff', fontSize:13 }}>»</button>
                  </div>
                )}
                {!empresaDesbloqueada && filas.length > 5 && (
                  <div style={{ padding:'24px 20px', borderTop:'1px solid rgba(255,255,255,.08)', background:'linear-gradient(to bottom, rgba(4,13,26,0), rgba(4,13,26,0.97) 40%)', textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', gap:12 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:'#fff' }}>{filas.length - 5} registros más disponibles · Plan Pro</div>
                    <button onClick={() => { setStep(0); setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior:'smooth' }), 100); }} style={{ background:COLORS.teal, color:'#fff', border:'none', borderRadius:9, padding:'10px 28px', fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:'inherit' }}>
                      Ver Plan Pro →
                    </button>
                  </div>
                )}
              </div>
            )}

            <div style={{ fontSize:11, color:COLORS.gray, textAlign:'center', paddingBottom:32 }}>
              Fuente: DIAN · Datos públicos · Ley 1712/2014 · Última actualización: {companyData.ultimaActualizacion}
            </div>
          </>)}
        </div>
      </div>
    );
  }

  // ── PASO 9: PERFIL DE PROVEEDOR EXTRANJERO ──────────────────────
  if (step === 9) {
    const fmtUSD9 = v => v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K` : `$${(v||0).toFixed(0)}`;
    const p = proveedorData;
    const maxFob9 = (p?.fobPorAño || []).reduce((m, r) => Math.max(m, r.valorFobTotal || 0), 1);

    return (
      <div style={{ minHeight:'100vh', background:'#040D1A', fontFamily:"'Inter',system-ui,sans-serif" }}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}} .prov-row:hover td{background:#f0f9ff !important;}`}</style>

        {/* NAV */}
        <div style={{ background:COLORS.navy, padding:'14px 28px', display:'flex', alignItems:'center', gap:14, position:'sticky', top:0, zIndex:100 }}>
          <button onClick={() => setStep(5)} style={{ background:'rgba(255,255,255,.12)', border:'none', color:'#fff', padding:'7px 14px', borderRadius:7, cursor:'pointer', fontSize:13, fontFamily:'inherit', fontWeight:600 }}>
            ← Volver
          </button>
          <div style={{ color:'#fff', fontWeight:800, fontSize:17, letterSpacing:-0.5 }}>K<span style={{ color:COLORS.teal }}>IN</span></div>
          <div style={{ color:'rgba(255,255,255,.45)', fontSize:13 }}>Proveedor · {proveedorNombre}</div>
        </div>

        <div style={{ maxWidth:1100, margin:'24px auto', padding:'0 20px' }}>

          {/* LOADING */}
          {proveedorLoading && (
            <div style={{ textAlign:'center', padding:'80px 0' }}>
              <div style={{ width:48, height:48, border:'4px solid rgba(255,255,255,.1)', borderTop:`4px solid ${COLORS.teal}`, borderRadius:'50%', animation:'spin .9s linear infinite', margin:'0 auto 20px' }} />
              <p style={{ color:COLORS.gray, fontSize:15 }}>Buscando importadores colombianos de <strong>{proveedorNombre}</strong>…</p>
            </div>
          )}

          {/* ERROR */}
          {!proveedorLoading && p?.error && (
            <div style={{ background:'rgba(248,113,113,.1)', border:'1px solid rgba(248,113,113,.2)', borderRadius:12, padding:24, textAlign:'center', color:'#f87171' }}>
              No se pudo obtener información. Intenta de nuevo.
            </div>
          )}

          {/* SIN RESULTADOS */}
          {!proveedorLoading && p && !p.error && p.totalOps === 0 && (
            <div style={{ background:'rgba(255,255,255,.05)', borderRadius:16, padding:40, textAlign:'center', border:'1px solid rgba(255,255,255,.1)' }}>
              <div style={{ fontSize:48, marginBottom:16 }}>🔍</div>
              <div style={{ fontWeight:700, fontSize:18, marginBottom:8 }}>Sin resultados para "{proveedorNombre}"</div>
              <div style={{ color:COLORS.gray, fontSize:14, marginBottom:20 }}>No encontramos importaciones desde este proveedor en la base DIAN (2023–2026).</div>
              <button onClick={() => setStep(5)} style={{ background:COLORS.teal, color:'#fff', border:'none', borderRadius:10, padding:'10px 20px', fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>← Volver al producto</button>
            </div>
          )}

          {/* RESULTADOS */}
          {!proveedorLoading && p && !p.error && p.totalOps > 0 && (<>

            {/* HEADER CARD */}
            <div style={{ background:`linear-gradient(135deg,${COLORS.navy} 0%,${COLORS.navyLight} 100%)`, borderRadius:16, padding:'28px 32px', marginBottom:20, color:'#fff' }}>
              <div style={{ fontSize:22, fontWeight:800, marginBottom:4 }}>{p.exportador}</div>
              <div style={{ color:'rgba(255,255,255,.45)', fontSize:14, marginBottom:20 }}>
                {p.paisOrigen || 'País desconocido'}
                {p.ciudadOrigen ? ` · ${p.ciudadOrigen}` : ''}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:16 }}>
                {[
                  { lbl:'CIF total (USD)', val: fmtUSD9(p.totalFob) },
                  { lbl:'Declaraciones', val: (p.totalOps||0).toLocaleString() },
                  { lbl:'Clientes en Colombia', val: (p.numClientes||0).toLocaleString() },
                ].map(({ lbl, val }) => (
                  <div key={lbl} style={{ background:'rgba(255,255,255,.08)', borderRadius:10, padding:'14px 18px' }}>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,.45)', textTransform:'uppercase', letterSpacing:.5, marginBottom:4 }}>{lbl}</div>
                    <div style={{ fontSize:22, fontWeight:800, fontFamily:"'JetBrains Mono','Courier New',monospace" }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* FOB POR AÑO */}
            {p.fobPorAño?.length > 0 && (
              <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, padding:'20px 24px', marginBottom:16, border:'1px solid rgba(255,255,255,.1)' }}>
                <div style={{ fontWeight:700, fontSize:14, color:"#fff", display:'flex', alignItems:'center', gap:10, marginBottom:16 }}><div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2, flexShrink:0 }}/>CIF importado por año (USD)</div>
                <div style={{ display:'flex', gap:20, alignItems:'flex-end' }}>
                  {p.fobPorAño.map((row, i) => {
                    const pct = Math.round((row.valorFobTotal || 0) / maxFob9 * 100);
                    return (
                      <div key={i} style={{ flex:1, textAlign:'center' }}>
                        <div style={{ fontSize:13, fontWeight:700, color:COLORS.teal, marginBottom:6 }}>{fmtUSD9(row.valorFobTotal)}</div>
                        <div style={{ height:100, background:'rgba(255,255,255,.06)', borderRadius:6, display:'flex', alignItems:'flex-end', overflow:'hidden' }}>
                          <div style={{ width:'100%', height:`${pct}%`, background:`linear-gradient(to top,${COLORS.teal},${COLORS.tealVibrant})`, borderRadius:'5px 5px 0 0' }} />
                        </div>
                        <div style={{ fontSize:14, fontWeight:800, color:"#fff", marginTop:6 }}>{row.año}</div>
                        <div style={{ fontSize:11, color:COLORS.gray }}>{(row.numOperaciones||0).toLocaleString()} decl.</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* CLIENTES COLOMBIANOS */}
            {p.clientesTop?.length > 0 && (
              <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, border:'1px solid rgba(255,255,255,.1)', overflow:'hidden', marginBottom:16 }}>
                <div style={{ padding:'16px 20px', borderBottom:'1px solid rgba(255,255,255,.08)', fontWeight:700, fontSize:14, color:"#fff", display:'flex', alignItems:'center', gap:10 }}><div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2, flexShrink:0 }}/>Clientes colombianos de {p.exportador}</div>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                    <thead>
                      <tr style={{ background:'rgba(255,255,255,.04)' }}>
                        {['#','NIT','Empresa','Depto.','FOB Total (USD)','Declaraciones','Última compra','Productos'].map(h => (
                          <th key={h} style={{ padding:'10px 12px', color:COLORS.gray, fontWeight:600, borderBottom:`2px solid #e2e8f0`, fontSize:12, textAlign:'left', whiteSpace:'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {p.clientesTop.map((c, i) => (
                        <tr key={c.nit} className="prov-row">
                          <td style={{ padding:'9px 12px', fontWeight:700, color:COLORS.gray }}>{i+1}</td>
                          <td style={{ padding:'9px 12px', fontFamily:'monospace', fontSize:12, color:COLORS.gray }}>{c.nit}</td>
                          <td style={{ padding:'9px 12px', fontWeight:600, color:"#fff", maxWidth:220 }}>
                            <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={c.razonSocial}>{c.razonSocial}</div>
                          </td>
                          <td style={{ padding:'9px 12px', color:COLORS.gray, fontSize:12 }}>{c.departamento || '—'}</td>
                          <td style={{ padding:'9px 12px', fontWeight:800, color:"#fff", whiteSpace:'nowrap', textAlign:'right' }}>{fmtUSD9(c.valorFobTotal)}</td>
                          <td style={{ padding:'9px 12px', textAlign:'center', color:COLORS.gray }}>{(c.numOps||0).toLocaleString()}</td>
                          <td style={{ padding:'9px 12px', color:COLORS.gray, fontSize:12, whiteSpace:'nowrap' }}>{c.ultimaCompra || '—'}</td>
                          <td style={{ padding:'9px 12px', maxWidth:260 }}>
                            <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                              {(c.subpartidas || []).slice(0,3).map(s => (
                                <span key={s.subpartida} title={s.descripcion || s.subpartida}
                                  style={{ background:'rgba(14,165,164,.15)', color:'#2DD4D3', padding:'2px 7px', borderRadius:4, fontSize:11, fontFamily:'monospace', fontWeight:600, cursor:'default' }}>
                                  {s.subpartida}
                                </span>
                              ))}
                              {(c.subpartidas||[]).length > 3 && (
                                <span style={{ fontSize:11, color:COLORS.gray, alignSelf:'center' }}>+{c.subpartidas.length - 3}</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TOP SUBPARTIDAS */}
            {p.subpartidasTop?.length > 0 && (
              <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, border:'1px solid rgba(255,255,255,.1)', overflow:'hidden', marginBottom:16 }}>
                <div style={{ padding:'16px 20px', borderBottom:'1px solid rgba(255,255,255,.08)', fontWeight:700, fontSize:14, color:"#fff", display:'flex', alignItems:'center', gap:10 }}><div style={{ width:3, height:16, background:COLORS.teal, borderRadius:2, flexShrink:0 }}/>Productos más importados de {p.exportador}</div>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr style={{ background:'rgba(255,255,255,.04)' }}>
                      {['Subpartida','Descripción','FOB Total (USD)','Declaraciones'].map(h => (
                        <th key={h} style={{ padding:'10px 12px', color:COLORS.gray, fontWeight:600, borderBottom:`2px solid #e2e8f0`, fontSize:12, textAlign:'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {p.subpartidasTop.map((s, i) => (
                      <tr key={s.subpartida} className="prov-row">
                        <td style={{ padding:'9px 12px', fontFamily:'monospace', fontWeight:700, color:COLORS.teal }}>{s.subpartida}</td>
                        <td style={{ padding:'9px 12px', color:'rgba(255,255,255,.75)', maxWidth:320 }}>
                          <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.descripcion || '—'}</div>
                        </td>
                        <td style={{ padding:'9px 12px', fontWeight:800, color:"#fff", whiteSpace:'nowrap', textAlign:'right' }}>{fmtUSD9(s.valorFob)}</td>
                        <td style={{ padding:'9px 12px', textAlign:'center', color:COLORS.gray }}>{(s.numOps||0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}


            <div style={{ fontSize:11, color:COLORS.gray, textAlign:'center', paddingBottom:32 }}>
              Fuente: DIAN · Datos públicos · Ley 1712/2014 · 2023–2026
            </div>
          </>)}
        </div>
      </div>
    );
  }


  // ── PASO 10: LOGÍSTICA ───────────────────────────────────────────
  if (step === 10) {
    const fmtFob10 = v => v >= 1e9 ? `$${(v/1e9).toFixed(1)}B` : v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K` : `$${(v||0).toFixed(0)}`;
    const rows = (logData?.transportadoras || []).filter(t =>
      !logBusca || t.empresa?.toLowerCase().includes(logBusca.toLowerCase())
    );
    return (
      <div style={{ minHeight:'100vh', background:'#040D1A', fontFamily:"'Inter',system-ui,sans-serif" }}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}} .log-row:hover td{background:#f0f9ff !important;}`}</style>

        {/* NAV */}
        <div style={{ background:COLORS.navy, padding:'14px 28px', display:'flex', alignItems:'center', gap:14, position:'sticky', top:0, zIndex:100 }}>
          <button onClick={() => setStep(0)} style={{ background:'rgba(255,255,255,.12)', border:'none', color:'#fff', padding:'7px 14px', borderRadius:7, cursor:'pointer', fontSize:13, fontFamily:'inherit', fontWeight:600 }}>
            ← Volver
          </button>
          <div style={{ color:'#fff', fontWeight:800, fontSize:17, letterSpacing:-0.5 }}>K<span style={{ color:COLORS.teal }}>IN</span></div>
          <div style={{ color:'rgba(255,255,255,.45)', fontSize:13 }}>Logística internacional · Importaciones Colombia</div>
        </div>

        <div style={{ maxWidth:1100, margin:'24px auto', padding:'0 20px' }}>

          {/* BUSCADOR */}
          <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, border:'1px solid rgba(255,255,255,.1)', padding:'24px 28px', marginBottom:20 }}>
            <div style={{ fontWeight:800, fontSize:20, color:"#fff", marginBottom:4 }}>
              Encuentra tu agente de logística ideal
            </div>
            <div style={{ color:COLORS.gray, fontSize:13, marginBottom:20 }}>
              Escribe el producto que quieres importar o exportar y ve qué empresas ya tienen experiencia con ese tipo de carga. Una empresa que ya manejó tu producto conoce los requisitos, documentos y rutas.
            </div>

            {/* Campo producto inteligente */}
            <div style={{ position:'relative', marginBottom:12 }}>
              <div style={{ display:'flex', gap:10 }}>
                <div style={{ position:'relative', flex:2 }}>
                  <input
                    value={logQ}
                    onChange={e => {
                      const v = e.target.value;
                      setLogQ(v);
                      setLogSelected(null);
                      // Si es numérico (acepta puntos/espacios: "0901.11"), úsalo como HS directamente
                      const digits = v.replace(/[.\s]/g, '');
                      if (digits && /^\d+$/.test(digits)) {
                        setLogHs(digits);
                        setLogSugs([]);
                      } else {
                        setLogHs('');
                        setLogSugs(buscarProductos(v));
                      }
                    }}
                    placeholder="Producto (cacao, café…) o partida arancelaria (0901.11)"
                    style={{ width:'100%', padding:'11px 14px', border:`1.5px solid ${logSelected ? COLORS.teal : '#e2e8f0'}`, borderRadius:9, fontSize:14, fontFamily:'inherit', outline:'none', boxSizing:'border-box' }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        setLogSugs([]);
                        if (!logHs && logQ) {
                          const sugs = buscarProductos(logQ);
                          if (sugs.length > 0) { setLogSelected(sugs[0]); setLogHs(sugs[0].hs); fetchTransportadoras(sugs[0].hs); }
                          else fetchTransportadoras(null);
                        } else { fetchTransportadoras(logHs); }
                      }
                      if (e.key === 'Escape') setLogSugs([]);
                    }}
                  />
                  {/* Sugerencias */}
                  {logSugs.length > 0 && (
                    <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'rgba(255,255,255,.05)', border:'1.5px solid rgba(255,255,255,.1)', borderRadius:9, boxShadow:'0 8px 24px rgba(0,0,0,.1)', zIndex:200, overflow:'hidden', marginTop:4 }}>
                      {logSugs.map(p => (
                        <div key={p.hs}
                          onClick={() => {
                            setLogQ(p.nombre);
                            setLogHs(p.hs);
                            setLogSelected(p);
                            setLogSugs([]);
                            fetchTransportadoras(p.hs);
                          }}
                          style={{ padding:'10px 16px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:'1px solid #f1f5f9' }}
                          onMouseEnter={e => e.currentTarget.style.background='#f0f9ff'}
                          onMouseLeave={e => e.currentTarget.style.background='#fff'}
                        >
                          <span style={{ fontWeight:600, color:"#fff" }}>{p.nombre}</span>
                          <span style={{ fontSize:12, color:COLORS.gray, background:'rgba(255,255,255,.06)', padding:'2px 8px', borderRadius:5 }}>Cap. {p.hs} · {p.capDesc}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setLogSugs([]);
                    if (!logHs && logQ) {
                      const sugs = buscarProductos(logQ);
                      if (sugs.length > 0) { setLogSelected(sugs[0]); setLogHs(sugs[0].hs); fetchTransportadoras(sugs[0].hs); }
                      else fetchTransportadoras(null);
                    } else { fetchTransportadoras(logHs); }
                  }}
                  disabled={logLoading}
                  style={{ padding:'10px 22px', background:COLORS.teal, color:'#fff', border:'none', borderRadius:9, fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap' }}
                >
                  {logLoading ? 'Cargando…' : 'Buscar'}
                </button>
                {(logQ || logHs) && (
                  <button onClick={() => { setLogQ(''); setLogHs(''); setLogSelected(null); setLogSugs([]); setLogData(null); setLogBusca(''); }}
                    style={{ padding:'10px 14px', background:'rgba(255,255,255,.06)', border:'1.5px solid rgba(255,255,255,.1)', borderRadius:9, fontSize:13, cursor:'pointer', fontFamily:'inherit', color:COLORS.gray }}>
                    ✕
                  </button>
                )}
              </div>
              {logSelected && (
                <div style={{ marginTop:8, fontSize:12, color:COLORS.teal, fontWeight:600 }}>
                  Empresas con experiencia en <strong>{logSelected.nombre}</strong> · Cap. {logSelected.hs} · {logSelected.capDesc}
                </div>
              )}
              {!logSelected && logHs && /^\d+$/.test(logHs) && (
                <div style={{ marginTop:8, fontSize:12, color:COLORS.gray }}>
                  Filtrando por {logHs.length <= 2 ? 'Capítulo' : logHs.length <= 4 ? 'Partida' : 'Subpartida'} <strong>{logHs}</strong>
                </div>
              )}
            </div>

            {/* Filtro empresa (solo visible si hay resultados) */}
            {logData && (
              <input
                value={logBusca}
                onChange={e => setLogBusca(e.target.value)}
                placeholder="Filtrar resultados por nombre de empresa…"
                style={{ width:'100%', padding:'9px 14px', border:'1.5px solid rgba(255,255,255,.1)', borderRadius:9, fontSize:13, fontFamily:'inherit', outline:'none', boxSizing:'border-box' }}
              />
            )}
          </div>

          {/* LOADING */}
          {logLoading && (
            <div style={{ textAlign:'center', padding:'80px 0' }}>
              <div style={{ width:48, height:48, border:'4px solid rgba(255,255,255,.1)', borderTop:`4px solid ${COLORS.teal}`, borderRadius:'50%', animation:'spin .9s linear infinite', margin:'0 auto 20px' }} />
              <p style={{ color:COLORS.gray, fontSize:15 }}>Consultando base de datos DIAN…</p>
            </div>
          )}

          {/* ERROR */}
          {!logLoading && logError && (
            <div style={{ background:'rgba(248,113,113,.1)', border:'1px solid rgba(248,113,113,.2)', borderRadius:12, padding:'20px 24px', color:'#f87171', fontSize:14 }}>
              ⚠️ {logError}
            </div>
          )}

          {/* RESULTADOS */}
          {!logLoading && !logError && logData && (
            <div style={{ background:'rgba(255,255,255,.05)', borderRadius:14, border:'1px solid rgba(255,255,255,.1)', overflow:'hidden' }}>
              {logData.fallback && (
                <div style={{ padding:'12px 20px', background:'rgba(212,162,76,.12)', borderBottom:'1px solid rgba(212,162,76,.3)', fontSize:13, color:COLORS.amber }}>
                  ⚠️ No encontramos importaciones registradas para esa partida — mostrando las principales empresas de carga en general.
                </div>
              )}
              <div style={{ padding:'14px 20px', background:'rgba(255,255,255,.04)', borderBottom:'1px solid rgba(255,255,255,.08)', display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:13, color:COLORS.gray }}>
                <span>
                  <strong style={{ color:"#fff" }}>{rows.length}</strong> empresas con experiencia en{' '}
                  {logData.fallback
                    ? 'todos los productos'
                    : logSelected
                      ? <strong style={{ color:"#fff" }}>Cap. {logSelected.hs} · {logSelected.capDesc}</strong>
                      : logData.hsCode
                        ? <strong style={{ color:"#fff" }}>{logData.hsCode.length <= 2 ? 'Capítulo' : logData.hsCode.length <= 4 ? 'Partida' : 'Subpartida'} {logData.hsCode}</strong>
                        : 'todos los productos'}
                  {logBusca ? ` · filtradas por "${logBusca}"` : ''}
                </span>
                <span style={{ fontSize:12 }}>Fuente: DIAN · Datos públicos · Ley 1712/2014</span>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr style={{ background:'rgba(255,255,255,.04)' }}>
                      {['#', 'Empresa transportadora', 'Operaciones', 'FOB total (USD)', '% especialización', 'Países origen', 'Subpartidas', 'Último año'].map(h => (
                        <th key={h} style={{ padding:'10px 14px', color:COLORS.gray, fontWeight:600, borderBottom:'2px solid #e2e8f0', fontSize:12, textAlign:'left', whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr><td colSpan={8} style={{ padding:40, textAlign:'center', color:COLORS.gray }}>Sin resultados para este filtro.</td></tr>
                    )}
                    {rows.map((t, i) => (
                      <tr key={t.empresa} className="log-row">
                        <td style={{ padding:'9px 14px', fontWeight:700, color:COLORS.gray, width:40 }}>{i + 1}</td>
                        <td style={{ padding:'9px 14px', fontWeight:700, color:"#fff", maxWidth:280 }}>
                          <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={t.empresa}>{t.empresa}</div>
                        </td>
                        <td style={{ padding:'9px 14px', textAlign:'center', fontWeight:800, color:"#fff" }}>{t.totalOps.toLocaleString()}</td>
                        <td style={{ padding:'9px 14px', fontWeight:700, color:COLORS.teal, whiteSpace:'nowrap' }}>{fmtFob10(t.totalFob)}</td>
                        <td style={{ padding:'9px 14px', textAlign:'center', whiteSpace:'nowrap' }} title="Qué porcentaje de todas las operaciones de esta empresa corresponde al producto buscado">
                          {t.pctEspec != null ? (
                            <span style={{ fontWeight:700, color: t.pctEspec >= 20 ? '#4ade80' : t.pctEspec >= 5 ? COLORS.amber : COLORS.gray }}>
                              {t.pctEspec.toFixed(1)}%
                            </span>
                          ) : <span style={{ color:COLORS.gray }}>—</span>}
                        </td>
                        <td style={{ padding:'9px 14px', color:'rgba(255,255,255,.75)' }}>
                          <div style={{ fontSize:11, maxWidth:220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={(t.paises||[]).join(', ')}>
                            {t.numPaises} {t.numPaises === 1 ? 'país' : 'países'}
                            {t.paises?.length > 0 && ` · ${t.paises.slice(0,3).join(', ')}${t.paises.length > 3 ? '…' : ''}`}
                          </div>
                        </td>
                        <td style={{ padding:'9px 14px', textAlign:'center', color:COLORS.gray }}>{t.numSubpartidas.toLocaleString()}</td>
                        <td style={{ padding:'9px 14px', textAlign:'center', color:'rgba(255,255,255,.75)' }}>{t.ultimoAnio || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ESTADO INICIAL */}
          {!logLoading && !logError && !logData && (
            <div style={{ textAlign:'center', padding:'60px 0', color:COLORS.gray }}>
              <div style={{ fontSize:16, fontWeight:600, color:"#fff", marginBottom:8 }}>¿Qué producto quieres importar o exportar?</div>
              <div style={{ fontSize:13, marginBottom:20 }}>Escribe el producto y encontraremos empresas que ya tienen experiencia con ese tipo de carga.</div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', justifyContent:'center', maxWidth:680, margin:'0 auto' }}>
                {QUICK_BUTTONS.map(p => (
                  <button key={p.hs} onClick={() => { setLogQ(p.nombre); setLogHs(p.hs); setLogSelected(p); fetchTransportadoras(p.hs); }}
                    style={{ padding:'8px 16px', background:'rgba(14,165,164,.12)', border:'1.5px solid rgba(14,165,164,.3)', borderRadius:20, fontSize:13, fontWeight:600, color:"#fff", cursor:'pointer', fontFamily:'inherit' }}>
                    {p.nombre}
                  </button>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    );
  }

  // ── PASO 3: RESULTADOS ────────────────────────────────────────────
  return (
    <div style={S.app}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}} tr:hover td{background:#f5f7ff !important;} input:focus{border-color:#003087 !important;}`}</style>
      <div style={S.resWrap}>

        <div style={S.resNav}>
          <button style={S.back} onClick={()=>setStep(2)}>← Atrás</button>
          <div style={{flex:1}}>
            <div style={S.resTit}>{operation==="X"?"Análisis de Exportación":"Análisis de Importación"}</div>
            <div style={S.metaRow}>
              <span style={S.metaTag}>{selHS?.description||selHS?.desc} · HS {selHS?.code}</span>
              <span style={S.metaTag}>{exp.iso==="all"?"Todos los países":stripFlag(exp.name)} → {imp.iso==="all"?"Todos los países":stripFlag(imp.name)}</span>
              {res.crecimiento_tendencia&&<span style={{...S.metaTag,background:"rgba(16,185,129,.12)",color:C.green}}>Tendencia: {res.crecimiento_tendencia}</span>}
              {hasRealComtrade && <span style={{...S.metaTag,background:"rgba(14,165,164,0.1)",color:COLORS.teal}}>✓ UN Comtrade real</span>}
              {hasRealWTO      && <span style={{...S.metaTag,background:"rgba(14,165,164,0.1)",color:COLORS.teal}}>✓ WTO real</span>}
            </div>
          </div>
          <div style={{display:"flex",gap:8,flexShrink:0}}>
            <button
              onClick={() => setShowSourcePanel(p => !p)}
              style={{padding:"7px 14px",background:showSourcePanel?"rgba(14,165,164,0.15)":"rgba(14,165,164,0.07)",border:`1px solid ${COLORS.teal}`,borderRadius:8,color:COLORS.teal,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',system-ui,sans-serif",whiteSpace:"nowrap"}}
            >
              Verificar fuentes
            </button>
            <button style={S.newBtn} onClick={reset}>Nueva búsqueda</button>
          </div>
        </div>

        {/* Panel de verificación de fuentes oficiales */}
        {showSourcePanel && !loading && (
          <div style={{background:"rgba(14,165,164,0.05)",border:`1px solid ${COLORS.teal}`,borderRadius:10,padding:"14px 18px",marginBottom:12}}>
            <div style={{fontSize:12,fontWeight:700,color:COLORS.teal,marginBottom:10}}>Verificar datos en fuentes oficiales</div>
            <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
              <a href={comtradeDeepLink} target="_blank" rel="noopener noreferrer" style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",background:"rgba(255,255,255,.06)",border:"1px solid rgba(255,255,255,.12)",borderRadius:8,textDecoration:"none",color:"#fff",fontSize:12,fontWeight:600}}>
                <div>
                  <div>UN Comtrade</div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,.4)",fontWeight:400}}>Estadísticas de comercio · {selHS?.code}</div>
                </div>
                <span style={{color:COLORS.teal,fontSize:10}}>↗</span>
              </a>
              <a href={wtoDeepLink} target="_blank" rel="noopener noreferrer" style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",background:"rgba(255,255,255,.06)",border:"1px solid rgba(255,255,255,.12)",borderRadius:8,textDecoration:"none",color:"#fff",fontSize:12,fontWeight:600}}>
                <div>
                  <div>WTO Tariff Data</div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,.4)",fontWeight:400}}>Aranceles MFN aplicados · HS {String(selHS?.code||"").slice(0,4)}</div>
                </div>
                <span style={{color:COLORS.teal,fontSize:10}}>↗</span>
              </a>
              <a href={worldbankDeepLink} target="_blank" rel="noopener noreferrer" style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",background:"rgba(255,255,255,.06)",border:"1px solid rgba(255,255,255,.12)",borderRadius:8,textDecoration:"none",color:"#fff",fontSize:12,fontWeight:600}}>
                <div>
                  <div>World Bank Open Data</div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,.4)",fontWeight:400}}>Exportaciones · {exp.iso==="all"?"Mundial":exp.iso}</div>
                </div>
                <span style={{color:COLORS.teal,fontSize:10}}>↗</span>
              </a>
            </div>
          </div>
        )}

        {/* Banner de datos en caché / stale */}
        {staleBanner && !loading && (
          <div style={{background:"rgba(212,162,76,0.12)",border:`1px solid ${COLORS.amber}`,borderRadius:10,padding:"10px 16px",marginBottom:12,fontSize:12,color:"rgba(255,255,255,.6)",display:"flex",alignItems:"center",gap:8}}>
            <span>{staleBanner}</span>
          </div>
        )}

        {loading&&(
          <div style={S.ldBox}>
            <div style={S.bigSpin}/>
            <div style={S.ldTit}>Consultando APIs oficiales + generando análisis...</div>
            <div style={S.ldSub}>{loadMsg}</div>
            <div style={S.ldSteps}>
              {[
                "UN Comtrade — estadísticas de comercio",
                "WTO — aranceles aplicados",
                "World Bank — contexto macroeconómico",
                "Análisis IA sobre datos verificados",
                "Score de oportunidad y top mercados",
              ].map((t,i)=>(<div key={i} style={S.ldStep}><span>{t}</span></div>))}
            </div>
            <div style={{marginTop:16,fontSize:12,color:C.gray}}>APIs reales + 2 análisis IA en paralelo...</div>
          </div>
        )}

        {error&&!loading&&(
          <div style={S.errBox}>
            <div style={S.ldTit}>No se pudieron cargar los datos</div>
            <div style={{fontSize:13,color:C.gray,margin:"8px auto 20px",maxWidth:400}}>{error}</div>
            <button style={{...S.nextBtn,maxWidth:220,margin:"0 auto",display:"block"}} onClick={runFetch}>Reintentar</button>
          </div>
        )}

        {tradeData&&!loading&&(
          <div style={{animation:"fadeIn .4s ease"}}>
            {/* TABS */}
            <div style={{background:"rgba(255,255,255,.04)",borderRadius:14,border:"1px solid rgba(255,255,255,.08)",marginBottom:16,overflowX:"auto"}}>
              <div style={{display:"flex",borderBottom:`1px solid ${C.border}`,padding:"0 8px"}}>
                {TABS.map(t=><Tab key={t.id} icon={t.icon} label={t.proOnly && !isPaid ? '🔒 ' + t.label : t.label} active={tab===t.id} onClick={()=>{ if(t.proOnly && !isPaid){ setStep(0); setTimeout(()=>document.getElementById('pricing')?.scrollIntoView({behavior:'smooth'}),100); return; } setTab(t.id); }} badge={t.badge}/>)}
              </div>
            </div>

            {/* TAB RESUMEN */}
            {tab==="resumen"&&<div style={{animation:"fadeIn .3s ease"}}>
              <div style={S.kpiRow}>
                {[
                  {
                    lbl:"Años con datos",
                    val: res.años_con_datos ?? tabla.length,
                    sub: `Desde ${res.primer_año_registro ?? "—"}`,
                    rawVal: res.años_con_datos ?? tabla.length,
                    source: hasRealComtrade ? "UN Comtrade" : "Estimación basada en datos comparables",
                    updated: comtradeUpdated,
                    link: comtradeDeepLink,
                    isReal: hasRealComtrade,
                  },
                  {
                    lbl:"Total acumulado",
                    val: fmtUSD(res.total_acumulado_usd),
                    sub: "Valor FOB histórico",
                    source: hasRealComtrade ? "UN Comtrade" : "Estimación basada en datos comparables",
                    updated: comtradeUpdated,
                    link: comtradeDeepLink,
                    isReal: hasRealComtrade,
                  },
                  {
                    lbl:"Promedio anual",
                    val: fmtUSD(res.promedio_anual_usd),
                    sub: "Valor FOB / año",
                    source: hasRealComtrade ? "UN Comtrade" : "Estimación basada en datos comparables",
                    updated: comtradeUpdated,
                    link: comtradeDeepLink,
                    isReal: hasRealComtrade,
                  },
                  {
                    lbl:"Precio/kg",
                    val: res.precio_promedio_kg ? `$${Number(res.precio_promedio_kg).toFixed(2)}/kg` : "—",
                    sub: "Precio unitario prom.",
                    source: hasRealComtrade ? "UN Comtrade" : "Estimación basada en datos comparables",
                    updated: comtradeUpdated,
                    link: comtradeDeepLink,
                    isReal: hasRealComtrade,
                  },
                  {
                    lbl:"Arancel destino",
                    val: res.arancel_aplicado_pct != null ? `${res.arancel_aplicado_pct}%` : "—",
                    sub: "Al ingresar al destino",
                    source: hasRealWTO ? (realData.wto.isTLC ? `WTO (TLC · ${realData.wto.preferentialRate_pct}%)` : "WTO (MFN aplicado)") : "Estimación basada en aranceles comparables",
                    updated: wtoUpdated,
                    link: wtoDeepLink,
                    isReal: hasRealWTO,
                  },
                  {
                    lbl:"Score global",
                    val: score.overall ? `${score.overall}/10` : "—",
                    sub: "Oportunidad de mercado",
                    source: "KIN IA",
                    updated: new Date().toISOString().split("T")[0],
                    link: null,
                    isReal: false,
                  },
                ].map((k,i)=>(
                  <div key={i} style={S.kpi}>
                    <div style={S.kpiLbl}>{k.lbl}</div>
                    <div style={S.kpiVal}>
                      <DataPoint
                        value={k.val}
                        source={k.source}
                        updated={k.updated}
                        link={k.link}
                        color={k.isReal ? COLORS.tealDark : C.dark}
                      />
                    </div>
                    <div style={S.kpiSub}>{k.sub}</div>
                  </div>
                ))}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
                {chartData.length>0&&<div style={S.box}><div style={S.boxTit}>Evolución Valor FOB (millones USD)</div><div style={S.boxSub}>Serie histórica · Fuente: {hasRealComtrade ? "UN Comtrade (─── verificado) + estimación (┄┄┄)" : "Estimación basada en patrones de comercio"}</div><ResponsiveContainer width="100%" height={200}><LineChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.07)"/><XAxis dataKey="año" tick={{fontSize:11,fill:"rgba(255,255,255,.45)"}}/><YAxis tick={{fontSize:11,fill:"rgba(255,255,255,.45)"}} unit="M"/><Tooltip formatter={(v,name)=>[`$${v}M USD`, name==="fobReal"?"FOB verificado (UN Comtrade)":"FOB estimado"]}/><Line type="monotone" dataKey="fob" stroke="rgba(255,255,255,.2)" strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls={true}/><Line type="monotone" dataKey="fobReal" stroke={C.blue} strokeWidth={3} dot={{fill:C.blue,r:4}} activeDot={{r:6}} connectNulls={false}/></LineChart></ResponsiveContainer></div>}
                {chartData.length>0&&<div style={S.box}><div style={S.boxTit}>Evolución Precio/kg (USD)</div><div style={S.boxSub}>Precio unitario promedio por año</div><ResponsiveContainer width="100%" height={200}><LineChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.07)"/><XAxis dataKey="año" tick={{fontSize:11,fill:"rgba(255,255,255,.45)"}}/><YAxis tick={{fontSize:11,fill:"rgba(255,255,255,.45)"}} unit="$"/><Tooltip formatter={v=>[`$${v}/kg`,"Precio"]}/><Line type="monotone" dataKey="precio" stroke={C.green} strokeWidth={3} dot={{fill:C.green,r:4}} activeDot={{r:6}}/></LineChart></ResponsiveContainer></div>}
              </div>
              {/* Pulso del mercado colombiano — datos DIAN reales */}
              {!dianLoading&&dianData&&!dianData.error&&dianData.resumen?.totalOperaciones>0&&(
                <div style={{...S.box,marginBottom:16,borderLeft:`4px solid ${COLORS.teal}`}}>
                  <div style={{...S.boxTit,color:COLORS.teal}}>Pulso del Mercado Colombia — Datos Reales DIAN</div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginTop:10}}>
                    {[
                      {lbl:"Importaciones totales", val:`$${((dianData.resumen?.totalValorFobUsd||0)/1e6).toFixed(1)}M CIF`},
                      {lbl:"Operaciones registradas", val:(dianData.resumen?.totalOperaciones||0).toLocaleString("es-CO")},
                      {lbl:"Empresas importando", val:`${dianData.empresasTop?.length||0}+ activas`},
                      {lbl:"Principal origen", val:dianData.paisesOrigenTop?.[0]?.pais||"—"},
                    ].map(k=>(
                      <div key={k.lbl} style={{textAlign:"center",padding:"10px 8px",background:"rgba(14,165,164,.08)",borderRadius:8}}>
                        <div style={{fontSize:16,fontWeight:800,color:"#fff",fontFamily:"'JetBrains Mono',monospace"}}>{k.val}</div>
                        <div style={{fontSize:11,color:"rgba(255,255,255,.45)",marginTop:2}}>{k.lbl}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {mktIntel?.consejo_experto&&<div style={{...S.box,borderLeft:`4px solid ${C.gold}`}}><div style={{...S.boxTit,color:C.gold}}>⭐ Consejo del Experto</div><div style={{fontSize:14,color:"rgba(255,255,255,.75)",lineHeight:1.7}}>{mktIntel.consejo_experto}</div></div>}

              {/* Fuentes de datos utilizadas */}
              <div style={{background:"rgba(255,255,255,.03)",border:"1px solid rgba(255,255,255,.08)",borderRadius:10,padding:"10px 16px",fontSize:11,color:C.gray,display:"flex",gap:16,flexWrap:"wrap"}}>
                <span style={{fontWeight:700,color:C.dark}}>Fuentes usadas:</span>
                {hasRealComtrade && <span>✓ <a href={comtradeUrl} target="_blank" rel="noopener noreferrer" style={{color:COLORS.teal}}>UN Comtrade</a> ({comtradeUpdated})</span>}
                {hasRealWTO      && <span>✓ <a href={wtoUrl} target="_blank" rel="noopener noreferrer" style={{color:COLORS.teal}}>WTO Tariff</a> ({wtoUpdated})</span>}
                {realData?.worldbankImporter && <span>✓ <a href="https://data.worldbank.org" target="_blank" rel="noopener noreferrer" style={{color:COLORS.teal}}>World Bank</a> ({realData.worldbankImporter.lastUpdate})</span>}
                <span>✓ Claude AI (análisis e interpretación)</span>
                {!hasRealComtrade && !hasRealWTO && <span style={{color:COLORS.amber}}>⚠️ APIs no disponibles — datos estimados por IA</span>}
              </div>
            </div>}

            {/* TAB HISTORICO */}
            {tab==="historico"&&<div style={{animation:"fadeIn .3s ease"}}>
              <div style={{...S.box,overflowX:"auto"}}>
                <div style={S.boxTit}>📋 Tabla Histórica — {stripFlag(exp.name)} → {stripFlag(imp.name)}</div>
                <div style={S.boxSub}>
                  {hasRealComtrade
                    ? `Fuente real: UN Comtrade (${comtradeUpdated}) + estimaciones IA para años sin datos · HS ${selHS?.code}`
                    : `Estimación profesional basada en patrones de comercio globales · HS ${selHS?.code}`}
                </div>
                <table style={S.tbl}>
                  <thead><tr>{["Año","Valor FOB (USD)","Cantidad (kg)","Precio/kg","Variación","Princ. Exportadores","Princ. Importadores/Empresas","Nota del año"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>{[...tabla].sort((a,b)=>b.año-a.año).map((row,i)=>{const t=trendIcon(row.variacion_pct);return(<tr key={i} style={{background:i%2===0?"rgba(255,255,255,.03)":"transparent"}}><td style={{...S.td,fontWeight:800,color:C.blue}}>{row.año}</td><td style={{...S.td,fontWeight:700}}>{fmtUSD(row.fob_usd)}</td><td style={S.td}>{fmtKG(row.cantidad_kg)}</td><td style={S.td}>{row.precio_unitario_usd_kg?`$${Number(row.precio_unitario_usd_kg).toFixed(2)}/kg`:"—"}</td><td style={{...S.td,color:t.color,fontWeight:700}}>{t.label}</td><td style={S.td}>{(row.principales_exportadores||[]).map((e,j)=><div key={j} style={{fontSize:12,marginBottom:2}}>• {e}</div>)}</td><td style={S.td}>{(row.principales_importadores||[]).map((e,j)=><div key={j} style={{fontSize:12,marginBottom:2}}>• {e}</div>)}</td><td style={{...S.td,fontSize:12,color:"rgba(255,255,255,.4)",minWidth:160}}>{row.nota||"—"}</td></tr>);})}</tbody>
                </table>
              </div>
              {(res.principales_competidores_exportadores?.length>0||res.acuerdos_comerciales?.length>0)&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>{res.principales_competidores_exportadores?.length>0&&<div style={S.extBox}><div style={S.extTit}>🏆 Principales competidores globales</div><div style={S.tagRow}>{res.principales_competidores_exportadores.map((c,i)=><span key={i} style={{...S.tag,background:C.light,color:C.blue}}>{c}</span>)}</div></div>}{res.acuerdos_comerciales?.length>0&&<div style={S.extBox}><div style={S.extTit}>🤝 Acuerdos comerciales vigentes</div><div style={S.tagRow}>{res.acuerdos_comerciales.map((a,i)=><span key={i} style={{...S.tag,background:"rgba(16,185,129,.12)",color:C.green}}>{a}</span>)}</div></div>}</div>}
            </div>}

            {/* TAB OPORTUNIDAD */}
            {tab==="oportunidad"&&<div style={{animation:"fadeIn .3s ease"}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
                <div style={S.box}>
                  <div style={S.boxTit}>🎯 Score de Oportunidad</div>
                  <div style={S.boxSub}>Calificación 1-10 por dimensión · Análisis KIN IA</div>
                  {score.overall&&<div style={{textAlign:"center",marginBottom:16}}>
                    <div style={{fontSize:52,fontWeight:900,color:score.overall>=7?C.green:score.overall>=5?C.gold:COLORS.mahogany}}>
                      {score.overall}
                    </div>
                    <div style={{fontSize:13,color:C.gray}}>Score global / 10</div>
                  </div>}
                  {[{label:"Crecimiento demanda",key:"crecimiento_demanda",color:C.green},{label:"Tamaño del mercado",key:"tamaño_mercado",color:C.blue},{label:"Barreras arancelarias",key:"barreras_arancelarias",color:C.gold},{label:"Estabilidad de precio",key:"estabilidad_precio",color:C.purple},{label:"Facilidad logística",key:"facilidad_logistica",color:C.sky}].map(({label,key,color})=>score[key]!=null&&<ScoreBar key={key} label={label} value={score[key]} color={color}/>)}
                </div>
                <div style={S.box}>
                  <div style={S.boxTit}>📅 Estacionalidad del Mercado</div>
                  <div style={S.boxSub}>Índice de actividad comercial por mes · KIN IA</div>
                  <Estacionalidad data={mktIntel?.estacionalidad}/>
                  {mktIntel?.benchmark_precio&&<div style={{marginTop:14,padding:"12px 14px",background:C.light,borderRadius:10}}>
                    <div style={{fontSize:12,fontWeight:700,color:C.dark,marginBottom:6}}>
                      💵 Benchmark de Precio
                      {hasRealComtrade && <span style={{fontSize:10,color:COLORS.teal,marginLeft:6}}>· Ref: UN Comtrade</span>}
                    </div>
                    <div style={{display:"flex",gap:16,fontSize:12}}>
                      <div><div style={{color:C.gray}}>Precio origen</div><div style={{fontWeight:700}}>${Number(mktIntel.benchmark_precio.precio_origen_usd_kg||0).toFixed(2)}/kg</div></div>
                      <div><div style={{color:C.gray}}>Promedio global</div><div style={{fontWeight:700}}>${Number(mktIntel.benchmark_precio.promedio_global_usd_kg||0).toFixed(2)}/kg</div></div>
                      <div><div style={{color:C.gray}}>Competitivo</div><div style={{fontWeight:700,color:mktIntel.benchmark_precio.competitivo?C.green:COLORS.mahogany}}>{mktIntel.benchmark_precio.competitivo?"✅ Sí":"❌ No"}</div></div>
                    </div>
                    {mktIntel.benchmark_precio.comentario&&<div style={{marginTop:6,fontSize:11,color:"rgba(255,255,255,.5)"}}>{mktIntel.benchmark_precio.comentario}</div>}
                  </div>}
                </div>
              </div>
              {mktIntel?.perfil_comprador&&<div style={{...S.box,borderLeft:`4px solid ${C.purple}`}}><div style={{...S.boxTit,color:C.purple}}>👤 Perfil del Comprador Típico</div><div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:12,marginTop:8}}>{[{lbl:"Tipo de empresa",val:mktIntel.perfil_comprador.tipo_empresa},{lbl:"Volumen mínimo",val:mktIntel.perfil_comprador.volumen_minimo_ton?`${mktIntel.perfil_comprador.volumen_minimo_ton} toneladas`:"—"},{lbl:"Forma de pago",val:mktIntel.perfil_comprador.forma_pago_tipica},{lbl:"Requisitos calidad",val:mktIntel.perfil_comprador.requisitos_calidad}].map(({lbl,val})=>(<div key={lbl} style={{background:C.light,borderRadius:8,padding:"10px 12px"}}><div style={{fontSize:10,color:C.gray,fontWeight:700,textTransform:"uppercase",marginBottom:4}}>{lbl}</div><div style={{fontSize:13,color:C.dark,fontWeight:600}}>{val||"—"}</div></div>))}</div></div>}
            </div>}

            {/* TAB PROVEEDORES */}
            {tab==="proveedores"&&<div style={{animation:"fadeIn .3s ease"}}>

              {/* Países de origen DIAN — de dónde lo traen realmente */}
              {dianData?.paisesOrigenTop?.length>0&&<div style={{...S.box,marginBottom:16}}>
                <div style={S.boxTit}>Exportadores por país de origen — Datos reales DIAN</div>
                <div style={S.boxSub}>Empresas extranjeras que venden este producto a Colombia · FOB · Período: {dianData.resumen?.rangoFechas}</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24,marginTop:8}}>
                  <div>
                    {(dianData.paisesOrigenTop||[]).map((p,i)=>(
                      <div key={i} style={{marginBottom:14,paddingBottom:14,borderBottom:"1px solid #f1f5f9"}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                          <span style={{fontWeight:700,fontSize:13}}>#{i+1} {p.pais}</span>
                          <div style={{textAlign:"right"}}>
                            <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:12,fontWeight:700,color:COLORS.teal}}>{p.porcentaje}%</span>
                            <span style={{fontSize:11,color:COLORS.gray,marginLeft:8}}>{p.numOperaciones} ops</span>
                          </div>
                        </div>
                        <div style={{height:6,background:"rgba(255,255,255,.08)",borderRadius:4,overflow:"hidden",marginBottom:4}}>
                          <div style={{height:"100%",width:`${p.porcentaje}%`,background:i===0?COLORS.teal:i===1?COLORS.navy:COLORS.amber,borderRadius:4,transition:"width .5s"}}/>
                        </div>
                        <div style={{fontSize:11,color:COLORS.gray,marginBottom:6}}>
                          Total FOB: <strong>${p.valorFobTotal>=1e6?(p.valorFobTotal/1e6).toFixed(1)+"M":(p.valorFobTotal/1e3).toFixed(0)+"K"}</strong>
                        </div>
                        {/* Exportadores extranjeros de este país */}
                        {(p.topExportadores||[]).length>0?(
                          <div style={{background:"rgba(255,255,255,.04)",borderRadius:6,padding:"6px 8px"}}>
                            <div style={{fontSize:10,color:"rgba(255,255,255,.55)",fontWeight:800,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.5px"}}>Proveedores en {p.pais}:</div>
                            {p.topExportadores.map((exp,j)=>(
                              <div key={j} style={{fontSize:11,color:"rgba(255,255,255,.8)",display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"3px 0",borderBottom:j<p.topExportadores.length-1?"1px solid rgba(255,255,255,.07)":"none"}}>
                                <div style={{flex:1,minWidth:0,paddingRight:8}}>
                                  <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:600}}>{exp.nombre}</div>
                                  {exp.ciudad&&<div style={{fontSize:10,color:COLORS.gray}}>{exp.ciudad}</div>}
                                </div>
                                <div style={{textAlign:"right",flexShrink:0}}>
                                  <div style={{fontWeight:700,color:COLORS.teal}}>
                                    ${exp.fob>=1e6?(exp.fob/1e6).toFixed(1)+"M":(exp.fob/1e3).toFixed(0)+"K"}
                                  </div>
                                  <div style={{fontSize:10,color:COLORS.gray}}>{exp.ops} ops</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ):(
                          <div style={{fontSize:11,color:COLORS.gray,fontStyle:"italic",paddingLeft:4}}>Sin nombre de exportador registrado en esta declaración</div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div>
                    {(dianData.paisesOrigenTop||[]).length>0&&<ResponsiveContainer width="100%" height={260}>
                      <BarChart data={(dianData.paisesOrigenTop||[]).slice(0,8).map(p=>({
                        name: p.pais.split(" ")[0].slice(0,12),
                        pct: p.porcentaje,
                        fob: Math.round(p.valorFobTotal/1e3),
                      }))} layout="vertical" margin={{left:0,right:16,top:4,bottom:4}}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.07)" horizontal={false}/>
                        <XAxis type="number" tick={{fontSize:10}} unit="%"/>
                        <YAxis dataKey="name" type="category" tick={{fontSize:11}} width={80}/>
                        <Tooltip formatter={v=>[`${v}%`,"Participación"]}/>
                        <Bar dataKey="pct" fill={COLORS.teal} radius={[0,4,4,0]}/>
                      </BarChart>
                    </ResponsiveContainer>}
                  </div>
                </div>
              </div>}

              {/* Exportadores globales del producto — de Comtrade/IA */}
              {topData.length>0&&<div style={{...S.box,marginBottom:16}}>
                <div style={S.boxTit}>Principales Exportadores Mundiales</div>
                <div style={S.boxSub}>Quién produce y exporta este producto globalmente · {hasRealComtrade?"UN Comtrade":"KIN IA"}</div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={topData} margin={{top:4,right:8,bottom:4,left:8}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.07)"/>
                    <XAxis dataKey="name" tick={{fontSize:11,fill:"rgba(255,255,255,.45)"}}/>
                    <YAxis tick={{fontSize:11,fill:"rgba(255,255,255,.45)"}} unit="M"/>
                    <Tooltip formatter={v=>[`$${v}M USD`,"Exportaciones"]}/>
                    <Bar dataKey="value" fill={COLORS.teal} radius={[4,4,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>}

              {/* Tabla comparativa de mercados / oportunidades por destino */}
              <div style={S.box}>
                <div style={S.boxTit}>Países con Mejor Oportunidad de Compra</div>
                <div style={S.boxSub}>
                  Dónde conseguir el mejor precio y condiciones
                  {hasRealWTO && <span style={{color:COLORS.teal,marginLeft:6}}>· Aranceles: WTO ({wtoUpdated})</span>}
                </div>
                <table style={{...S.tbl,minWidth:500}}>
                  <thead><tr>{["#","País proveedor","Volumen exportado","Crecimiento","Arancel entrada","Oportunidad"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>{tMkts.map((m,i)=>{const t=trendIcon(m.crecimiento_pct);return(<tr key={i} style={{background:i%2===0?"rgba(255,255,255,.03)":"transparent"}}><td style={{...S.td,fontWeight:800,color:C.gray,width:30}}>#{i+1}</td><td style={{...S.td,fontWeight:700}}>{m.pais}</td><td style={{...S.td,fontWeight:600}}>{m.valor_importacion_musd?`$${m.valor_importacion_musd}M`:"—"}</td><td style={{...S.td,color:t.color,fontWeight:600}}>{t.label}</td><td style={S.td}>{m.arancel_pct!=null?`${m.arancel_pct}%`:"—"}</td><td style={S.td}><OpportunityBadge level={m.oportunidad}/></td></tr>);})}</tbody>
                </table>
              </div>
            </div>}

            {/* TAB MIS COMPETIDORES */}
            {tab==="competidores"&&<div style={{animation:"fadeIn .3s ease"}}>
              {dianLoading&&<div style={{...S.box,textAlign:"center",padding:40,color:COLORS.gray}}>
                <div style={{fontSize:28,marginBottom:12}}>🔄</div>
                <div style={{fontWeight:600}}>Consultando datos DIAN...</div>
              </div>}
              {!dianLoading&&(!dianData||dianData.error||(dianData.resumen?.totalOperaciones===0&&!dianData.empresasTop?.length))&&<div style={{...S.box,textAlign:"center",padding:40,color:COLORS.gray}}>
                <div style={{fontSize:28,marginBottom:12}}>📭</div>
                <div style={{fontWeight:600}}>Sin registros DIAN para HS {selHS?.code?.slice(0,4)}</div>
                <div style={{fontSize:13,marginTop:6,maxWidth:360,margin:"8px auto 0"}}>
                  {dianData?.error ? `Error: ${dianData.detalle||dianData.error}` : "Este código HS no tiene importaciones registradas en Colombia (2024-2026). Prueba con un código más amplio (ej: 2 dígitos)."}
                </div>
              </div>}
              {!dianLoading&&dianData&&!dianData.error&&dianData.resumen?.totalOperaciones>0&&(()=>{
                const empresas = dianData.empresasTop||[];
                const totalFob = dianData.resumen?.totalValorFobUsd||0;
                const top3Fob  = empresas.slice(0,3).reduce((s,e)=>s+e.valorFobTotal,0);
                const top10Fob = empresas.slice(0,10).reduce((s,e)=>s+e.valorFobTotal,0);
                const restFob  = totalFob - top10Fob;
                const concTop3 = totalFob>0 ? Math.round(top3Fob/totalFob*1000)/10 : 0;
                const concTop10= totalFob>0 ? Math.round(top10Fob/totalFob*1000)/10 : 0;
                return(<>
                  {/* KPIs */}
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
                    {[
                      {icon:"🏢",val:(dianData.resumen?.totalOperaciones||0).toLocaleString("es-CO"),lbl:"Operaciones totales",color:"#fff"},
                      {icon:"💵",val:`$${((totalFob)/1e6).toFixed(1)}M`,lbl:"Valor CIF mercado Colombia",color:COLORS.teal},
                      {icon:"🏆",val:`${concTop3}%`,lbl:"Concentrado en top 3",color:COLORS.amber},
                      {icon:"📅",val:dianData.resumen?.rangoFechas?.split(" a ")[1]||"—",lbl:"Último dato disponible",color:"#7c3aed"},
                    ].map(k=>(
                      <div key={k.lbl} style={{...S.box,padding:"16px 14px",textAlign:"center"}}>
                        <div style={{fontSize:20,marginBottom:4}}>{k.icon}</div>
                        <div style={{fontSize:20,fontWeight:800,color:k.color,fontFamily:"'JetBrains Mono',monospace"}}>{k.val}</div>
                        <div style={{fontSize:11,color:COLORS.gray,marginTop:2}}>{k.lbl}</div>
                      </div>
                    ))}
                  </div>

                  {/* Concentración de mercado */}
                  <div style={{...S.box,marginBottom:16}}>
                    <div style={S.boxTit}>Concentración del Mercado</div>
                    <div style={S.boxSub}>¿El mercado está dominado por pocas empresas o es atomizado?</div>
                    <div style={{display:"flex",gap:0,height:28,borderRadius:8,overflow:"hidden",marginBottom:12}}>
                      {empresas.slice(0,10).map((e,i)=>{
                        const pct = totalFob>0 ? e.valorFobTotal/totalFob*100 : 0;
                        const colors=["#0A2540","#0EA5A4","#D4A24C","#3B82F6","#8B5CF6","#EC4899","#10B981","#F59E0B","#6366F1","#14B8A6"];
                        return pct>0.5&&<div key={i} title={`${e.razonSocial}: ${pct.toFixed(1)}%`} style={{width:`${pct}%`,background:colors[i],transition:"width .5s"}}/>;
                      })}
                      {restFob>0&&<div style={{flex:1,background:"rgba(255,255,255,.12)"}} title={`Resto: ${(100-concTop10).toFixed(1)}%`}/>}
                    </div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                      {empresas.slice(0,5).map((e,i)=>{
                        const pct = totalFob>0 ? Math.round(e.valorFobTotal/totalFob*1000)/10 : 0;
                        const colors=["#0A2540","#0EA5A4","#D4A24C","#3B82F6","#8B5CF6"];
                        return(<div key={i} style={{display:"flex",alignItems:"center",gap:5,fontSize:12}}>
                          <div style={{width:10,height:10,borderRadius:2,background:colors[i],flexShrink:0}}/>
                          <span style={{color:COLORS.gray}}>{e.razonSocial.slice(0,20)} <strong style={{color:"#fff"}}>{pct}%</strong></span>
                        </div>);
                      })}
                      <div style={{display:"flex",alignItems:"center",gap:5,fontSize:12}}>
                        <div style={{width:10,height:10,borderRadius:2,background:"rgba(255,255,255,.2)",flexShrink:0}}/>
                        <span style={{color:COLORS.gray}}>Otros <strong style={{color:"#fff"}}>{Math.round((100-concTop10)*10)/10}%</strong></span>
                      </div>
                    </div>
                  </div>

                  {/* Tabla competidores */}
                  <div style={{...S.box,marginBottom:16}}>
                    <div style={S.boxTit}>Top 20 Importadores — Colombia</div>
                    <div style={S.boxSub}>Fuente: DIAN · Período: {dianData.resumen?.rangoFechas} · Ley 1712/2014</div>
                    <div style={{overflowX:"auto",position:"relative"}}>
                      <table style={{...S.tbl,minWidth:700}}>
                        <thead><tr>
                          {["#","Razón Social","Ciudad","Origen principal","Valor CIF","% Mercado","Ops","Últ. importación"].map(h=>(
                            <th key={h} style={S.th}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>{empresas.map((e,i)=>{
                          const pct = totalFob>0 ? Math.round(e.valorFobTotal/totalFob*1000)/10 : 0;
                          const isTop3 = i<3;
                          const locked = !isPaid && i>=5;
                          return(
                            <tr key={i} style={{background:i%2===0?"#f8faff":"#fff",filter:locked?"blur(4px)":"none",userSelect:locked?"none":"auto"}}>
                              <td style={{...S.td,fontWeight:800,color:isTop3?COLORS.amber:COLORS.gray,width:30}}>#{i+1}</td>
                              <td style={{...S.td}}>
                                <div style={{fontWeight:700,fontSize:13}}>{locked?"████████████████":e.razonSocial}</div>
                                <div style={{fontSize:11,color:COLORS.gray,fontFamily:"'JetBrains Mono',monospace"}}>{locked?"███████":e.nit}</div>
                              </td>
                              <td style={{...S.td,fontSize:12}}>{locked?"████":e.ciudad||e.departamento||"—"}</td>
                              <td style={{...S.td,fontSize:12}}>
                                {!locked&&e.origenPrincipal
                                  ? <span style={{background:"rgba(14,165,164,.15)",color:"#2DD4D3",padding:"2px 8px",borderRadius:6,fontWeight:600,fontSize:11}}>{e.origenPrincipal}</span>
                                  : locked?"████":"—"}
                              </td>
                              <td style={{...S.td,fontWeight:700,fontFamily:"'JetBrains Mono',monospace",color:"#fff"}}>
                                {locked?"$██.█M":e.valorFobTotal>=1e6 ? `$${(e.valorFobTotal/1e6).toFixed(1)}M` : `$${(e.valorFobTotal/1e3).toFixed(0)}K`}
                              </td>
                              <td style={{...S.td,minWidth:100}}>
                                <div style={{display:"flex",alignItems:"center",gap:6}}>
                                  <div style={{flex:1,height:6,background:"rgba(255,255,255,.08)",borderRadius:3,overflow:"hidden"}}>
                                    <div style={{height:"100%",width:locked?"0%":`${Math.min(pct,100)}%`,background:isTop3?COLORS.amber:COLORS.teal,borderRadius:3}}/>
                                  </div>
                                  <span style={{fontSize:11,fontWeight:700,color:COLORS.gray,width:36,textAlign:"right"}}>{locked?"██%":`${pct}%`}</span>
                                </div>
                              </td>
                              <td style={{...S.td,textAlign:"center"}}>{locked?"███":e.numOperaciones.toLocaleString()}</td>
                              <td style={{...S.td,fontSize:12,color:COLORS.gray}}>{locked?"████":e.ultimaImportacion||"—"}</td>
                            </tr>
                          );
                        })}</tbody>
                      </table>
                      {!isPaid && empresas.length>5 && (
                        <div style={{position:"absolute",bottom:0,left:0,right:0,height:200,background:"linear-gradient(to bottom, transparent, rgba(248,250,255,0.97) 40%)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-end",paddingBottom:24,gap:10}}>
                          <div style={{fontSize:15,fontWeight:700,color:"#fff"}}>Ver los {empresas.length-5} importadores restantes</div>
                          <div style={{fontSize:13,color:COLORS.gray}}>Incluye valor CIF, origen, NIT y fecha de última importación</div>
                          <button onClick={()=>setShowLogin(true)} style={{background:COLORS.teal,color:"#fff",border:"none",borderRadius:8,padding:"10px 28px",fontWeight:700,fontSize:14,cursor:"pointer"}}>
                            {user ? "Actualizar plan →" : "Iniciar sesión para ver →"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Evolución mensual del mercado */}
                  <div style={{...S.box,marginBottom:16}}>
                    <div style={S.boxTit}>Actividad del Mercado — Últimos 24 meses</div>
                    <div style={S.boxSub}>Valor CIF total importado a Colombia por mes · Fuente: DIAN</div>
                    {(dianData.evolucionMensual||[]).length>0&&(
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={(dianData.evolucionMensual||[]).map(e=>({
                          mes: e.mes_anio?.slice(5)||e.mes_anio,
                          fob: Math.round((e.valorFobTotal||0)/1e3),
                          ops: e.numOperaciones,
                        }))} margin={{top:4,right:8,bottom:4,left:8}}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.07)"/>
                          <XAxis dataKey="mes" tick={{fontSize:10}}/>
                          <YAxis tick={{fontSize:10}} tickFormatter={v=>`$${(v/1000).toFixed(0)}M`}/>
                          <Tooltip formatter={(v,n)=>n==="fob"?[`$${(v/1000).toFixed(1)}M USD`,"Valor FOB"]:[`${v}`,"Operaciones"]}/>
                          <Bar dataKey="fob" fill={COLORS.teal} radius={[3,3,0,0]} name="fob"/>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>

                  {/* Footer legal */}
                  <div style={{background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:8,padding:"10px 16px",fontSize:11,color:COLORS.gray}}>
                    Datos públicos · Ley 1712/2014 · Fallo Tribunal Administrativo de Cundinamarca, septiembre 2022. No incluye personas naturales. Fuente: DIAN. Última actualización: {dianData.ultimaActualizacion}.
                  </div>
                </>);
              })()}
            </div>}

            {/* TAB CALCULADORA */}
            {tab==="calculadora"&&<div style={{animation:"fadeIn .3s ease"}}><Calculadora tradeData={tradeData} marketIntel={mktIntel} dianData={dianData}/></div>}

            {/* TAB REQUISITOS */}
            {tab==="requisitos"&&mktIntel&&<div style={{animation:"fadeIn .3s ease"}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
                <div style={S.box}>
                  <div style={S.boxTit}>Documentos Requeridos</div>
                  {[{title:"Para exportar",list:mktIntel.documentos_exportacion,color:C.blue},{title:"Para importar",list:mktIntel.documentos_importacion,color:C.green}].map(({title,list,color})=>list?.length>0&&(<div key={title} style={{marginBottom:14}}><div style={{fontSize:12,fontWeight:700,color,marginBottom:8}}>{title}</div>{list.map((d,i)=>(<div key={i} style={{display:"flex",gap:8,padding:"6px 0",borderBottom:`1px solid ${C.light}`,fontSize:13,color:"rgba(255,255,255,.8)"}}><span style={{color:C.green}}>✓</span><span>{d}</span></div>))}</div>))}
                </div>
                <div style={S.box}>
                  <div style={S.boxTit}>Guía Logística</div>
                  {[{lbl:"Transporte recomendado",val:mktIntel.logistica?.transporte_recomendado},{lbl:"Incoterms típicos",val:(mktIntel.logistica?.incoterms_tipicos||[]).join(" · ")},{lbl:"Flete estimado",val:mktIntel.logistica?.flete_estimado_usd_ton?`$${mktIntel.logistica.flete_estimado_usd_ton} USD/ton`:"—"},{lbl:"Días de tránsito",val:mktIntel.logistica?.dias_transito?`~${mktIntel.logistica.dias_transito} días`:"—"},{lbl:"Puertos de origen",val:(mktIntel.logistica?.puertos_origen||[]).join(", ")},{lbl:"Puertos de destino",val:(mktIntel.logistica?.puertos_destino||[]).join(", ")}].map(({lbl,val})=>(<div key={lbl} style={{display:"flex",justifyContent:"space-between",gap:12,padding:"8px 0",borderBottom:`1px solid ${C.light}`,fontSize:13}}><span style={{color:C.gray,flexShrink:0}}>{lbl}</span><span style={{fontWeight:600,textAlign:"right"}}>{val||"—"}</span></div>))}
                  {mktIntel.logistica?.consejo_logistico&&<div style={{marginTop:12,background:"rgba(16,185,129,.1)",borderRadius:8,padding:"10px 12px",fontSize:12,color:"rgba(255,255,255,.75)",lineHeight:1.5}}>{mktIntel.logistica.consejo_logistico}</div>}
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                {mktIntel.certificaciones?.length>0&&<div style={S.extBox}><div style={S.extTit}>Certificaciones</div><div style={S.tagRow}>{mktIntel.certificaciones.map((c,i)=><span key={i} style={{...S.tag,background:"rgba(16,185,129,.12)",color:C.green}}>{c}</span>)}</div></div>}
                {mktIntel.barreras_no_arancelarias?.length>0&&<div style={S.extBox}><div style={S.extTit}>Barreras No Arancelarias</div><div style={{display:"flex",flexDirection:"column",gap:6}}>{mktIntel.barreras_no_arancelarias.map((b,i)=><div key={i} style={{display:"flex",gap:8,fontSize:13,color:"rgba(255,255,255,.75)"}}><span style={{color:COLORS.mahoganyLight}}>⚠️</span><span>{b}</span></div>)}</div></div>}
              </div>
            </div>}

            {/* TAB ANÁLISIS IA */}
            {tab==="analisis"&&<div style={{animation:"fadeIn .3s ease"}}>
              <div style={S.anaGrid}>
                {[
                  {key:"oportunidad",   title:"¿Es buena oportunidad?",       color:C.green, icon:"💡"},
                  {key:"riesgos",       title:"Riesgos a considerar",          color:C.gold,  icon:"⚠️"},
                  {key:"contexto_mercado",title:"Contexto actual del mercado", color:C.sky,   icon:"🌐"},
                  {key:"recomendacion", title:"¿Por dónde empezar?",           color:C.blue,  icon:"🚀"},
                ].filter(({key})=>ana[key]).map(({key,title,color,icon})=>(
                  <div key={key} style={{...S.anaCard,borderTopColor:color}}>
                    <div style={{...S.anaTit,color}}>{icon} {title}</div>
                    <div style={S.anaTxt}>{ana[key]}</div>
                  </div>
                ))}
              </div>

              {mktIntel?.consejo_experto&&(
                <div style={{...S.box,borderLeft:`4px solid ${C.gold}`}}>
                  <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:12}}>
                    <div style={{width:36,height:36,flexShrink:0,background:"radial-gradient(circle, #D4A24C 0%, #BA7517 70%)",boxShadow:"0 0 18px rgba(212,162,76,0.45)",borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <span style={{fontSize:16,color:"#fff",lineHeight:1}}>✦</span>
                    </div>
                    <div style={{...S.boxTit,color:C.gold,margin:0}}>Consejo Estratégico del Experto</div>
                  </div>
                  <div style={{fontSize:14,color:"rgba(255,255,255,.75)",lineHeight:1.7}}>{mktIntel.consejo_experto}</div>
                </div>
              )}

              <div style={{fontSize:11,color:C.gray,marginTop:8,lineHeight:1.5}}>
                🤖 Este análisis fue generado por Claude AI interpretando datos de:
                {hasRealComtrade ? " UN Comtrade (datos reales)," : " UN Comtrade (estimación profesional),"}
                {hasRealWTO      ? " WTO (aranceles reales)," : " WTO (estimación),"}
                {realData?.worldbankImporter ? " World Bank (datos reales)." : " World Bank (no disponible)."}
              </div>
            </div>}

            <div style={S.note}>
              <strong>📌 Sobre los datos:</strong> Los valores marcados con ⊛ provienen de fuentes oficiales (UN Comtrade, WTO, World Bank). Pasa el cursor para ver la fuente y fecha exacta. El análisis textual es generado por IA con esos datos como base. Para operaciones reales, valida con un agente de aduana certificado.
            </div>
            <div style={S.foot}>
              <div><div style={{fontWeight:700,marginBottom:4}}>¿Listo para el siguiente paso?</div><div style={{fontSize:13,opacity:.8}}>Agente de aduana · VUCE · Certificado de origen · ProColombia · DIAN</div></div>
              <button style={S.footBtn} onClick={reset}>Nueva búsqueda</button>
            </div>
          </div>
        )}
      </div>
  </div>
  );
}
